import React, { useState, useRef, useEffect, useCallback, useLayoutEffect, useMemo, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { ItineraryOriginalDocuments } from './ItineraryOriginalDocuments';
import { MyTripDates, TripParticipationFields, TripParticipationNotice, participationError } from './TripParticipation';
import type { TripParticipation } from '../../functions/src/travellerRoutePolicy.mjs';
import { motion, AnimatePresence } from 'motion/react';
import { Compass, Settings, ExternalLink, ChevronDown, Info, Clock, MapIcon, Edit2, Plus, Trash2, X, MapPin, CalendarClock, Check, UserPlus, ArrowRight, ArrowLeft, Download, LogIn, LogOut, CheckCircle2, Send, Search, FileInput, Sparkles, RotateCcw, BedDouble, StickyNote, AlertTriangle, CircleAlert, Route, Star, DollarSign } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { usePlacesWidget } from '../hooks/usePlacesWidget';
import { confirmPlaces } from '../services/verifyPlaces';
import { DestinationScheduler, LegRouteMap } from './DestinationScheduler';
import { loadGoogleMaps } from '../services/googleMapsLoader';
import { ItineraryGapsModal } from './ItineraryGapsModal';
import { computeItineraryGaps } from '../utils/itineraryGaps';
import { conflictAudience } from '../../functions/src/bookingConflicts.mjs';
import { DestinationHeroCard } from './DestinationHeroCard';
import { ItineraryDestinationCard } from './ItineraryDestinationCard';
import { DestinationStrip, DestinationStripSide } from './DestinationStrip';
import { renderEmojiOrIcon } from './Icons';
import { getLocationEmoji } from '../utils/locationAssets';
import { getExpandedDays, getFirstName, DEST_GRADIENTS, destAccentColor, calculatedDurationMinutes, nightsBetween, accsForDest, accommodationGaps, shortDateLabel } from '../utils/tripUtils';
import { hasBookingContent, hasBookingDetails } from '../utils/bookingStatus';
import { BookingDetailsForm } from './BookingDetailsForm';
import { AssignedTravellers, TravellerAssignment } from './TravellerAssignment';
import { ItemParticipationForm } from './ItemParticipationForm';
import { BookingBanner, ImportTag, ItemDetailsSection, SavedBookingInformation } from './SavedItemDetails';
import { formatClock } from '../utils/schedulePrefs';
import { destTitle, destinationForDate, transportDuringVisit } from '../utils/destinationItems';
// "Today" is the device's local calendar date. toISOString() is UTC, which in
// Melbourne is still YESTERDAY until 10-11am — so the date strip's today, the
// current-destination ring, the transport/stay "today" badges and the Schedule's
// open-on-today all sat a day behind every morning.
import { addDays, calendarDate, localDateKey } from '../utils/travelState';
import { countryCode } from '../utils/countryFlag';
import { findTravellerByEmail } from '../services/userLookup';
import { saveDayUpdate as sharedSaveDayUpdate, saveTransportBooking, saveAccommodationBooking, saveDestinationRemoval } from '../utils/itineraryWrites';
import { WeatherDisplay, DestinationForecastRange } from './WeatherDisplay';
import { EmojiPicker } from './EmojiPicker';
import { ModernCompass } from './ModernCompass';
import { WizardLoadingScreen } from './WizardLoadingScreen';
import { DateRangePicker } from './DateRangePicker';
import { ImportBookingModal, ImportItineraryModal } from './LazyModals';
import { resolveTz } from '../utils/airportTimezones';
import { fetchDrivingDuration } from '../services/drivingDuration';

/**
 * The zone of a picked place, from Google's own city / state / country for it
 * — so a transport entered by hand carries its timezones like an imported one,
 * and its duration doesn't depend on the airport being in our table.
 */
const placeTz = (place: any, code?: string): string => {
  const part = (type: string, short = false) =>
    place?.address_components?.find((c: any) => c.types?.includes(type))?.[short ? 'short_name' : 'long_name'] || '';
  return resolveTz(undefined, code,
    [part('locality') || part('postal_town'), part('administrative_area_level_1', true), part('country')].filter(Boolean).join(', '));
};

import { Day, Transport, Attraction, Eat, User, Destination, TripConfig, Expense, Accommodation, type BookingCostKind } from '../types';
import { getRecommendations, trackFlight, getDestinationInfo } from '../services/geminiService';
import { generateDestinationDetails, ITINERARY_FILE_ACCEPT } from '../services/itineraryParserService';
import { getExchangeRate } from '../services/currencyService';
import { prepareBookingExpense } from '../utils/bookingExpenses';
import { BOOKING_COST_LABELS, costKindForPaymentStatus, paymentStatusForCostKind } from '../utils/bookingCostReview';
import { expenseBaseCurrency, linkedExpenseLock } from '../utils/expenseUtils';
import { subcatsOf, subcatEmoji, TRANSPORT_MODE_ICON, normalizeSubcat, L1_BY_KEY, l1KeyForType } from '../constants/taxonomy';
import { SubcategoryIcon } from './SubcategoryIcon';
import { SubcategoryPicker } from './SubcategoryPicker';
import { TransportPurposeField } from './TransportPurposeField';
// Shared with scheduleTravel's boundary matching, so a card title and the leg
// the Day Planner picks can't read the same place name differently.
import { cityLabel as _cityLabel } from '../utils/placeNames';
import { Card, Chip, SLabel, EmptyState, FIELD, fieldBorder, BookedStrip, ERROR_BOX, ERROR_LINE } from './UI';
import { UserAvatar, By } from './UserAvatar';
import { TripRoleSelect, TravellerRoleBadges } from './TravellerPermissions';
import { TripSwitcherCard } from './TripSwitcherCard';
import { ItineraryBranchWorkspace, type BranchWorkspaceData, type BranchTimelineContext } from './ItineraryBranchWorkspace';
import { awayOn, boundaryScope, PersonalRouteContext, JunctionContext, routeScheduleVisit, routeVisitIndex, type PersonalHomeRoute } from '../utils/itineraryBranches';
const ExpenseScope = React.createContext<string | undefined>(undefined);
import { destinationKey } from '../utils/destinationItems';
import { TAB_LABELS, type NavTabId } from '../constants/tabs';
import { useProfiles } from '../utils/ProfilesContext';
import { db, functions, auth } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { arrayUnion, getDoc, updateDoc, doc, setDoc } from 'firebase/firestore';
import {
  notifyTransportAdded,
  notifyAccommodationAdded,
  notifyShortlisted,
  notifyAddedToSchedule,
  notifyDestinationAdded,
  notifyDestinationRemoved,
  notifyTripDatesChanged,
  notifyTripRenamed,
  notifyDayNoteAdded,
  notifyItemDateChanged,
  notifyOwnBookingParticipation,
  createTripNotifications } from '../services/notificationService';

import {
  DndContext, 
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formatPlaceLabel } from '../utils/placeUtils';
import { byOrder, withOrder } from '../utils/itemOrder';
import { saveExpenseDelete } from '../utils/expenseWrites';
import { figureNudge } from '../utils/figureNudge';
import { userMessage } from '../utils/userMessage';
import { isAdminInvite, isTripAdmin, tripAdminIds, writeTripRole, type TripRole } from '../utils/tripRoles';
import { peekPendingNav, takePendingNav } from '../utils/deepLinks';
import { PaidBySplit } from './PaidBySplit';
import { Modal } from './Modal';
import { formatMinutes, journeyFor, layoverCity, layoverMinutes, sameTransportLeg } from '../utils/journeys';
import { isOnItinerary, transportsInWindow } from '../utils/transportPlacement';

interface SectionProps {
  id?: string;
  icon: string | React.ReactNode;
  title: string;
  badge?: string;
  badgeColor?: string;
  accent?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  // Rendered in the header row, right of the title and left of the
  // expand/collapse chevron — e.g. the Plan/Explore toggle.
  headerExtra?: React.ReactNode;
  // Opt-in (default false): sticks this section's header row to the top of
  // the scrolling ancestor while its own body content scrolls past — an
  // iOS-Contacts-style "current section" header, handed off automatically
  // to the next sticky section's header once you scroll into it (no extra
  // logic needed for that — it falls out of each section's own wrapper
  // being exactly as tall as that section's header+body combined, which
  // plain `position: sticky` is naturally bounded by). Left false (the
  // existing behaviour) for every other use of this shared component —
  // this was added specifically for the Shortlist tab's three sections.
  stickyHeader?: boolean;
  // Rendered directly below the header row, inside the same sticky unit —
  // only shown while `open` is true, but NOT part of the collapsible
  // AnimatePresence body (so it doesn't animate open/closed itself, and can
  // stick together with the header even though its content is the
  // Plan/Explore toggle living logically "inside" the expanded section).
  // Ignored unless `stickyHeader` is also true.
  key?: any;
}

// Recursively remove all `undefined` values from an object / array so that
// A request to open one of the six "entry item" types (attraction, eat,
// nightlife, note, transport, accommodation) in the shared portal-modal
// viewer/editor. Two listeners handle it — DayDetail (attraction/eat/
// nightlife/note, reusing its existing editItem/editNote save logic) and
// TripOverview (transport/accommodation, reusing its existing viewTransport/
// viewAccom state) — dispatched as a window CustomEvent rather than threaded
// through props so it can be fired from anywhere (a card in the active tab,
// a notification, a Home-tab insight card) without the caller needing to
// know which of those two happens to be mounted.
export type ItemModalRequest = {
  type: 'attraction' | 'eat' | 'nightlife' | 'shopping' | 'note' | 'transport' | 'accommodation';
  itemId: string;
  dayDate?: string; // required for every type except 'accommodation' (trip-level)
  mode?: 'view' | 'edit';
};

export function openItemModal(req: ItemModalRequest) {
  window.dispatchEvent(new CustomEvent('open-item-modal', { detail: req }));
}

/**
 * Renders a plain-text string with any http/https URLs converted to
 * clickable <a> tags that open in a new tab.
 */
/**
 * Where a URL written in prose actually ends.
 *
 * "up to the next space" is how you find one, and it is not where one stops: a
 * note reading "book at https://hotel.com/x. Ask for a late check-out" gave a
 * link whose href ended in a full stop, and "(https://hotel.com/x)" gave one
 * ending in a bracket. Both are dead links, in the one place a traveller pastes
 * something they intend to come back to.
 *
 * Sentence punctuation is trimmed off the end. A closing bracket only when
 * nothing in the URL opened it — Wikipedia and Maps write real URLs with
 * balanced brackets in them, and those keep theirs.
 */
function urlEnd(raw: string): string {
  let url = raw;
  const opens: Record<string, string> = { ')': '(', ']': '[', '}': '{' };
  while (url.length > 0) {
    const last = url[url.length - 1];
    if ('.,;:!?\'"'.includes(last)) { url = url.slice(0, -1); continue; }
    const open = opens[last];
    if (open) {
      // Balanced means at least as many openers as closers: the trailing one
      // belongs to the URL. One closer too many, and it belongs to the prose.
      const balanced = url.split(open).length >= url.split(last).length;
      if (!balanced) { url = url.slice(0, -1); continue; }
    }
    break;
  }
  return url;
}

function renderWithLinks(text: string): React.ReactNode {
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  for (const match of (text || '').matchAll(/https?:\/\/[^\s]+/g)) {
    const start = match.index ?? 0;
    // Only as far as the URL really goes; whatever was trimmed stays as the
    // sentence's own punctuation, in the text where it belongs.
    const url = urlEnd(match[0]);
    if (!url) continue;
    if (start > cursor) nodes.push(text.slice(cursor, start));
    nodes.push(
      <a
        key={start}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={e => e.stopPropagation()}
        className="underline text-gold/80 hover:text-gold break-all"
      >
        {url}
      </a>
    );
    cursor = start + url.length;
  }
  if (cursor < (text || '').length) nodes.push(text.slice(cursor));
  return nodes;
}

function AccordionSection({ id, icon, title, badge, badgeColor, accent, children, defaultOpen = false, onEdit, onDelete, headerExtra, stickyHeader }: SectionProps) {
  /**
   * Whether this header is currently pinned. A sticky header only needs to
   * hide anything while it's actually holding content back; sitting in flow
   * it should be as transparent as the rest of the page, which is what a
   * permanent fill got wrong.
   *
   * Detected with a sentinel immediately above it rather than by observing
   * the header: once stuck, the header sits flush against the scroller's top
   * edge and is fully visible, so it looks the same pinned or not.
   */
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!stickyHeader || !sentinel || typeof IntersectionObserver === 'undefined') return;
    // The scroller is <main>, not the viewport — the tab's content sits below
    // the app header, so a viewport-rooted observer would fire far too early.
    const root = sentinel.closest('main');
    const observer = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting && entry.boundingClientRect.top <= (entry.rootBounds?.top ?? 0)),
      { root, threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [stickyHeader]);

  const [open, setOpen] = useState(defaultOpen);

  const headerRow = (
    <div className="flex items-center transition-colors group">
      <button
        onClick={() => setOpen(!open)}
        className="flex-1 min-w-0 flex items-center gap-4 py-3 text-left"
      >
        <span className="flex-shrink-0 text-gold group-hover:scale-110 transition-transform duration-300">
          {typeof icon === 'string' ? renderEmojiOrIcon(icon, 20) : icon}
        </span>
        <span className="flex-1 min-w-0 text-app-text font-bold text-base tracking-tight leading-tight text-balance truncate">{title}</span>
        {badge && <Chip label={badge} color={badgeColor} size="xs" />}
      </button>
      {headerExtra && (
        <div onClick={(e) => e.stopPropagation()} className="shrink-0">
          {headerExtra}
        </div>
      )}
      <button aria-label={open ? 'Collapse' : 'Expand'} aria-expanded={open} onClick={() => setOpen(!open)} className="tap-target relative p-2 shrink-0">
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          className="text-app-sub opacity-30 block"
        >
          <ChevronDown className="w-4 h-4" />
        </motion.span>
      </button>
      {onEdit && (
        <button aria-label="Edit"
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          className="p-3 text-app-sub/40 hover:text-gold transition-colors"
        >
          <Edit2 className="w-4 h-4" />
        </button>
      )}
      {onDelete && (
        <button aria-label="Delete"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="p-3 text-app-sub/40 hover:text-red-500 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );

  return (
    <div className="mb-6">
      {stickyHeader ? (
        <>
          {/* Zero-height sentinel, read by the observer above: when it leaves
              the top of the scroller the header is pinned. The header itself
              can't be observed for this — once stuck it sits at the root's
              edge and stays fully visible, so it looks identical either way. */}
          <div ref={sentinelRef} aria-hidden className="h-px -mb-px" />
          {/* -mt-px/pt-px: the wrapper's opaque background, once it has one,
              is pixel-identical to the page's — but GPU compositing puts them
              on separate layers, and at some zoom levels that leaves a
              hairline seam right at the boundary. Overlapping by 1px rather
              than meeting edge-to-edge papers over the subpixel gap.
              data-stuck drives the fill: transparent in flow, white only
              while it's actually holding content back (index.css). */}
          <div data-stuck={stuck} className="section-sticky sticky top-0 z-20 bg-app-bg -mt-px pt-px -mx-6 px-6">
            {headerRow}
          </div>
        </>
      ) : (
        headerRow
      )}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pb-2">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FormInput({ label, value, onChange, placeholder, type = "text", error, maxLength }: { label: string; value: string | number; onChange: (v: any) => void; placeholder?: string; type?: string; error?: boolean; maxLength?: number }) {
  return (
    <div className="space-y-1.5 flex-1 min-w-0">
      <label className="ds-label block">{label}</label>
      <input 
        type={type}
        value={value}
        onChange={(e) => onChange(type === "number" ? parseFloat(e.target.value) : e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className={`${FIELD} ${fieldBorder(error, true)}`}
        style={{ colorScheme: 'light' }}
      />
    </div>
  );
}

// Category lists are sourced from the canonical taxonomy (src/constants/taxonomy.ts)
// so the itinerary, expenses, scheduler, import modals and Cloud Functions all
// agree. Edit the taxonomy, not these.
const DINING_CATS    = subcatsOf('food');
const NIGHTLIFE_CATS = subcatsOf('nightlife');
const ATTRACTION_CATS = subcatsOf('experiences');
const ACCOM_CATS     = subcatsOf('accommodation');

// Transport.mode is one of taxonomy.ts's transport subcats (Flight|Train|Bus|Boat|Car|Taxi|Other).


// When a travel day has multiple legs in different modes (e.g. a taxi to the
// airport, then a flight), the Day Planner date strip's transport badge shows
// only the single "highest" mode — lower number wins.
const TRANSPORT_MODE_PRIORITY: Record<string, number> = {
  Flight: 0, Boat: 1, Train: 2, Bus: 3, Car: 4, Taxi: 4, Other: 5,
};
const primaryTransport = (transports: Transport[]): Transport | undefined =>
  [...transports].sort((a, b) => (TRANSPORT_MODE_PRIORITY[a.mode] ?? 99) - (TRANSPORT_MODE_PRIORITY[b.mode] ?? 99))[0];

/** "Mon, 6 Jun" for a single date, or "Mon, 6 Jun - Wed, 8 Jun" for a range —
 *  the destination hero card's Shortlists-mode date label (a whole stay,
 *  not one day). */
const fmtShortDate = (iso: string) =>
  new Date(iso.slice(0, 10) + 'T12:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const fmtDateRange = (startIso: string, endIso: string) => `${fmtShortDate(startIso)} - ${fmtShortDate(endIso)}`;

/** Travellers an item can be assigned to: this scope's accepted members. */
const assignable = (trip?: TripConfig | null) => (trip?.travellers || []).filter((u: User) => u.inviteStatus !== 'pending');

function TransportForm({ transport, transports = [], onSave, onCancel, dayDate, tripConfig: sourceConfig, currentUser, onSavingChange, isNew = false }: { transport: Transport; transports?: Transport[]; onSave: (t: Transport, previous: Transport | null) => void | boolean | Promise<void | boolean>; onCancel: () => void; dayDate: string; tripConfig?: TripConfig; currentUser?: User; onSavingChange?: (saving: boolean) => void; isNew?: boolean }) {
  const route = React.useContext(PersonalRouteContext);
  const originalTransport = useRef(isNew ? null : transport).current;
  // A linked charge owns its payer/split, but is not evidence of the booking total.
  const linkedExpense = transport.expenseLogId
    ? (sourceConfig?.expenses || []).find(e => e.id === transport.expenseLogId && !e.itineraryDeleted)
    : undefined;

  const [data, setData] = useState<Transport>(() => {
    const d = { ...transport };
    if (!d.id) d.id = crypto.randomUUID();
    if (isNew && !d.createdAt) d.createdAt = new Date().toISOString();
    // Only auto-fill the DATE part if the transport has no dep/arr at all.
    // Never inject a fake time — times should stay blank until the user fills them in,
    // so that cards without times show "--:--" rather than a made-up 12:00/14:00.
    if (!d.dep) d.dep = dayDate; // date only, no T
    if (!d.arr) d.arr = dayDate; // date only, no T
    if (!linkedExpense && !d.costCurrency) d.costCurrency = expenseBaseCurrency(sourceConfig);
    return d;
  });
  const tripConfig = isNew && route?.some(v=>v.source.id===sourceConfig?.id || v.source.rootTripId===sourceConfig?.id)
    ? boundaryScope(data,route) ?? sourceConfig : sourceConfig;
  const attemptedScope = useRef<string | undefined>(undefined);
  const [tracking, setTracking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Initialise to true when this transport already has a live linked expense,
  // so editing and saving doesn't accidentally call markExpenseDeleted.
  const [logToExpenses, setLogToExpenses] = useState(!!linkedExpense);
  const [paidBy, setPaidBy] = useState(linkedExpense?.paid_by || currentUser?.id || '');
  const [costSplit, setCostSplit] = useState<string[]>(
    linkedExpense?.split?.length ? linkedExpense.split : (currentUser?.id ? [currentUser.id] : [])
  );
  const [expenseNote, setExpenseNote] = useState<string>(linkedExpense?.note || '');
  useEffect(() => {
    if (!isNew || attemptedScope.current || !tripConfig) return;
    setCostSplit(ids=>ids.filter(id=>tripConfig.memberIds.includes(id)));
    setPaidBy(id=>tripConfig.memberIds.includes(id) ? id : currentUser?.id || '');
  }, [tripConfig?.id]);
  const previewJourney = journeyFor({ ...data, isConnection: true },
    [...transports.filter(t => t.id !== data.id), data].sort((a, b) => (a.dep || '').localeCompare(b.dep || '')));
  const connectionLeg = previewJourney[previewJourney.findIndex(t => t.id === data.id) - 1];

  // Set an ISO stamp's date without inventing a time — a date-only value
  // stays date-only, so the card shows "--:--" rather than a made-up 00:00
  // (see the seeding comment above), and clearing a time drops back to the
  // date rather than leaving a malformed "2026-10-02T".
  const withDate = (iso: string, date: string) => {
    const time = iso?.includes('T') ? iso.split('T')[1] : '';
    return time ? `${date}T${time}` : date;
  };
  const withTime = (iso: string, time: string) => {
    const date = (iso || '').split('T')[0] || dayDate;
    return time ? `${date}T${time}` : date;
  };

  /** Only ever moves the arrival FORWARD, and only when it would otherwise
   *  land before the departure — the one case where leaving it alone stores
   *  impossible data. The arrival date is the user's to set: a leg crossing
   *  two days, typed into the Arrival Date picker, survives untouched. The
   *  previous version reset the arrival to the departure date on every time
   *  keystroke, so it silently destroyed that choice and capped every
   *  crossing at exactly +1 day — a 28-hour ferry collapsed onto one date. */
  const syncArrivalDate = (depISO: string, arrISO: string) => {
    if (!depISO?.includes('T') || !arrISO?.includes('T') || arrISO >= depISO) return arrISO;
    const arrTime = arrISO.split('T')[1];
    const depDate = depISO.split('T')[0];
    // Noon, and formatted from the local parts: parsing a bare date gives
    // UTC midnight, and toISOString() converts back, so either end of that
    // round trip can land on the wrong day depending on the offset.
    const sameDay = `${depDate}T${arrTime}`;
    if (sameDay >= depISO) return sameDay;
    const d = new Date(`${depDate}T12:00`);
    d.setDate(d.getDate() + 1);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${arrTime}`;
  };

  const extractCityCode = (place: any) => {
    let city = "";
    let code = "";
    
    if (place.address_components) {
      // Look for locality (City)
      const cityComp = place.address_components.find((c: any) => 
        c.types.includes("locality") || c.types.includes("postal_town")
      );
      if (cityComp) city = cityComp.long_name;

      // Fallback: sublocality, then administrative area
      if (!city) {
        const subComp = place.address_components.find((c: any) =>
          c.types.includes("sublocality_level_1") || c.types.includes("sublocality")
        );
        if (subComp) city = subComp.long_name;
      }
      // Last resort: admin area — strip verbose prefixes like "Città Metropolitana di",
      // "Province of", "Metropolitan City of" which are not useful as city names
      if (!city) {
        const areaComp = place.address_components.find((c: any) =>
          c.types.includes("administrative_area_level_2") || c.types.includes("administrative_area_level_1")
        );
        if (areaComp) {
          city = areaComp.long_name
            .replace(/^(città\s+metropolitana|metropolitan\s+(city|area)|province|county|région|department|district)\s+(of|di|de|des|du|der)\s+/gi, '')
            .trim();
        }
      }
    }

    // Try to extract airport/station code from name like "London Heathrow (LHR)"
    if (place.name) {
      const codeMatch = place.name.match(/\(([A-Z0-9]{3,4})\)/);
      if (codeMatch) code = codeMatch[1];
      
      if (!city) {
        city = place.name.split(',')[0].trim();
      }
    }

    return { city, code };
  };

  const { ref: fromRef } = usePlacesWidget({
    onPlaceSelected: (place) => {
      const { city, code } = extractCityCode(place);
      const val = place.formatted_address || place.name || '';
      setData(d => ({ ...d, from: val, fromCity: city, fromCode: code, depTz: placeTz(place, code) || undefined }));
      if (val) setError(null);
    },
    options: { 
      types: ["establishment", "geocode"],
      fields: ["address_components", "formatted_address", "name", "types"] 
    }
  });

  const { ref: toRef } = usePlacesWidget({
    onPlaceSelected: (place) => {
      const { city, code } = extractCityCode(place);
      const val = place.formatted_address || place.name || '';
      setData(d => ({ ...d, to: val, toCity: city, toCode: code, arrTz: placeTz(place, code) || undefined }));
      if (val) setError(null);
    },
    options: { 
      types: ["establishment", "geocode"],
      fields: ["address_components", "formatted_address", "name", "types"] 
    }
  });

  const handleTrackFlight = async () => {
    if (!data.flightNum) return;
    setTracking(true);
    try {
      // The departure date entered wins over the day the form was opened on.
      const flightInfo = await trackFlight(data.flightNum.trim(), (data.dep || '').slice(0, 10) || dayDate);
      if (!flightInfo) {
        setError("Couldn't find that flight. Check the number and date, or enter the details yourself.");
      } else {
        setError(null);
        // Extract only times from ISO strings if present
        let depTime = "";
        let arrTime = "";
        
        if (flightInfo.dep && flightInfo.dep.includes('T')) {
          depTime = flightInfo.dep.split('T')[1].substring(0, 5); // HH:MM
        } else if (flightInfo.dep) {
          depTime = flightInfo.dep.substring(0, 5);
        }

        if (flightInfo.arr && flightInfo.arr.includes('T')) {
          arrTime = flightInfo.arr.split('T')[1].substring(0, 5); // HH:MM
        } else if (flightInfo.arr) {
          arrTime = flightInfo.arr.substring(0, 5);
        }

        // The tracker supplies times, not dates. Keep the departure date the
        // user already chose — it used to reset to dayDate, throwing away an
        // overnight departure typed in before tracking — and roll the arrival
        // forward through the same rule the date fields use.
        const finalDep = withTime(data.dep, depTime);
        const finalArr = syncArrivalDate(finalDep, withTime(data.arr || finalDep, arrTime));

        setData(d => ({
          ...d,
          // Only what the lookup actually returned replaces what's there.
          carrier: flightInfo.carrier || d.carrier,
          dep: finalDep,
          arr: finalArr,
          depTz: flightInfo.depTz || d.depTz,
          arrTz: flightInfo.arrTz || d.arrTz,
          terminal: flightInfo.terminal || d.terminal,
          duration: flightInfo.duration || d.duration,
          from: flightInfo.from || flightInfo.fromCity || d.from,
          fromCode: flightInfo.fromCode || d.fromCode,
          fromCity: flightInfo.fromCity || d.fromCity,
          to: flightInfo.to || flightInfo.toCity || d.to,
          toCode: flightInfo.toCode || d.toCode,
          toCity: flightInfo.toCity || d.toCity,
        }));
      }
    } catch (err) {
      console.error("Tracking error:", err);
      setError("Couldn't look up that flight just now. Enter the details yourself, or try again.");
    } finally {
      setTracking(false);
    }
  };

  const onModeChange = (mode: string) => {
    setData({ ...data, mode, isRental: mode === 'Car' && !!data.isRental, icon: subcatEmoji(mode, 'transport') });
  };

  const handleSave = async () => {
    if (saving) return;
    if (!data.from.trim() || !data.to.trim()) {
      setError("Departure and arrival points are required.");
      return;
    }
    if (!(data.dep || '').slice(0, 10) || !(data.arr || '').slice(0, 10)) {
      setError("Departure and arrival dates are required.");
      return;
    }
    // It has to land somewhere on the itinerary — a stay, a changeover, or
    // either side of the trip — or it ends up under "Other saved transport".
    // Same rule the timeline draws with (journeys stay together; a leg gets a
    // day's leeway into the next stop), so Save and the timeline can't disagree.
    if (tripConfig?.destinations?.length && !data.isRental) {
      const others = Object.entries(tripConfig.itinerary || {}).flatMap(([day, d]) =>
        (d?.transports || []).filter(o => o.id !== data.id).map(o => ({ ...o, _dayDate: day })));
      const candidate = { ...data, _dayDate: (data.dep || dayDate).slice(0, 10) };
      if (!isOnItinerary(candidate, tripConfig, [...others, candidate])) {
        setError(`This doesn't line up with your itinerary on ${shortDateLabel(candidate._dayDate)}: it needs to leave from or arrive in one of your destinations around then, or fall before or after the trip. Check the dates and places, or add the destination first.`);
        return;
      }
    }
    // A crossing leg is allowed to arrive days later; arriving *before* it
    // left never is, and nothing downstream could make sense of it.
    // Compared at whatever granularity both ends actually have: a timed
    // departure with a date-only arrival isn't "before", it's incomplete.
    const timed = data.dep?.includes('T') && data.arr?.includes('T');
    const dep = timed ? data.dep : (data.dep || '').slice(0, 10);
    const arr = timed ? data.arr : (data.arr || '').slice(0, 10);
    if (dep && arr && arr < dep) {
      setError("Arrival is before departure — check the dates and times.");
      return;
    }
    if (logToExpenses && !data.expenseLogId) {
      const amt = data.costAmount;
      if (amt == null || isNaN(amt) || amt <= 0 || !/^\d+(\.\d{1,2})?$/.test(String(Math.round(amt * 100) / 100))) {
        setError("A valid cost (up to 2 decimal places) is required to log as an expense.");
        return;
      }
    }
    if (!linkedExpense && data.paidStatus === 'Deposit paid' && !(typeof data.depositAmount === 'number' && data.depositAmount > 0)) {
      setError("Enter the deposit amount paid, or change the payment status.");
      return;
    }
    setError(null);
    if (attemptedScope.current && attemptedScope.current !== tripConfig?.id) {
      setError('Check the original branch for your previous save before changing this journey’s branch.');
      return;
    }
    if (isNew && data.participantIds?.some(id=>!tripConfig?.memberIds.includes(id))) {
      setError('Choose travellers who are part of this journey’s branch.');
      return;
    }
    setSaving(true);
    onSavingChange?.(true);
    try {
      attemptedScope.current = tripConfig?.id;
      const saved = await onSave({ ...data, _logToExpenses: logToExpenses, _paidBy: paidBy, _costSplit: costSplit, _expenseNote: expenseNote } as any, originalTransport);
      if (saved === false) setError('Could not save transport. Your entry is still here; please try again.');
    } catch (err) {
      setError(userMessage(err, 'Could not save transport. Please try again.'));
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  };

  const isFlight = data.mode === 'Flight';
  const carrierHint: Record<string, [string, string]> = {
    Flight: ['Airline', 'e.g. Qantas'], Train: ['Train operator', 'e.g. Eurostar'],
    Bus: ['Bus operator', 'e.g. FlixBus'], Boat: ['Ferry operator', 'e.g. DFDS'],
    Car: data.isRental ? ['Rental company', 'e.g. Hertz'] : ['Driver / operator', 'Optional'], Taxi: ['Taxi company', 'e.g. local taxi service'],
  };
  const [carrierLabel, carrierPlaceholder] = carrierHint[data.mode] || ['Operator', 'Operator name'];
  const bookingSourcePlaceholder: Record<string, string> = {
    Flight: 'e.g. airline website or Skyscanner', Train: 'e.g. train operator website',
    Bus: 'e.g. bus operator website', Boat: 'e.g. ferry operator website',
    Car: 'e.g. rental company website', Taxi: 'e.g. taxi app',
  };
  const placePlaceholder: Record<string, string> = {
    Flight: 'Search airport or city…', Train: 'Search station…', Bus: 'Search bus stop…',
    Boat: 'Search port…', Car: 'Search pick-up or drop-off place…', Taxi: 'Search pick-up or drop-off place…',
  };

  return (
    <fieldset disabled={saving} className="space-y-4 min-w-0">
      {error && (
        <div role="alert" className={ERROR_BOX}>
          {error}
        </div>
      )}
      <SubcategoryPicker l1="transport" value={data.mode} onChange={onModeChange} label="Transport mode" />
      <TransportPurposeField mode={data.mode} isRental={data.isRental} onChange={isRental => setData(d => ({ ...d, isRental, isConnection: false }))} />

      {isFlight && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
          <div className="space-y-1.5">
            <label className="ds-label block">Flight Number</label>
            <div className="flex gap-2">
              <input 
                value={data.flightNum || ""}
                onChange={e => setData({ ...data, flightNum: e.target.value.toUpperCase() })}
                className="flex-1 min-w-0 bg-white border border-app-border/60 rounded-xl px-4 py-3 text-sm text-app-text font-normal focus:outline-none focus:ring-4 focus:ring-gold/5 focus:border-gold/40 transition placeholder:text-app-sub/40"
                placeholder="SQ391"
              />
              <button aria-label="Integrate with Google Flights" 
                type="button"
                onClick={handleTrackFlight}
                disabled={tracking}
                className="bg-gold/10 text-gold px-3 rounded-xl border border-gold/20 hover:bg-gold/20 transition-colors disabled:opacity-50 shrink-0"
                title="Integrate with Google Flights"
              >
                {tracking ? <Clock className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <FormInput label={carrierLabel} value={data.carrier} onChange={(v) => setData({ ...data, carrier: v })} placeholder={carrierPlaceholder} />
        </div>
      )}

      {!isFlight && (
        <div className="grid grid-cols-1 gap-4">
          <FormInput label={carrierLabel} value={data.carrier} onChange={(v) => setData({ ...data, carrier: v })} placeholder={carrierPlaceholder} />
          <FormInput label="Service number" value={data.flightNum || ''} onChange={flightNum => setData({ ...data, flightNum })} />
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5 relative">
          <label className="ds-label block whitespace-nowrap">{data.isRental ? 'Pickup location *' : 'Departure Point *'}</label>
          <input 
            ref={fromRef as any}
            value={data.from}
            onChange={(e) => {
              setData({ ...data, from: e.target.value, depTz: undefined }); // typed, not picked: the stored zone no longer applies
              if (e.target.value) setError(null);
            }}
            placeholder={placePlaceholder[data.mode] || 'Search departure place…'}
            className={`${FIELD} ${error && !data.from ? 'border-rose-300' : 'border-app-border/60 focus:ring-gold/5 focus:border-gold/40'}`}
          />
        </div>
        <div className="space-y-1.5 relative">
          <label className="ds-label block whitespace-nowrap">{data.isRental ? 'Return location *' : 'Arrival Point *'}</label>
          <input 
            ref={toRef as any}
            value={data.to}
            onChange={(e) => {
              setData({ ...data, to: e.target.value, arrTz: undefined });
              if (e.target.value) setError(null);
            }}
            placeholder={placePlaceholder[data.mode] || 'Search arrival place…'}
            className={`${FIELD} ${error && !data.to ? 'border-rose-300' : 'border-app-border/60 focus:ring-gold/5 focus:border-gold/40'}`}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-4">
        <div className="space-y-1.5">
          <label className="ds-label block">{data.isRental ? 'Pickup date *' : 'Departure Date *'}</label>
          <input 
            type="date"
            value={data.dep ? data.dep.split('T')[0] : ""}
            onChange={e => {
              // Moving the departure has to carry the arrival with it, or the
              // arrival is left behind on the old date and ends up before it.
              const newDep = withDate(data.dep, e.target.value);
              setData({ ...data, dep: newDep, arr: syncArrivalDate(newDep, data.arr) });
              if (e.target.value) setError(null);
            }}
            className={`${FIELD} ${error && !data.dep ? 'border-rose-300' : 'border-app-border/60 focus:ring-gold/5 focus:border-gold/40'}`}
          />
        </div>
        <div className="space-y-1.5">
          <label className="ds-label block">{data.isRental ? 'Pickup time' : 'Departure Time *'}</label>
          <input 
            type="time"
            value={data.dep && data.dep.includes('T') ? data.dep.split('T')[1] : ""}
            onChange={e => {
              const newDep = withTime(data.dep, e.target.value);
              setData({ ...data, dep: newDep, arr: syncArrivalDate(newDep, data.arr) });
              if (e.target.value) setError(null);
            }}
            className={`${FIELD} ${error && !data.dep ? 'border-rose-300' : 'border-app-border/60 focus:ring-gold/5 focus:border-gold/40'}`}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-4">
        <div className="space-y-1.5">
          <label className="ds-label block">{data.isRental ? 'Return date *' : 'Arrival Date *'}</label>
          <input 
            type="date"
            value={data.arr ? data.arr.split('T')[0] : ""}
            onChange={e => {
              // Deliberately not synced: an explicitly chosen arrival date is
              // the whole point of a multi-day crossing. handleSave rejects
              // one that lands before the departure.
              setData({ ...data, arr: withDate(data.arr, e.target.value) });
              if (e.target.value) setError(null);
            }}
            className={`${FIELD} ${error && !data.arr ? 'border-rose-300' : 'border-app-border/60 focus:ring-gold/5 focus:border-gold/40'}`}
          />
        </div>
        <div className="space-y-1.5">
          <label className="ds-label block">{data.isRental ? 'Return time' : 'Arrival Time *'}</label>
          <input 
            type="time"
            value={data.arr && data.arr.includes('T') ? data.arr.split('T')[1] : ""}
            onChange={e => {
              setData({ ...data, arr: syncArrivalDate(data.dep, withTime(data.arr, e.target.value)) });
              if (e.target.value) setError(null);
            }}
            className={`${FIELD} ${error && !data.arr ? 'border-rose-300' : 'border-app-border/60 focus:ring-gold/5 focus:border-gold/40'}`}
          />
        </div>
      </div>

      <ItemDetailsSection>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormInput label="Seat" value={data.seatNumber || ''} onChange={seatNumber => setData({ ...data, seatNumber })} />
          <FormInput label="Baggage allowance" value={data.baggageAllowance || ''} onChange={baggageAllowance => setData({ ...data, baggageAllowance })} />
          <FormInput label="Travel class" value={data.cabinClass || ''} onChange={cabinClass => setData({ ...data, cabinClass })} />
          <FormInput label="Terminal / platform" value={data.terminal || ''} onChange={terminal => setData({ ...data, terminal })} />
          <FormInput label="Via" value={data.via || ''} onChange={via => setData({ ...data, via })} />
          <FormInput label="Arrive at departure point" value={data.arriveAtAirport || ''} onChange={arriveAtAirport => setData({ ...data, arriveAtAirport })} placeholder="e.g. 11:30 am" />
          {!data.isRental && <FormInput label="Duration (if known)" value={data.duration || ''} onChange={duration => setData({ ...data, duration })} placeholder="e.g. 2h 15m" />}
        </div>
      </ItemDetailsSection>
        <div className="space-y-1.5 mb-4">
          <label className="ds-label block">Notes</label>
          <textarea
            value={data.notes || ""}
            onChange={(e) => setData({ ...data, notes: e.target.value })}
            placeholder={isFlight ? 'Terminal, gate, seat number, or other details…' : data.mode === 'Car' || data.mode === 'Taxi' ? 'Pick-up instructions or other details…' : 'Platform, seat number, or other details…'}
            className={`${FIELD} ${fieldBorder(false, true)} min-h-[80px] resize-none`}
          />
        </div>
      {tripConfig?.id !== sourceConfig?.id && <p role="status" className="text-sm text-app-sub">Saving to {tripConfig?.title}. This journey leaves or rejoins that branch.</p>}
      <TravellerAssignment travellers={assignable(sourceConfig).filter(p=>tripConfig?.memberIds.includes(p.id) || data.participantIds?.includes(p.id))} value={data.participantIds} onChange={participantIds => setData(d => ({ ...d, participantIds }))} />
      <BookingDetailsForm booked={hasBookingDetails(data)} onBookedChange={booked => setData({ ...data, booked })}
        source={data.bookingService || ""} onSourceChange={bookingService => setData({ ...data, bookingService })}
        reference={data.ref || ""} onReferenceChange={value => setData({ ...data, ref: value })}
        notes={data.bookingNotes || ""} onNotesChange={bookingNotes => setData({ ...data, bookingNotes })}
        documents={data.documents} sourcePlaceholder={bookingSourcePlaceholder[data.mode] || 'Where did you book?'}>
        {tripConfig && currentUser && (
          <CostAndLogSection
            sourceId={data.id} sourceType="transport"
            costAmount={data.costAmount}
            costCurrency={data.costCurrency || expenseBaseCurrency(tripConfig)}
            costKind={data.costKind}
            paidStatus={data.paidStatus || ''}
            depositAmount={data.depositAmount}
            depositCurrency={data.depositCurrency}
            note={expenseNote}
            logToExpenses={logToExpenses}
            paidBy={paidBy}
            split={costSplit}
            expenseLogId={data.expenseLogId}
            travellers={(tripConfig.travellers || []).filter((u: User) => u.inviteStatus !== 'pending')}
            baseCurrency={expenseBaseCurrency(tripConfig)}
            currentUser={currentUser}
            expenses={tripConfig.expenses || []}
            onChange={(patch) => {
              if ('costAmount' in patch) setData(d => ({ ...d, costAmount: patch.costAmount }));
              if (patch.costCurrency !== undefined) setData(d => ({ ...d, costCurrency: patch.costCurrency }));
              if (patch.costKind !== undefined) setData(d => ({ ...d, costKind: patch.costKind }));
              if (patch.paidStatus !== undefined) setData(d => ({ ...d, paidStatus: patch.paidStatus as any }));
              if ('depositAmount' in patch) setData(d => ({ ...d, depositAmount: patch.depositAmount }));
              if (patch.depositCurrency !== undefined) setData(d => ({ ...d, depositCurrency: patch.depositCurrency }));
              if (patch.note !== undefined) setExpenseNote(patch.note);
              if (patch.logToExpenses !== undefined) setLogToExpenses(patch.logToExpenses);
              if (patch.paidBy !== undefined) setPaidBy(patch.paidBy);
              if (patch.split !== undefined) setCostSplit(patch.split);
            }}
          />
        )}
      </BookingDetailsForm>

      {!data.isRental && hasBookingDetails(data) && <div className="space-y-1.5">
        <label className="flex items-center gap-3 min-h-11 text-sm text-app-text">
          <input type="checkbox" checked={!!data.isConnection} disabled={!data.isConnection && !connectionLeg}
            onChange={e => setData(d => ({ ...d, isConnection: e.target.checked }))}
            className="w-4 h-4 accent-gold" />
          Show with previous leg (same booking)
        </label>
        <p role="status" className="text-sm text-app-sub">{connectionLeg
          ? `${data.isConnection ? 'Will show together with' : 'Select to show together with'} ${connectionLeg.fromCity || connectionLeg.from} → ${connectionLeg.toCity || connectionLeg.to} after saving.`
          : 'No previous leg with this booking reference. Check the reference in Booking & payment for both legs.'}</p>
        <p className="text-sm text-app-sub">This groups the saved details. Layovers still appear between separate bookings.</p>
      </div>}

      <div className="flex gap-2 pt-4">
        <button onClick={onCancel} className="flex-1 p-3 rounded-xl border border-app-border text-[11px] font-black uppercase tracking-widest text-app-sub hover:bg-app-faint transition">Cancel</button>
        <button onClick={handleSave} className="flex-1 p-3 rounded-xl bg-gold text-white text-[11px] font-black uppercase tracking-widest hover:brightness-110 transition shadow-lg shadow-gold/20">{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </fieldset>
  );
}

// Shared portal shell for editing a single card (accommodation/transport entry)
// — centred modal over a dimmed backdrop, matching the Trips-tab add/view
// modals. Per the AnimatePresence+createPortal gotcha documented elsewhere in
// this file, this stays a plain conditional render with no AnimatePresence
// wrapping it (no exit animation, but reliable mount/unmount).
export function EditModal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return <Modal title={title} onClose={onClose}>{children}</Modal>;
}

// Shared delete-confirmation popup — styling matches the existing destination
// delete confirm (TripManagementForm's confirmingDeleteDest), just as an actual
// portal popup instead of an inline card swap.
export function ConfirmDeleteModal({ title, message, extraOption, onConfirm, onCancel, isDeleting, confirmLabel = 'Delete', busyLabel = 'Deleting…' }: {
  title: string; message: string;
  /** An optional second thing the delete can take with it — offered as a
   *  checkbox rather than assumed either way. Unticked by default: the
   *  cautious answer should be the one you get by not reading carefully. */
  extraOption?: { label: string; hint?: string; /** Shown instead of the hint, with the option locked off. */ disabledReason?: string | null };
  onConfirm: (alsoRemoveExtra: boolean) => void | boolean | Promise<void | boolean>;
  onCancel: () => void; isDeleting?: boolean;
  /** For a removal that isn't a deletion (e.g. a traveller leaving the trip). */
  confirmLabel?: string; busyLabel?: string;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alsoRemoveExtra, setAlsoRemoveExtra] = useState(false);
  const busy = pending || isDeleting;
  const handleConfirm = async () => {
    if (busy) return;
    setPending(true);
    setError(null);
    try {
      if (await onConfirm(alsoRemoveExtra && !extraOption?.disabledReason) === false) setError('Could not delete this item. Please try again.');
    } catch (err) {
      setError(userMessage(err, 'Could not delete this item. Please try again.'));
    } finally {
      setPending(false);
    }
  };
  return createPortal(
    // Solid (not translucent/blurred) backdrop, deliberately — this is
    // routinely opened from *inside* an already-open portal modal (delete
    // buttons on item view/edit modals, the destination editor, etc.), and
    // a translucent bg-app-bg/80 + blur here stacked on top of another
    // modal's own translucent backdrop reads as "modal peeking through
    // modal" rather than one clean screen. A fully opaque backdrop always
    // completely covers whatever's behind it, whether that's the plain
    // page or another modal — never a partial double-layer.
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      onClick={() => { if (!busy) onCancel(); }}
      className="fixed inset-0 z-[210] flex items-center justify-center p-6 bg-app-bg"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-app-bg border border-app-border rounded-3xl p-8 max-w-sm w-full shadow-2xl"
      >
        <div className="flex flex-col items-center text-center gap-4">
          <div className="w-10 h-10 bg-rose-50 rounded-full flex items-center justify-center">
            <Trash2 className="w-4 h-4 text-rose-500" />
          </div>
          <div>
            <p className="font-serif text-2xl font-bold text-app-text">{title}</p>
            <p className="text-xs text-app-sub mt-1">{message}</p>
            {error && <p role="alert" className="text-xs text-rose-600 mt-2">{error}</p>}
          </div>
          {extraOption && (
            <button
              type="button"
              onClick={() => setAlsoRemoveExtra(v => !v)}
              disabled={busy || !!extraOption.disabledReason}
              aria-disabled={!!extraOption.disabledReason}
              className={`w-full flex items-start gap-3 text-left rounded-xl border border-app-border/60 bg-app-faint/60 px-3.5 py-3 transition-colors ${
                extraOption.disabledReason ? 'cursor-not-allowed' : 'hover:border-app-sub/40'
              }`}
            >
              <span className={`mt-0.5 w-4 h-4 rounded-[5px] border shrink-0 flex items-center justify-center transition-all ${
                extraOption.disabledReason ? 'border-app-border/50 bg-app-faint'
                  : alsoRemoveExtra ? 'bg-rose-500 border-rose-500 text-white' : 'border-app-border bg-white'
              }`}>
                {alsoRemoveExtra && !extraOption.disabledReason && <Check className="w-3 h-3 stroke-[3]" />}
              </span>
              <span className="min-w-0">
                <span className={`block text-xs font-bold ${extraOption.disabledReason ? 'text-app-sub/60' : 'text-app-text'}`}>{extraOption.label}</span>
                {(extraOption.disabledReason || extraOption.hint) && (
                  <span className={`block text-sm font-medium leading-snug mt-0.5 ${extraOption.disabledReason ? 'text-app-sub' : 'text-app-sub/70'}`}>
                    {extraOption.disabledReason || extraOption.hint}
                  </span>
                )}
              </span>
            </button>
          )}
          <div className="flex gap-2 w-full">
            <button
              onClick={onCancel}
              disabled={busy}
              className="flex-1 py-2.5 rounded-xl border border-app-border/60 text-[11px] font-black uppercase tracking-widest text-app-sub hover:bg-app-faint transition-colors"
            >
              Keep it
            </button>
            <button
              onClick={handleConfirm}
              disabled={busy}
              className="flex-1 py-2.5 rounded-xl bg-rose-500 text-white text-[11px] font-black uppercase tracking-widest hover:brightness-110 transition disabled:opacity-50"
            >
              {busy ? busyLabel : confirmLabel}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>,
    document.getElementById('import-modal-root') ?? document.body
  );
}

function AccommodationForm({ accommodation, onSave, onCancel, country, defaultDates, tripConfig, currentUser, isNew = false, onSavingChange }: { accommodation: Accommodation; onSave: (a: Accommodation, previous: Accommodation | null) => void | boolean | Promise<void | boolean>; onCancel: () => void; country?: string; defaultDates?: {start: string, end: string}; tripConfig?: TripConfig; currentUser?: User; isNew?: boolean; onSavingChange?: (saving: boolean) => void }) {
  const originalAccommodation = useRef(isNew ? null : accommodation).current;
  const [saving, setSaving] = useState(false);
  // Preserve the linked charge's payer/split without treating it as the stay's total.
  const linkedExpense = (accommodation as any)?.expenseLogId
    ? (tripConfig?.expenses || []).find(e => e.id === (accommodation as any).expenseLogId && !e.itineraryDeleted)
    : undefined;

  const [data, setData] = useState<Day['accommodation']>(() => {
    const d: any = { ...accommodation };
    if (!d.id) d.id = crypto.randomUUID();
    if (isNew && !d.createdAt) d.createdAt = new Date().toISOString();
    if (!linkedExpense && !d.costCurrency) d.costCurrency = expenseBaseCurrency(tripConfig);
    return d;
  });
  const [errorMsg, setErrorMsg] = useState('');
  const [logToExpenses, setLogToExpenses] = useState(!!linkedExpense);
  const [paidBy, setPaidBy] = useState(linkedExpense?.paid_by || currentUser?.id || '');
  const [costSplit, setCostSplit] = useState<string[]>(
    linkedExpense?.split?.length ? linkedExpense.split : (currentUser?.id ? [currentUser.id] : [])
  );
  const [expenseNote, setExpenseNote] = useState<string>(linkedExpense?.note || '');

  useEffect(() => {
    let checkInStr = data.checkIn || '';
    if (checkInStr && !checkInStr.includes('T') && defaultDates?.start) {
      checkInStr = `${defaultDates.start}T${checkInStr}`;
      setData(prev => ({...prev, checkIn: checkInStr}));
    } else if (!checkInStr && defaultDates?.start) {
      checkInStr = `${defaultDates.start}T14:00`;
      setData(prev => ({...prev, checkIn: checkInStr}));
    }
    
    let checkOutStr = data.checkOut || '';
    if (checkOutStr && !checkOutStr.includes('T') && defaultDates?.end) {
      checkOutStr = `${defaultDates.end}T${checkOutStr}`;
      setData(prev => ({...prev, checkOut: checkOutStr}));
    } else if (!checkOutStr && defaultDates?.end) {
      checkOutStr = `${defaultDates.end}T10:00`;
      setData(prev => ({...prev, checkOut: checkOutStr}));
    }
  }, []);

  const handleSave = async () => {
    if (saving) return;
    if (!data.name || data.name.trim() === '') {
      setErrorMsg("Accommodation name is required.");
      return;
    }
    if (!data.checkIn || !data.checkOut) {
      setErrorMsg("Check in and check out dates are required.");
      return;
    }
    if (logToExpenses && !data.expenseLogId) {
      const amt = data.costAmount;
      if (amt == null || isNaN(amt) || amt <= 0 || !/^\d+(\.\d{1,2})?$/.test(String(Math.round(amt * 100) / 100))) {
        setErrorMsg("A valid cost (up to 2 decimal places) is required to log as an expense.");
        return;
      }
    }
    if (!linkedExpense && (data as any).paidStatus === 'Deposit paid' && !(typeof (data as any).depositAmount === 'number' && (data as any).depositAmount > 0)) {
      setErrorMsg("Enter the deposit amount paid, or change the payment status.");
      return;
    }
    setErrorMsg('');
    setSaving(true);
    onSavingChange?.(true);
    try {
      const saved = await onSave({ ...data, _logToExpenses: logToExpenses, _paidBy: paidBy, _costSplit: costSplit, _expenseNote: expenseNote } as any, originalAccommodation);
      if (saved === false) setErrorMsg('Could not save accommodation. Your entry is still here; please try again.');
    } catch (err) {
      setErrorMsg(userMessage(err, 'Could not save accommodation. Please try again.'));
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  };

  const { ref: accomRef } = usePlacesWidget({
    onPlaceSelected: (place) => {
      setData(d => ({ 
        ...d, 
        name: place.name || d.name, 
        address: place.formatted_address || d.address,
        mapUrl: place.url || d.mapUrl
      }));
    },
    options: {
      fields: ["name", "formatted_address", "url"],
      types: ["lodging"],
      componentRestrictions: countryCode(country || '') ? { country: countryCode(country || '')! } : undefined
    }
  });

  const { ref: addrRef } = usePlacesWidget({
    onPlaceSelected: (place) => {
      setData(d => ({ 
        ...d, 
        address: place.formatted_address || d.address,
        mapUrl: place.url || d.mapUrl
      }));
    },
    options: {
      fields: ["formatted_address", "url"],
      componentRestrictions: countryCode(country || '') ? { country: countryCode(country || '')! } : undefined
    }
  });

  return (
    <fieldset disabled={saving} className="space-y-4 min-w-0">
      <SubcategoryPicker l1="accommodation" label="Property type" value={data.category}
        onChange={category => setData({ ...data, category })} />
        <div className="space-y-1.5 flex-1 min-w-0">
          <label className="ds-label block">Accommodation Name</label>
          <input
            ref={accomRef as any}
            value={data.name}
            onChange={(e) => setData({ ...data, name: e.target.value })}
            className={`${FIELD} ${fieldBorder(false, true)}`}
            placeholder="Search for hotel..."
          />
        </div>
      <div className="grid grid-cols-2 gap-4">
        <FormInput label="Check In Date" value={data.checkIn ? data.checkIn.split('T')[0] : ''} onChange={(v) => setData({ ...data, checkIn: `${v}T${data.checkIn && data.checkIn.includes('T') ? data.checkIn.split('T')[1] : '14:00'}` })} type="date" />
        <FormInput label="Check In Time" value={data.checkIn && data.checkIn.includes('T') ? data.checkIn.split('T')[1] : ''} onChange={(v) => setData({ ...data, checkIn: `${data.checkIn ? data.checkIn.split('T')[0] : (defaultDates?.start || '')}T${v}` })} type="time" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormInput label="Check Out Date" value={data.checkOut ? data.checkOut.split('T')[0] : ''} onChange={(v) => setData({ ...data, checkOut: `${v}T${data.checkOut && data.checkOut.includes('T') ? data.checkOut.split('T')[1] : '10:00'}` })} type="date" />
        <FormInput label="Check Out Time" value={data.checkOut && data.checkOut.includes('T') ? data.checkOut.split('T')[1] : ''} onChange={(v) => setData({ ...data, checkOut: `${data.checkOut ? data.checkOut.split('T')[0] : (defaultDates?.end || '')}T${v}` })} type="time" />
      </div>
      <div className="space-y-1.5">
        <label className="ds-label block">Address</label>
        <input 
          ref={addrRef as any}
          value={data.address}
          onChange={(e) => setData({ ...data, address: e.target.value })}
          className={`${FIELD} ${fieldBorder(false, true)}`}
          placeholder="Enter address..."
        />
      </div>
      <ItemDetailsSection>
        <FormInput label="Area" value={data.area || ''} onChange={area => setData({ ...data, area })} />
        <FormInput label="Property stars" type="number" value={data.stars ?? ''} onChange={stars => setData({ ...data, stars: stars === '' ? null : Math.max(0, Math.min(5, Number(stars))) })} />
      </ItemDetailsSection>
        <div className="space-y-1.5 mb-4">
          <label className="ds-label block">Notes</label>
          <textarea
            value={data.notes || ""}
            onChange={(e) => setData({ ...data, notes: e.target.value })}
            placeholder="Room type, breakfast included, or other details..."
            className={`${FIELD} ${fieldBorder(false, true)} min-h-[80px] resize-none`}
          />
        </div>
      <TravellerAssignment travellers={assignable(tripConfig)} value={data.participantIds} onChange={participantIds => setData(d => ({ ...d, participantIds }))} />
      <BookingDetailsForm booked={hasBookingDetails(data)} onBookedChange={booked => setData({ ...data, booked })}
        source={data.bookingService || ""} onSourceChange={bookingService => setData({ ...data, bookingService })}
        reference={data.confirmRef || ""} onReferenceChange={value => setData({ ...data, confirmRef: value })}
        notes={data.bookingNotes || ""} onNotesChange={bookingNotes => setData({ ...data, bookingNotes })}
        documents={data.documents} sourcePlaceholder={'e.g. Booking.com'}>
        {tripConfig && currentUser && (
          <CostAndLogSection
            sourceId={data.id} sourceType="accommodation"
            costAmount={data.costAmount}
            costCurrency={data.costCurrency || expenseBaseCurrency(tripConfig)}
            costKind={data.costKind}
            paidStatus={data.paidStatus || ''}
            depositAmount={(data as any).depositAmount}
            depositCurrency={(data as any).depositCurrency}
            note={expenseNote}
            logToExpenses={logToExpenses}
            paidBy={paidBy}
            split={costSplit}
            expenseLogId={data.expenseLogId}
            travellers={(tripConfig.travellers || []).filter((u: User) => u.inviteStatus !== 'pending')}
            baseCurrency={expenseBaseCurrency(tripConfig)}
            currentUser={currentUser}
            expenses={tripConfig.expenses || []}
            onChange={(patch) => {
              if ('costAmount' in patch) setData(d => ({ ...d, costAmount: patch.costAmount }));
              if (patch.costCurrency !== undefined) setData(d => ({ ...d, costCurrency: patch.costCurrency }));
              if (patch.costKind !== undefined) setData(d => ({ ...d, costKind: patch.costKind }));
              if (patch.paidStatus !== undefined) setData(d => ({ ...d, paidStatus: patch.paidStatus as any }));
              if ('depositAmount' in patch) setData(d => ({ ...(d as any), depositAmount: patch.depositAmount }));
              if (patch.depositCurrency !== undefined) setData(d => ({ ...(d as any), depositCurrency: patch.depositCurrency }));
              if (patch.note !== undefined) setExpenseNote(patch.note);
              if (patch.logToExpenses !== undefined) setLogToExpenses(patch.logToExpenses);
              if (patch.paidBy !== undefined) setPaidBy(patch.paidBy);
              if (patch.split !== undefined) setCostSplit(patch.split);
            }}
          />
        )}
      </BookingDetailsForm>

      {errorMsg && <p role="alert" className={`${ERROR_LINE} text-center mt-2`}>{errorMsg}</p>}

      <div className="flex gap-2 pt-4">
        <button onClick={onCancel} className="flex-1 p-3 rounded-xl border border-app-border text-[11px] font-black uppercase tracking-widest text-app-sub hover:bg-app-faint transition">Cancel</button>
        <button onClick={handleSave} className="flex-1 p-3 rounded-xl bg-gold text-white text-[11px] font-black uppercase tracking-widest hover:brightness-110 transition">{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </fieldset>
  );
}

/** Google star rating, rendered inline after a category label as
 *  "· ★ 4.6 (1,234)". Returns null when there's no rating, so callers can
 *  drop it straight after the label without guarding themselves. */
export function InlineRating({ rating, total }: { rating?: number | null; total?: number | null }) {
  if (rating == null) return null;
  return (
    <>
      <span className="mx-1.5 text-app-sub/30">•</span>
      <span className="text-amber-500">★</span>
      <span className="ml-0.5 text-app-sub/70">{rating.toFixed(1)}</span>
      {total != null && <span className="ml-1 text-app-sub/40 normal-case tracking-normal font-semibold">({total.toLocaleString()})</span>}
    </>
  );
}

/** Compact refine input — one row, expands only while focused. Replaces the
 *  boxed two-row "Refine Suggestions" panel the full-page Explore view used,
 *  which was far too chunky to sit inside a section's own carousel. */
function RefineRow({ value, onChange, onSubmit, busy, placeholder }: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  busy: boolean;
  placeholder: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onSubmit(); } }}
        placeholder={placeholder}
        className="flex-1 min-w-0 bg-white/70 border border-app-border/40 rounded-full px-4 py-2 text-xs text-app-text placeholder:text-app-sub/40 outline-none focus:border-gold/40 focus:bg-white transition"
      />
      <button aria-label="Regenerate suggestions"
        onClick={onSubmit}
        disabled={busy}
        title="Regenerate suggestions"
        className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full bg-gold/10 text-gold border border-gold/20 hover:bg-gold/20 transition-colors disabled:opacity-40"
      >
        {busy ? <RotateCcw className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

/**
 * Talisman Explorer — the inline suggestion carousel that opens under a
 * section when its "Discover new places" CTA is tapped. Same data path as
 * the old full-section Explore view it replaces (getRecommendations +
 * per-user/day/type Firestore cache + a Places pass to attach real Google
 * ratings), but presented as a horizontal snap carousel modelled on the
 * scheduler's "Pick a spot" cards, and *consuming*: adding a card saves it
 * to the Shortlist and removes it from the carousel, so what's left is
 * always still-unclaimed.
 */
function ExplorerCarousel({ city, type, onAdd, tripId, dayDate, userId, existingNames = [], onClose }: {
  city: string;
  type: 'attractions' | 'food' | 'nightlife' | 'shopping';
  onAdd: (item: any) => Promise<boolean>;
  tripId: string;
  dayDate: string;
  userId: string;
  /** Names already saved in this section — filtered out of the carousel so
   *  it never re-offers something the list already has. Matters because the
   *  Firestore explore cache outlives the items added from it, so a cached
   *  set would otherwise keep proposing spots you took days ago. */
  existingNames?: string[];
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [recs, setRecs] = useState<any[]>([]);
  // Held while the suggestions are checked against Places, so nothing is
  // shown that then turns out not to exist.
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refinement, setRefinement] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Names already taken from this carousel — kept so a refresh doesn't
  // re-offer something the user just added to the list below.
  const [claimed, setClaimed] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const entryIds = useRef<Record<string, string>>({});

  const handleAdd = async (item: any) => {
    setAdding(item.name);
    setAddError(null);
    try {
      const saved = await onAdd({ ...item, _entryId: entryIds.current[item.name] ||= crypto.randomUUID() });
      if (saved) setClaimed(prev => new Set(prev).add(item.name));
      else setAddError('Could not save this spot. Please try again.');
    } catch (err) {
      setAddError(userMessage(err, 'Could not save this spot. Please try again.'));
    } finally {
      setAdding(null);
    }
  };

  const cacheDocRef = tripId && dayDate && userId
    ? doc(db, 'trips', tripId, 'exploreCache', `${userId}_${dayDate}_${type}`)
    : null;

  const fetchRecs = async (prompt: string = "", forceRefresh = false) => {
    setIsRefreshing(true);
    setError(null);
    try {
      if (!forceRefresh && cacheDocRef) {
        const cached = await getDoc(cacheDocRef);
        if (cached.exists() && cached.data()?.recs?.length > 0) {
          setRecs(cached.data()!.recs);
          setLoading(false);
          setIsRefreshing(false);
          return;
        }
      }
      const data = await getRecommendations(city, type, prompt);
      setRecs(data);
      if (cacheDocRef && data.length > 0) {
        setDoc(cacheDocRef, { recs: data, generatedAt: new Date().toISOString() }).catch(() => {});
      }
    } catch (e: any) {
      const raw: string = e?.message ?? '';
      setError(!raw || /^internal$/i.test(raw.trim()) ? 'Could not load suggestions. Please try again.' : raw);
    } finally {
      setIsRefreshing(false);
      setLoading(false);
    }
  };

  // Once per mount only. The carousel is mounted only while open, so
  // closing and reopening is a genuine refetch. Keying this on
  // city/dayDate (as the full-page Explore view did) meant any re-render
  // that changed the active day silently replaced a *refined* result set
  // with the generic cached one — which reads exactly like "refine did
  // nothing", intermittently, depending on the destination.
  useEffect(() => {
    setLoading(true);
    fetchRecs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Check every suggestion against Google Places, and drop the ones that
   * aren't there.
   *
   * The model writes these from memory: name, address and — until now — the
   * star rating too, which the schema itself called an estimate and the card
   * showed as if it were Google's. Most are real. Some are a real restaurant
   * on the wrong street, and some are a plausible-sounding place that has
   * never existed. Either way the traveller finds out by turning up.
   *
   * This pass used to fetch the rating and nothing else, keeping the
   * suggestion whatever came back — so a lookup that found nothing left the
   * invented rating on display. Now the lookup is the test (confirmPlaces),
   * and a suggestion it can't confirm isn't offered at all.
   *
   * Results are written back to the cache, so the quota is spent once per
   * destination per day rather than on every open. The carousel holds its
   * loading state until they land — cards that appear and then quietly vanish
   * are worse than a moment's wait.
   */
  useEffect(() => {
    // placeChecked, not the older ratingChecked: every suggestion cached before
    // this pass existed carries ratingChecked from the rating-only lookup that
    // never tested whether the place was real. Reading a new flag is what makes
    // those caches get verified once rather than keeping their unverified
    // suggestions until the cache is replaced for some other reason.
    if (!recs.length || recs.every(r => r.placeChecked)) return;
    let cancelled = false;
    setVerifying(true);
    (async () => {
      try {
        const confirmed = await confirmPlaces(recs.filter(r => !r.placeChecked));
        // null means Places itself was unavailable, which is no verdict on the
        // suggestions: keep them but hide the model's unverified rating.
        // placeChecked stops this retrying forever for this mount.
        if (cancelled) return;
        const merged = confirmed === null
          ? recs.map(rec => ({ ...rec, rating: undefined, ratingTotal: undefined, placeChecked: true }))
          : recs.flatMap(rec => {
              if (rec.placeChecked) return [rec];
              const facts = confirmed.get(String(rec.name || '').trim());
              if (!facts) return [];
              // rating/ratingTotal are spread from facts, so a real place with
              // no Google rating loses the estimate it arrived with.
              return [{ ...rec, placeChecked: true, rating: undefined, ratingTotal: undefined, ...facts }];
            });
        setRecs(merged);
        // Only a real verdict is written back. When Places was unavailable the
        // placeChecked marks above exist to stop this effect looping for THIS
        // mount — persisting them would record unverified suggestions as
        // verified in the shared cache, so one failed library load meant that
        // day's suggestions were never checked again.
        if (cacheDocRef && confirmed !== null) setDoc(cacheDocRef, { recs: merged, generatedAt: new Date().toISOString() }, { merge: true }).catch(() => {});
      } finally {
        if (!cancelled) setVerifying(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recs]);

  const alreadySaved = new Set(existingNames.map(n => n.trim().toLowerCase()));
  const visible = recs.filter(r => !claimed.has(r.name) && !alreadySaved.has(String(r.name || '').trim().toLowerCase()));

  const placeholder = type === 'food'
    ? "Local street food, fine dining…"
    : type === 'nightlife'
    ? "Rooftop bars, live music…"
    : type === 'shopping'
    ? "Markets, boutiques, souvenirs…"
    : "Off the beaten track, architecture…";

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      className="overflow-hidden"
    >
      <div className="rounded-2xl border border-gold/35 bg-gold-soft/70 backdrop-blur-xl p-3 space-y-3 mt-1">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 ds-label text-gold">
            <Sparkles className="w-3.5 h-3.5" /> Talisman Explorer
          </div>
          <button aria-label="Close Explorer" onClick={onClose} disabled={adding !== null} title="Close Explorer" className="p-1 -m-1 text-app-sub/40 hover:text-app-text transition-colors disabled:opacity-40">
            <X className="w-4 h-4" />
          </button>
        </div>

        <RefineRow
          value={refinement}
          onChange={setRefinement}
          onSubmit={() => fetchRecs(refinement, true)}
          busy={isRefreshing || adding !== null}
          placeholder={placeholder}
        />

        {addError && <p role="alert" className="text-xs text-rose-600 leading-relaxed">{addError}</p>}
        {loading || verifying ? (
          <div className="flex flex-col items-center gap-2 py-6">
            <ModernCompass size={56} />
            <div className="text-sm text-app-sub">Curating spots…</div>
          </div>
        ) : error ? (
          <div className="py-5 flex flex-col items-center gap-2 text-center">
            <p className="text-xs text-app-sub/70 max-w-[260px] leading-relaxed">{error}</p>
            <button onClick={() => fetchRecs(refinement, true)} className="ds-label text-gold hover:text-gold/70 transition-colors">
              Try again
            </button>
          </div>
        ) : visible.length === 0 ? (
          <div className="py-5 text-center ds-label">
            {/* Two different nothings. "That's all of them" is a lie when the
                list came back empty because none of the suggestions could be
                found on the map — refining is still the answer, but the
                traveller should know nothing was withheld from them. */}
            {recs.length === 0
              ? 'No spots we could place on the map — refine above'
              : "That's all of them — refine above for more"}
          </div>
        ) : (
          <div className="flex items-stretch gap-3 -mx-3 px-3 overflow-x-auto no-scrollbar pb-1 snap-x snap-mandatory">
            <AnimatePresence mode="popLayout" initial={false}>
              {visible.map((r) => (
                <motion.div
                  key={r.name}
                  layout
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.22 } }}
                  transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                  className="shrink-0 w-[86%] snap-center rounded-2xl border border-app-border/40 bg-white overflow-hidden shadow-sm"
                >
                  {/* Laid out as a Shortlist card, because that is what it
                      becomes the moment it is added — same name weight, same
                      ds-label meta line with the shared InlineRating on it,
                      same muted body copy. It used to have its own voice
                      (serif italic name, a bespoke star block, an address set
                      in label type) which made the carousel read as a
                      different app arriving inside this one. */}
                  <div className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="flex-1 text-sm font-bold text-app-text leading-snug text-balance line-clamp-2">{r.name}</h4>
                      <button
                        onClick={() => handleAdd(r)}
                        disabled={adding !== null}
                        aria-label={adding === r.name ? `Saving ${r.name}` : `Add ${r.name} to Shortlist`}
                        title="Add to Shortlist"
                        className="w-7 h-7 rounded-full bg-gold text-white flex items-center justify-center shrink-0 shadow-sm hover:brightness-110 active:scale-95 transition disabled:opacity-40"
                      >
                        <Plus className="w-4 h-4 stroke-[3]" />
                      </button>
                    </div>
                    {(r.category || r.rating != null) && (
                      <div className="ds-label">
                        {r.category}
                        <InlineRating rating={r.rating} total={r.ratingTotal} />
                      </div>
                    )}
                    {r.address && (
                      <div className="flex items-start gap-1.5 text-app-sub/60">
                        <MapPin className="w-3 h-3 shrink-0 mt-0.5" />
                        <span className="text-xs leading-relaxed line-clamp-2">{r.address}</span>
                      </div>
                    )}
                    {r.desc && <p className="text-xs text-app-sub/70 leading-relaxed line-clamp-3">{r.desc}</p>}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </motion.div>
  );
}

/** The CTA that opens ExplorerCarousel, sat at the foot of each section. */
function ExplorerCta({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      // Glass at 70%, the same family as the trip cards at 60% — translucent
      // cream over the drifting streaks rather than a plate laid on top of
      // them. The old opaque white plate it used to sit on was a hard-edged
      // rectangle over the tint; this holds its colour without one, and gold
      // text still reads on it.
      className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl border-2 border-dashed border-gold/50 bg-gold-soft/70 backdrop-blur-xl text-gold hover:bg-gold-soft/90 hover:border-gold/70 active:scale-[0.99] transition"
    >
      <Sparkles className="w-4 h-4" />
      <span className="text-sm font-normal">Discover new places with <span className="font-bold">Talisman Explorer</span></span>
    </button>
  );
}

export function CostAndLogSection({
  costAmount, costCurrency, costKind, paidStatus,
  depositAmount, depositCurrency, note,
  logToExpenses, paidBy, split,
  expenseLogId, sourceId, sourceType,
  travellers, baseCurrency, currentUser, expenses,
  onChange,
  hideExpenseLink = false,
  showPaymentStatus = true,
  allowExpenseLogging = true,
}: {
  costAmount: number | undefined;
  costCurrency: string;
  costKind?: BookingCostKind;
  paidStatus: string;
  depositAmount?: number;
  depositCurrency?: string;
  note?: string;
  logToExpenses: boolean;
  paidBy: string;
  split: string[];
  expenseLogId?: string;
  sourceId?: string;
  sourceType?: string;
  travellers: User[];
  baseCurrency: string;
  currentUser: User;
  expenses: Expense[];
  onChange: (patch: {
    costAmount?: number; costCurrency?: string; costKind?: BookingCostKind; paidStatus?: string;
    depositAmount?: number; depositCurrency?: string; note?: string;
    logToExpenses?: boolean; paidBy?: string; split?: string[];
  }) => void;
  hideExpenseLink?: boolean;
  showPaymentStatus?: boolean;
  allowExpenseLogging?: boolean;
}) {
  const CURRENCIES = ['USD','GBP','EUR','AUD','SGD','JPY','CAD','NZD','CHF','HKD','THB','INR','MYR','IDR','PHP','VND'];
  const expenseScope = React.useContext(ExpenseScope);
  const expenseStillExists = expenseLogId ? expenses.some(e => e.id === expenseLogId) : false;
  const isLogged = !!(expenseLogId && expenseStillExists);
  const effectiveKind = costKind || costKindForPaymentStatus(paidStatus as any);
  const canLogCharge = allowExpenseLogging && (effectiveKind === 'paid' || effectiveKind === 'deposit' || effectiveKind === 'due');

  const linked = expenses.find(expense => !expense.itineraryDeleted && expense.id === expenseLogId)
    || expenses.find(expense => !expense.itineraryDeleted && !!sourceId && expense.sourceId === sourceId
      && (expense.sourceType === sourceType || (sourceType === 'other' && expense.sourceType === 'note')));
  if (expenseLogId && !linked) return <p className="text-sm text-app-sub">Linked expense unavailable. Reopen Expenses before changing cost or payment.</p>;
  if (linked) {
    return <div className="pt-3 border-t border-app-border/20 mt-1 space-y-3">
      <div className="flex items-start gap-3 p-3 rounded-xl bg-app-faint border border-app-border/30">
        <span className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center shrink-0"><DollarSign className="w-5 h-5 text-gold" aria-hidden="true" /></span>
        <div className="min-w-0 space-y-1">
          <h3 className="text-sm font-medium text-app-sub">Linked expense</h3>
          <p className="text-sm text-app-text">{linked.currency} {linked.amount.toLocaleString()}</p>
          {linked.paidStatus && <p className={`text-sm ${linked.paidStatus === 'Deposit paid' ? 'text-amber-800' : 'text-app-sub'}`}>{BOOKING_COST_LABELS[costKindForPaymentStatus(linked.paidStatus)].status}</p>}
          {linked.paidStatus === 'Deposit paid' && linked.depositAmount != null && <p className="text-sm text-app-sub">Deposit paid: {linked.depositCurrency || linked.currency} {linked.depositAmount.toLocaleString()}</p>}
      {!hideExpenseLink && <button type="button" className="text-sm text-gold hover:underline min-h-11"
        onClick={() => window.dispatchEvent(new CustomEvent(linked.paid_by === currentUser.id ? 'open-linked-expense-edit' : 'open-linked-expense', { detail: {id:linked.id,scope:expenseScope} }))}>
        {linked.paid_by === currentUser.id ? 'Edit expense' : 'View expense'}
      </button>}
        </div>
      </div>
    </div>;
  }

  return (
    <div className="pt-3 border-t border-app-border/20 mt-1 space-y-3">
      <h3 className="text-sm font-medium text-app-sub">Cost &amp; payment</h3>
      <div className="space-y-1.5">
        <div className="ds-label">{BOOKING_COST_LABELS[effectiveKind].amount}</div>
        <div className="grid grid-cols-[1fr_auto] gap-2">
          <input
            type="number"
            aria-label={BOOKING_COST_LABELS[effectiveKind].amount}
            min="0"
            step="0.01"
            value={costAmount ?? ''}
            onChange={e => onChange({ costAmount: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
            placeholder="0.00"
            className={`${FIELD} ${fieldBorder(false, true)}`}
          />
          <select
            aria-label="Cost currency"
            value={costCurrency || baseCurrency || 'USD'}
            onChange={e => onChange({ costCurrency: e.target.value })}
            className="bg-white border border-app-border/60 rounded-xl px-3 py-3 text-sm text-app-text font-normal focus:outline-none focus:ring-4 focus:ring-gold/5 focus:border-gold/40 transition appearance-none"
          >
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {showPaymentStatus && <div className="space-y-1.5">
        <label className="ds-label block">Payment to provider</label>
        <select
          aria-label="Payment to provider"
          value={['paid', 'deposit', 'due'].includes(effectiveKind) ? effectiveKind : ''}
          onChange={e => {
            const kind = e.target.value as BookingCostKind;
            onChange({ costKind: kind, paidStatus: paymentStatusForCostKind(kind) || '',
              ...(!isLogged && kind !== 'paid' && kind !== 'deposit' && kind !== 'due' ? { logToExpenses: false } : {}) });
          }}
          className="w-full bg-white border border-app-border/60 rounded-xl px-4 py-3 text-sm text-app-text font-normal focus:outline-none focus:ring-4 focus:ring-gold/5 focus:border-gold/40 transition appearance-none"
        >
          <option value="" disabled>Select payment status</option>
          {(['paid', 'deposit', 'due'] as const).map(kind => <option key={kind} value={kind}>{BOOKING_COST_LABELS[kind].status}</option>)}
        </select>
      </div>}

      {/* Deposit amount — only when a deposit has been paid */}
      {effectiveKind === 'deposit' && (
        <div className="space-y-1.5">
          <label className="ds-label block">Deposit paid</label>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <input
              type="number"
              aria-label="Deposit paid"
              min="0"
              step="0.01"
              value={depositAmount ?? ''}
              onChange={e => onChange({ depositAmount: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
              placeholder="0.00"
              className={`${FIELD} ${fieldBorder(false, true)}`}
            />
            <select
              aria-label="Deposit currency"
              value={depositCurrency || costCurrency || baseCurrency || 'USD'}
              onChange={e => onChange({ depositCurrency: e.target.value })}
              className="bg-white border border-app-border/60 rounded-xl px-3 py-3 text-sm text-app-text font-normal focus:outline-none focus:ring-4 focus:ring-gold/5 focus:border-gold/40 transition appearance-none"
            >
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      )}

      {showPaymentStatus && !isLogged && !canLogCharge && (costAmount ?? 0) > 0 && !hideExpenseLink && (
        <p className="text-sm text-app-sub">Confirm the agreed cost and payment status before logging an expense.</p>
      )}

      {!hideExpenseLink && !isLogged && canLogCharge && (costAmount ?? 0) > 0 && (
        <div className="p-3 bg-app-faint rounded-xl border border-app-border/30 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] font-black uppercase tracking-widest text-app-text">Log as expense</div>
              <div className="text-sm text-app-sub mt-0.5">Creates a traceable expense entry</div>
            </div>
            <button
              type="button"
              role="switch"
              aria-label="Log full cost as expense"
              aria-checked={logToExpenses}
              onClick={() => onChange({ logToExpenses: !logToExpenses })}
              className={`relative w-10 h-6 rounded-full transition-colors flex-shrink-0 ${logToExpenses ? 'bg-gold' : 'bg-app-border/40'}`}
            >
              <span
                className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm transition-all duration-200 ${logToExpenses ? 'left-5' : 'left-1'}`}
              />
            </button>
          </div>
          {logToExpenses && (
            <div className="space-y-3 pt-2 border-t border-app-border/20">
              <PaidBySplit travellers={travellers} paidBy={paidBy || currentUser.id} split={split} onChange={onChange} payerLabel="Responsible for payment" />
              <p className="text-sm text-app-sub">Shares are owed to this traveller against the full cost, even if payment to the provider is due later.</p>
            </div>
          )}
        </div>
      )}

      {/* Expense notes — attached to the created expense; last in Cost Details */}
      {!hideExpenseLink && !isLogged && canLogCharge && logToExpenses && (
        <div className="space-y-1.5">
          <label className="ds-label block">Expense notes</label>
          <textarea
            value={note || ''}
            onChange={e => onChange({ note: e.target.value })}
            placeholder="e.g. Deposit paid by Alex for both travellers"
            className={`${FIELD} ${fieldBorder(false, true)} min-h-[70px] resize-none`}
          />
        </div>
      )}
    </div>
  );
}

export function BookingCosts({ item, relatedItems = [], sourceType, expenses, hideExpenseLink = false }: {
  item: { id?: string; costAmount?: number; costCurrency?: string; costKind?: BookingCostKind; paidStatus?: string; depositAmount?: number; depositCurrency?: string; cost?: string; expenseLogId?: string };
  relatedItems?: { id?: string; expenseLogId?: string }[];
  sourceType?: string;
  expenses?: Expense[];
  hideExpenseLink?: boolean;
}) {
  const expenseScope = React.useContext(ExpenseScope);
  const linkedItems = [item, ...relatedItems];
  const charges = expenses?.filter(expense => !expense.itineraryDeleted
    && linkedItems.some(linked => expense.id === linked.expenseLogId || (!!linked.id && expense.sourceId === linked.id && (!sourceType
      || expense.sourceType === sourceType || (sourceType === 'other' && expense.sourceType === 'note'))))) || [];
  const fullCosts = charges.filter(expense => expense.amountBasis === 'full-cost');
  const primary = fullCosts.find(expense => expense.id === item.expenseLogId)
    || (fullCosts.length === 1 ? fullCosts[0] : undefined);
  // View modals retain their opening item; financial display follows the live ledger.
  if (primary) item = { ...item, costAmount: primary.amount, costCurrency: primary.currency,
    costKind: costKindForPaymentStatus(primary.paidStatus), paidStatus: primary.paidStatus,
    depositAmount: primary.depositAmount, depositCurrency: primary.depositCurrency };
  const hasBookingAmount = item.costAmount != null || !!item.cost?.trim();
  const kind = item.costKind || costKindForPaymentStatus(item.paidStatus as any);
  const labels = BOOKING_COST_LABELS[kind];
  const hasPayment = kind === 'paid' || kind === 'deposit' || kind === 'due';
  if (!hasBookingAmount && !hasPayment && !charges.length && !(item.expenseLogId && !expenses)) return null;
  return <div className="border-t border-app-border/30 pt-4 space-y-2">
    <h3 className="text-sm font-medium text-app-sub">Cost &amp; payment</h3>
    {(!charges.length || (hideExpenseLink && primary)) && <>
    {hasBookingAmount && <div className="flex justify-between gap-3 text-sm">
      <span className="text-app-sub">{labels.amount}</span>
      <span className="text-app-text">{item.costAmount != null ? `${item.costCurrency || ''} ${item.costAmount.toLocaleString()}`.trim() : item.cost}</span>
    </div>}
    {hasPayment && <p className={`text-sm ${kind === 'deposit' ? 'text-amber-800' : 'text-app-sub'}`}>Payment to provider: {labels.status}</p>}
    {kind === 'deposit' && item.depositAmount != null && <p className="text-sm text-app-sub">Deposit paid: {item.depositCurrency || item.costCurrency} {item.depositAmount.toLocaleString()}</p>}
    </>}
    {!hideExpenseLink && (expenses ? charges.map(charge => <button key={charge.id} type="button"
      onClick={() => window.dispatchEvent(new CustomEvent('open-linked-expense', { detail: {id:charge.id,scope:expenseScope} }))}
      className="w-full flex items-center justify-between gap-3 p-3 rounded-xl bg-app-faint border border-app-border/30 text-left hover:border-gold/40">
        <span className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center shrink-0"><DollarSign className="w-5 h-5 text-gold" aria-hidden="true" /></span>
        <span className="min-w-0 flex-1"><span className="block text-xs text-app-sub mb-1">Linked expense</span>
          <span className="block text-sm text-app-text break-words">{charge.desc || 'Expense'}</span>
          <span className="block text-sm text-app-sub mt-1">{charge.currency} {charge.amount.toLocaleString()} · View expense</span>
          {charge.paidStatus && <span className={`block text-sm mt-1 ${charge.paidStatus === 'Deposit paid' ? 'text-amber-800' : 'text-app-sub'}`}>{BOOKING_COST_LABELS[costKindForPaymentStatus(charge.paidStatus)].status}</span>}
          {charge.paidStatus === 'Deposit paid' && charge.depositAmount != null && <span className="block text-sm text-app-sub mt-1">Deposit paid: {charge.depositCurrency || charge.currency} {charge.depositAmount.toLocaleString()}</span>}
        </span>
        <ArrowRight className="w-4 h-4 shrink-0 text-gold" aria-hidden="true" />
      </button>)
      : item.expenseLogId && <button type="button" onClick={() => window.dispatchEvent(new CustomEvent('open-linked-expense', { detail: {id:item.expenseLogId,scope:expenseScope} }))} className="text-sm text-gold hover:underline">View linked expense</button>)}
    {hideExpenseLink && charges.filter(charge => charge.id !== primary?.id).map(charge => <div key={charge.id} className="text-sm text-app-sub">
      <p>{charge.desc} · {charge.currency} {charge.amount.toLocaleString()}</p>
      {charge.paidStatus && <p>{BOOKING_COST_LABELS[costKindForPaymentStatus(charge.paidStatus)].status}</p>}
      {charge.paidStatus === 'Deposit paid' && charge.depositAmount != null && <p>Deposit paid: {charge.depositCurrency || charge.currency} {charge.depositAmount.toLocaleString()}</p>}
    </div>)}
  </div>;
}

function ItemAddForm({ type, initialData, onSave, onCancel, onSavingChange, country, tripConfig, currentUser, dayDate }: { type: 'attraction' | 'food' | 'nightlife' | 'shopping' | 'note', initialData?: any, onSave: (item: any) => void | boolean | Promise<void | boolean>, onCancel: () => void, onSavingChange?: (saving: boolean) => void, country?: string, tripConfig?: TripConfig, currentUser?: User, dayDate?: string }) {
  const entryId = useRef(initialData?.id || Math.random().toString(36).slice(2, 11));
  const [name, setName] = useState(initialData?.name || '');
  const [notes, setNotes] = useState(initialData?.notes ?? initialData?.desc ?? '');
  const [address, setAddress] = useState(initialData?.address || '');
  const [mapUrl, setMapUrl] = useState(initialData?.mapUrl || '');
  const [addedBy, setAddedBy] = useState(initialData?.addedBy || '');
  const [saving, setSaving] = useState(false);
  const [meal, setMeal] = useState(initialData?.meal || '');
  const [category, setCategory] = useState(initialData?.category || '');
  const [cuisine, setCuisine] = useState(initialData?.cuisine || '');
  const [tip, setTip] = useState(initialData?.tip || '');
  const [link, setLink] = useState(initialData?.link || '');
  const [durationMinutes, setDurationMinutes] = useState<number | undefined>(initialData?.durationMinutes);
  const [booked, setBooked] = useState(!!initialData?.booked);
  const [participantIds, setParticipantIds] = useState<string[] | undefined>(initialData?.participantIds);
  const [bookingRef, setBookingRef] = useState(initialData?.ref || '');
  const [bookingService, setBookingService] = useState(initialData?.bookingService || '');
  const [bookingNotes, setBookingNotes] = useState(initialData?.bookingNotes || '');
  const [bookingTime, setBookingTime] = useState(initialData?.time || '');
  const [bookingDate, setBookingDate] = useState(initialData?.bookingDate || '');
  const [errorMsg, setErrorMsg] = useState('');
  // Charge amounts and payer/split belong to Expenses; booking amounts stay on the item.
  const linkedExpense = initialData?.expenseLogId
    ? (tripConfig?.expenses || []).find((e: any) => e.id === initialData.expenseLogId && !e.itineraryDeleted)
    : undefined;
  const [costAmount, setCostAmount] = useState<number | undefined>(initialData?.costAmount);
  const [costCurrency, setCostCurrency] = useState(initialData?.costCurrency || linkedExpense?.currency || expenseBaseCurrency(tripConfig));
  const [paidStatus, setPaidStatus] = useState(initialData?.paidStatus || '');
  const [costKind, setCostKind] = useState<BookingCostKind>(initialData?.costKind || costKindForPaymentStatus(initialData?.paidStatus));
  const [depositAmount, setDepositAmount] = useState<number | undefined>(initialData?.depositAmount);
  const [depositCurrency, setDepositCurrency] = useState<string | undefined>(initialData?.depositCurrency);
  const [expenseNote, setExpenseNote] = useState<string>(linkedExpense?.note || '');
  const [logToExpenses, setLogToExpenses] = useState(!!linkedExpense);
  const [paidBy, setPaidBy] = useState(linkedExpense?.paid_by || currentUser?.id || '');
  const [costSplit, setCostSplit] = useState<string[]>(
    linkedExpense?.split?.length ? linkedExpense.split : (currentUser?.id ? [currentUser.id] : [])
  );
  
  // Google rating for a place picked from the autocomplete, so a manual add
  // carries the same "RESTAURANT · ★ 4.6 (1,234)" line a Talisman Explorer
  // item does. `ratedName` records which name the rating belongs to: if the
  // field is retyped afterwards it no longer describes this place, and the
  // rating is dropped on save rather than mislabelling a different one.
  const [rating, setRating] = useState<number | undefined>(initialData?.rating);
  const [ratingTotal, setRatingTotal] = useState<number | undefined>(initialData?.ratingTotal);
  const [ratedName, setRatedName] = useState<string>(initialData?.rating != null ? (initialData?.name || '') : '');

  const { ref: mapRef } = usePlacesWidget({
    onPlaceSelected: (place) => {
      setName(place.name || '');
      if (place.name) setErrorMsg('');
      if (place.formatted_address) setAddress(place.formatted_address);
      if (place.url) setMapUrl(place.url);
      const r = (place as any).rating;
      const rt = (place as any).user_ratings_total;
      setRating(typeof r === 'number' ? r : undefined);
      setRatingTotal(typeof rt === 'number' ? rt : undefined);
      setRatedName(place.name || '');
    },
    options: {
      fields: ["name", "formatted_address", "url", "rating", "user_ratings_total"],
      types: ["establishment"],
      componentRestrictions: countryCode(country || '') ? { country: countryCode(country || '')! } : undefined
    }
  });

  const handleSave = async () => {
    if (saving) return;
    if (!name.trim()) {
      setErrorMsg('Entry name is required.');
      return;
    }
    if (type === 'food' && !meal) {
      setErrorMsg('Please select a category.');
      return;
    }
    if ((type === 'attraction' || type === 'nightlife') && !category) {
      setErrorMsg('Please select a category.');
      return;
    }
    if (!linkedExpense && paidStatus === 'Deposit paid' && !(typeof depositAmount === 'number' && depositAmount > 0)) {
      setErrorMsg('Enter the deposit amount paid, or change the payment status.');
      return;
    }
    setErrorMsg('');
    const ratingStillApplies = rating != null && name.trim() === ratedName.trim();
    setSaving(true);
    onSavingChange?.(true);
    try {
      const saved = await onSave({
      _entryId: entryId.current,
      name, notes, address, mapUrl, addedBy, meal, category, cuisine, tip, link, durationMinutes, costAmount, costCurrency: linkedExpense ? initialData?.costCurrency : costCurrency, costKind, paidStatus,
      booked, ref: bookingRef.trim(), time: bookingTime, bookingDate, bookingService, bookingNotes,
      depositAmount, depositCurrency,
      // Undefined on purpose: switching back to Everyone removes the saved list.
      participantIds: type === 'note' ? undefined : participantIds,
      // Conditional keys: on edit these spread over the existing item, so an
      // explicit `undefined` would wipe a rating that's already saved.
      ...(ratingStillApplies ? { rating } : {}),
      ...(ratingStillApplies && ratingTotal != null ? { ratingTotal } : {}),
      _logToExpenses: logToExpenses, _paidBy: paidBy, _costSplit: costSplit, _expenseNote: expenseNote,
      });
      if (saved === false) setErrorMsg('Changes were not saved. Your edit is still here. Try again, or close and reopen to review the latest version.');
    } catch (error) {
      setErrorMsg(userMessage(error, 'Could not save. Your edit is still here. Please try again.'));
    } finally { setSaving(false); onSavingChange?.(false); }
  };

  return (
    /* No inner card: the modal's own border is the card, and a second frame
       inside it just boxed the same content twice. The fieldset carries the
       spacing the inner div used to. */
    <fieldset disabled={saving} className="space-y-4 animate-fade-up">
        <div className="space-y-1.5">
          <div className="ds-label">{type === 'note' ? 'Title' : 'Place or activity name'} <span className="text-app-sub/40">*</span></div>
          <div className="relative">
            <input
              ref={type === 'note' ? undefined : (mapRef as any)}
              value={name}
              onChange={e => { setName(e.target.value); if (e.target.value.trim()) setErrorMsg(''); }}
              className={`w-full bg-white border rounded-xl px-5 py-3 text-app-text font-normal focus:outline-none focus:ring-4 focus:ring-gold/5 focus:border-gold/40 transition-all text-sm appearance-none ${errorMsg && !name.trim() ? 'border-rose-300' : 'border-app-border/60'}`}
              placeholder={type === 'note' ? 'e.g. Catchup with Amy' : 'Search Google Maps...'}
            />
            {type !== 'note' && <MapIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-app-sub/40" />}
          </div>
        </div>

        {type === 'food' && (
          <SubcategoryPicker l1="food" label="Category *" value={meal}
            hasError={!!errorMsg && !meal} onChange={value => { setMeal(value); setErrorMsg(''); }} />
        )}

        {type === 'attraction' && (
          <SubcategoryPicker l1="experiences" label="Category *" value={category}
            hasError={!!errorMsg && !category} onChange={value => { setCategory(value); setErrorMsg(''); }} />
        )}

        {type === 'nightlife' && (
          <SubcategoryPicker l1="nightlife" label="Category *" value={category}
            hasError={!!errorMsg && !category} onChange={value => { setCategory(value); setErrorMsg(''); }} />
        )}

        <div className="grid grid-cols-1 gap-4">
          <FormInput label="Address" value={address} onChange={setAddress} />
        </div>
        <ItemDetailsSection>
          {(type === 'food' || cuisine) && <FormInput label="Cuisine" value={cuisine} onChange={setCuisine} />}
          <FormInput label="Tip" value={tip} onChange={setTip} />
          <FormInput label="Website" value={link} onChange={setLink} />
          <FormInput label="Duration (minutes)" type="number" value={durationMinutes ?? ''}
            onChange={value => setDurationMinutes(value === '' ? undefined : Math.max(0, Number(value)))} />
        </ItemDetailsSection>

        <div className="space-y-1.5">
          <label className="ds-label block" htmlFor={`item-notes-${entryId.current}`}>Notes</label>
          <textarea id={`item-notes-${entryId.current}`}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            className={`${FIELD} ${fieldBorder(false, true)} min-h-[100px] resize-none`}
            placeholder={type === 'note' ? 'Any extra detail worth remembering...' : 'What to expect, attire, accessibility or special requests…'}
          />
        </div>

        {type !== 'note' && <TravellerAssignment travellers={assignable(tripConfig)} value={participantIds} onChange={setParticipantIds} />}
        <BookingDetailsForm booked={booked} onBookedChange={setBooked}
          source={bookingService} onSourceChange={setBookingService} reference={bookingRef} onReferenceChange={setBookingRef}
          notes={bookingNotes} onNotesChange={setBookingNotes} documents={initialData?.documents}>
          {type !== 'note' && <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label htmlFor={`booking-date-${entryId.current}`} className="ds-label block">Booking date</label>
              <input id={`booking-date-${entryId.current}`} type="date" value={bookingDate} onChange={e => setBookingDate(e.target.value)} className={`${FIELD} ${fieldBorder(false, true)}`} />
            </div>
            <div className="space-y-1.5">
              <label htmlFor={`booking-time-${entryId.current}`} className="ds-label block">{type === 'food' ? 'Reservation time' : 'Booking time'}</label>
              <input id={`booking-time-${entryId.current}`} type="time" value={bookingTime} onChange={e => setBookingTime(e.target.value)} className={`${FIELD} ${fieldBorder(false, true)}`} />
            </div>
          </div>}
        {tripConfig && currentUser && (
          <CostAndLogSection
            sourceId={initialData?.id} sourceType={type === 'note' ? 'other' : type}
            costAmount={costAmount}
            costCurrency={costCurrency}
            costKind={costKind}
            paidStatus={paidStatus}
            depositAmount={depositAmount}
            depositCurrency={depositCurrency}
            note={expenseNote}
            logToExpenses={logToExpenses}
            paidBy={paidBy}
            split={costSplit}
            expenseLogId={initialData?.expenseLogId}
            travellers={(tripConfig.travellers || []).filter((u: User) => u.inviteStatus !== 'pending')}
            baseCurrency={expenseBaseCurrency(tripConfig)}
            currentUser={currentUser}
            expenses={tripConfig.expenses || []}
            onChange={(patch) => {
              if ('costAmount' in patch) setCostAmount(patch.costAmount);
              if (patch.costCurrency !== undefined) setCostCurrency(patch.costCurrency);
              if (patch.costKind !== undefined) setCostKind(patch.costKind);
              if (patch.paidStatus !== undefined) setPaidStatus(patch.paidStatus);
              if ('depositAmount' in patch) setDepositAmount(patch.depositAmount);
              if (patch.depositCurrency !== undefined) setDepositCurrency(patch.depositCurrency);
              if (patch.note !== undefined) setExpenseNote(patch.note);
              if (patch.logToExpenses !== undefined) setLogToExpenses(patch.logToExpenses);
              if (patch.paidBy !== undefined) setPaidBy(patch.paidBy);
              if (patch.split !== undefined) setCostSplit(patch.split);
            }}
          />
        )}
        </BookingDetailsForm>

        {errorMsg && (
          <div role="alert" className={ERROR_BOX}>
            {errorMsg}
          </div>
        )}

        <div className="flex gap-2 pt-4">
          <button disabled={saving} onClick={onCancel} className="flex-1 py-3 rounded-xl border border-app-border text-[11px] font-black uppercase tracking-widest text-app-sub">Cancel</button>
          <button disabled={saving} onClick={handleSave} className="flex-1 py-3 rounded-xl bg-gold text-white text-[11px] font-black uppercase tracking-widest shadow-lg shadow-gold/20">{saving ? 'Saving…' : 'Save'}</button>
        </div>
    </fieldset>
  );
}

function normalizeDuration(raw: string): string {
  return raw
    .replace(/\b(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|h)\b/gi, '$1h')
    .replace(/\b(\d+(?:\.\d+)?)\s*(?:minutes?|mins?|m)\b/gi, '$1m')
    .trim();
}

// Duration from dep/arr timestamps when no explicit duration string is
// stored — shared by TransportDetail's own display and any other transport
// summary row that wants a duration whenever it's derivable, not just when
// t.duration happens to be set.
function getCalculatedDuration(
  t: { fromCode?: string; from?: string; fromCity?: string; toCode?: string; to?: string; toCity?: string },
  dep?: string, arr?: string, depTz?: string, arrTz?: string
): string | null {
  const diffMinutes = calculatedDurationMinutes(t, dep, arr, depTz, arrTz);
  if (diffMinutes == null) return null;
  const h = Math.floor(diffMinutes / 60);
  const m = diffMinutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function transportDurationLabel(t: Transport, drivingMinutes: number | null): string | null {
  if (t.isRental) return null;
  const saved = t.duration?.trim();
  if (saved && !/^tbc$/i.test(saved)) return normalizeDuration(saved);
  const timed = getCalculatedDuration(t, t.dep, t.arr, t.depTz, t.arrTz);
  if (timed) return timed;
  return (t.mode === 'Car' || t.mode === 'Taxi') && drivingMinutes != null
    ? `Est. ${formatMinutes(drivingMinutes)}` : null;
}

function TransportDuration({ t, detail = false }: { t: Transport; detail?: boolean }) {
  const from = (t.fromAddress || t.from || '').trim();
  const to = (t.toAddress || t.to || '').trim();
  const routeKey = !t.isRental && (t.mode === 'Car' || t.mode === 'Taxi') && !transportDurationLabel(t, null)
    && from && to && from.toLowerCase() !== to.toLowerCase() ? JSON.stringify([from, to]) : '';
  const [estimate, setEstimate] = useState<{ key: string; minutes: number | null } | null>(null);
  useEffect(() => {
    if (!routeKey) return;
    let active = true;
    fetchDrivingDuration(from, to).then(minutes => {
      if (active) setEstimate({ key: routeKey, minutes });
    });
    return () => { active = false; };
  }, [routeKey]);
  const label = transportDurationLabel(t, estimate?.key === routeKey ? estimate.minutes : null);
  return detail
    ? <div className="text-app-sub/90 font-sans text-sm mt-3 text-center">{label ?? 'TBC'}</div>
    : label ? <span className="text-gold font-normal shrink-0">· {label}</span> : null;
}

function RouteMap({ from, fromCode, fromCity, to, toCode, toCity, mode, visible = true }: {
  from: string; fromCode?: string; fromCity?: string;
  to: string; toCode?: string; toCity?: string;
  mode: string; visible?: boolean;
}) {
  const apiKey = (import.meta as any).env.VITE_GOOGLE_MAPS_API_KEY;
  const isFlight = mode === 'Flight';

  // Build a geocode-friendly location string.
  // Bare airport codes like "MEL"/"SIN" are dangerously ambiguous for the
  // Static Maps geocoder (e.g. "MEL" matches a small town in Italy before
  // Melbourne airport). Prefer city, then "<code> airport" to disambiguate.
  const buildLoc = (full: string, code?: string, city?: string): string => {
    if (isFlight) {
      if (city && code) return `${city} airport (${code})`;
      if (city) return city;
      if (code) return `${code} airport`;
      return full.split(',')[0].trim();
    }
    // Driving / transit modes — airport code rarely applies; prefer full / city
    return full.trim() || city || code || '';
  };

  const originRaw = buildLoc(from, fromCode, fromCity);
  const destRaw   = buildLoc(to,   toCode,   toCity);

  const gmMode = mode === 'Car' || mode === 'Taxi' ? 'driving'
    : mode === 'Train' || mode === 'Bus' ? 'transit'
    : null;

  const routeKey = JSON.stringify([originRaw, destRaw, mode]);
  const [route, setRoute] = useState<{ key: string; result: any } | null>(null);
  const [chooseMaps, setChooseMaps] = useState(false);
  useEffect(() => {
    if (!gmMode || !apiKey || !originRaw || !destRaw) return;
    let active = true;
    loadGoogleMaps().then(() => {
      if (!active) return;
      const maps = window.google?.maps;
      if (!maps?.DirectionsService) { setRoute({ key: routeKey, result: null }); return; }
      new maps.DirectionsService().route({ origin: originRaw, destination: destRaw, travelMode: gmMode === 'driving' ? 'DRIVING' : 'TRANSIT',
        ...(gmMode === 'transit' ? { transitOptions: { modes: [mode === 'Train' ? 'TRAIN' : 'BUS'] } } : {}) },
        (result: any, status: string) => {
          // Transit modes are preferences, not guarantees. Do not draw a bus
          // suggestion as a booked train (or a train as a booked bus).
          const vehicles = (result?.routes?.[0]?.legs || []).flatMap((leg: any) =>
            (leg.steps || []).filter((step: any) => step.travel_mode === 'TRANSIT')
              .map((step: any) => step.transit?.line?.vehicle?.type));
          const allowed = mode === 'Train' ? ['RAIL', 'HEAVY_RAIL', 'HIGH_SPEED_TRAIN', 'COMMUTER_TRAIN', 'METRO_RAIL', 'SUBWAY', 'TRAM'] : ['BUS', 'INTERCITY_BUS', 'TROLLEYBUS'];
          const matchesMode = gmMode !== 'transit' || (vehicles.length > 0 && vehicles.every((vehicle: string) => allowed.includes(vehicle)));
          if (active) setRoute({ key: routeKey, result: status === 'OK' && matchesMode ? result : null });
        });
    }).catch(() => { if (active) setRoute({ key: routeKey, result: null }); });
    return () => { active = false; };
  }, [routeKey, apiKey]);
  const result = route?.key === routeKey ? route.result : null;
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(originRaw)}&destination=${encodeURIComponent(destRaw)}${gmMode ? `&travelmode=${gmMode}` : ''}`;
  const appleUrl = `https://maps.apple.com/?saddr=${encodeURIComponent(originRaw)}&daddr=${encodeURIComponent(destRaw)}${gmMode ? `&dirflg=${gmMode === 'driving' ? 'd' : 'r'}` : ''}`;

  const col = 'c5a059';
  // `visible` forces the map to frame both endpoints — without it, if one
  // location fails to geocode the map silently zooms to whichever did, which
  // is what produced the "Mel, Italy" view.
  let staticSrc = `https://maps.googleapis.com/maps/api/staticmap?size=600x300&scale=2&key=${apiKey}` +
    `&visible=${encodeURIComponent(originRaw)}%7C${encodeURIComponent(destRaw)}` +
    `&markers=color:0x${col}%7Csize:mid%7Clabel:A%7C${encodeURIComponent(originRaw)}` +
    `&markers=color:0x${col}%7Csize:mid%7Clabel:B%7C${encodeURIComponent(destRaw)}` +
    `&style=feature:poi%7Cvisibility:off`;
  if (isFlight) {
    staticSrc += `&path=geodesic:true%7Ccolor:0x${col}88%7Cweight:2%7C${encodeURIComponent(originRaw)}%7C${encodeURIComponent(destRaw)}`;
  }

  return (
    <div className="space-y-2">
      <div className="aspect-[2/1] w-full rounded-2xl overflow-hidden border border-app-border/20 shadow-sm bg-app-faint cursor-pointer" role="button" tabIndex={0} aria-label={`Choose a maps app for ${originRaw} to ${destRaw}`}
        onClick={e => { if (!(e.target as HTMLElement).closest('a,button')) setChooseMaps(true); }}
        onKeyDown={e => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setChooseMaps(true); } }}>
        {result ? visible ? <LegRouteMap result={result} /> : null : gmMode && apiKey && route?.key !== routeKey
          ? <p role="status" className="p-4 text-sm text-app-sub">Loading route preview…</p> : apiKey
          ? <img src={staticSrc} alt={`Endpoints: ${originRaw} to ${destRaw}`} className="w-full h-full object-contain" />
          : <p className="p-4 text-sm text-app-sub">Map preview unavailable.</p>}
      </div>
      {gmMode && <p className="text-sm text-app-sub">{result ? 'Google route suggestion · not your booked service.' : 'Endpoint preview · tap the map for directions.'}</p>}
      {chooseMaps && <Modal title="Open directions" onClose={() => setChooseMaps(false)}>
        <div className="space-y-2">
          {[["Google Maps", directionsUrl], ["Apple Maps", appleUrl]].map(([label, url]) =>
            <a key={label} href={url} target="_blank" rel="noopener noreferrer" onClick={() => setChooseMaps(false)}
              className="w-full min-h-11 py-3 rounded-xl border border-app-border text-app-sub text-sm font-semibold flex items-center justify-center gap-2">
              <MapIcon className="w-4 h-4" />{label}
            </a>)}
        </div>
      </Modal>}
    </div>
  );
}

export function TransportDetail({ t, dayDate, expenses, hideExpenseLink = false, hideCosts = false, children, bookingSectionId, travellers }: { t: Transport; dayDate?: string; expenses?: Expense[]; hideExpenseLink?: boolean; hideCosts?: boolean; children?: React.ReactNode; bookingSectionId?: string; travellers?: User[] }) {
  const [showRoute, setShowRoute] = useState(true);

  // Clean city name for the card title (strip station/airport identifiers)
  const cityFrom = _cityLabel(t.from, t.fromCity);
  const cityTo   = _cityLabel(t.to,   t.toCity);
  // Try to extract 3-letter IATA code from strings like "London Heathrow (LHR)"
  const extractIATA = (s: string) => s?.match(/\(([A-Z]{3})\)/)?.[1] ?? '';
  const codeFrom = t.fromCode || extractIATA(t.from) || cityFrom;
  const codeTo = t.toCode || extractIATA(t.to) || cityTo;


  const formatTimeInfo = (timeStr?: string) => {
    if (!timeStr) return "--:--";
    // Date-only string (YYYY-MM-DD, no time component) — nothing to display
    if (/^\d{4}-\d{2}-\d{2}$/.test(timeStr)) return "--:--";

    const timeDisplay = formatClock(timeStr);
    if (!timeDisplay) return '--:--';
    let dayOffset = 0;

    if (timeStr.includes('T')) {
      // Badge "+N" when this stamp falls on a later calendar date than the
      // leg's own departure — the thing a traveller reads an overnight
      // arrival off. Anchored to `t.dep` rather than the day card, because
      // `dayDate` is optional and in practice never passed, which left this
      // badge permanently at +0 and invisible on every red-eye.
      const base = (dayDate && /^\d{4}-\d{2}-\d{2}/.test(dayDate) ? dayDate : t.dep || '').slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(base)) {
        const localDate = timeStr.slice(0, 10); // YYYY-MM-DD from "YYYY-MM-DDTHH:MM"
        const baseMs = Date.UTC(
          +base.slice(0, 4), +base.slice(5, 7) - 1, +base.slice(8, 10)
        );
        const stampMs = Date.UTC(
          +localDate.slice(0, 4), +localDate.slice(5, 7) - 1, +localDate.slice(8, 10)
        );
        dayOffset = Math.round((stampMs - baseMs) / (1000 * 60 * 60 * 24));
      }
    }

    return (
      <div className="flex items-start justify-center">
        <span>{timeDisplay.split(/\s+/).map((part, i) => <span key={i} className="block">{part}</span>)}</span>
        {dayOffset > 0 && (
          <sup className="text-sm font-bold text-app-text mt-0 ml-0.5">
            +{dayOffset}
          </sup>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Topline Sentence */}
      <div className="flex justify-between items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-app-text font-bold text-lg leading-tight text-balance">
            {t.isRental ? `Car hire in ${cityFrom}` : `${t.mode} from ${cityFrom} to ${cityTo}`}
            {t.via && (
              <span className="text-app-sub/60 font-medium text-sm ml-2 italic">
                (via {t.via})
              </span>
            )}
          </div>
          {t.carrier && (
            <div className="text-app-sub text-sm font-normal mt-1">
              {t.carrier}
            </div>
          )}
        </div>
        {t.imported && (
          <div className="text-[10px] font-semibold text-gold/70 min-h-6 flex items-center gap-0.5 shrink-0">
            <FileInput className="w-2.5 h-2.5" />
            <span>Import</span>
          </div>
        )}
      </div>

      {/* Times Display (Always visible) */}
      {t.isRental ? <div className="grid grid-cols-2 gap-3">
        {[["Pickup", t.dep, t.from], ["Return", t.arr, t.to]].map(([label, date, place]) => {
          const Icon = label === 'Pickup' ? LogIn : LogOut;
          return <div key={label} className="min-w-0 bg-app-faint rounded-2xl py-3 px-4 border border-app-border shadow-inner text-center">
            <div className="flex items-center justify-center gap-1 mb-1.5">
              <Icon className="w-3 h-3 text-app-sub/50" aria-hidden="true" />
              <span className="text-sm text-app-sub">{label}</span>
            </div>
            <div className="text-2xl font-serif font-black text-app-text leading-none">{formatClock(date || '') || '--:--'}</div>
            {date && !isNaN(calendarDate(date).getTime()) && <div className="text-sm text-app-sub mt-1.5">
              {calendarDate(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </div>}
            {place && <p className="text-sm text-app-sub break-words mt-1.5">{place}</p>}
          </div>;
        })}
      </div> : <div className="bg-app-faint rounded-2xl py-5 px-6 border border-app-border/20 shadow-inner">
        <div className="grid grid-cols-3 items-center gap-x-2">
          {/* Times Row aligned with Icon/Line */}
          <div className="text-app-text font-serif text-3xl font-black leading-none text-center">{formatTimeInfo(t.dep)}</div>
          <div className="relative flex h-8 items-center justify-center px-4">
            <div className="absolute inset-x-4 top-1/2 h-px -translate-y-1/2 bg-gradient-to-r from-transparent via-app-border to-transparent" />
            <span className="relative z-10 flex items-center justify-center bg-app-faint px-2 select-none">
              <SubcategoryIcon name={t.mode} l1="transport" className="w-5 h-5 text-gold" />
            </span>
          </div>
          <div className="text-app-text font-serif text-3xl font-black leading-none text-center">{formatTimeInfo(t.arr)}</div>

          {/* Labels Row (aligned below) */}
          <div className="text-app-sub text-sm font-normal mt-3 text-center leading-tight hyphens-auto break-words">{codeFrom}</div>
          <TransportDuration t={t} detail />
          <div className="text-app-sub text-sm font-normal mt-3 text-center leading-tight hyphens-auto break-words">{codeTo}</div>
        </div>
      </div>}
      <div className="space-y-0">
      {/* Route details */}
      <div>
        <button
          type="button" aria-expanded={showRoute}
          onClick={() => setShowRoute(!showRoute)}
          className="flex items-center gap-2 text-gold transition-colors w-full min-h-11"
        >
          <MapIcon className="w-3 h-3 text-gold" />
          <span className="text-[11px] font-black uppercase tracking-widest leading-none">Map</span>
          <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform duration-300 ${showRoute ? 'rotate-180' : ''}`} />
        </button>
            <div hidden={!showRoute}>
              <div className="py-2">
                <RouteMap
                  from={t.from} fromCode={t.fromCode} fromCity={t.fromCity}
                  to={t.to} toCode={t.toCode} toCity={t.toCity}
                  mode={t.isRental ? 'Other' : t.mode}
                  visible={showRoute}
                />
              </div>
            </div>
      </div>

      {/* Ancillary details: dated endpoints first, without repeating the headline. */}
      <ItemDetailsSection>
        <div className="py-2 space-y-4">
          {[
            ['Departure', t.isRental ? '' : [t.from, /^\d{4}-\d{2}-\d{2}/.test(t.dep || '') ? shortDateLabel(t.dep.slice(0, 10)) : ''].filter(Boolean).join(' · ')],
            ['Arrival', t.isRental ? '' : [t.to, /^\d{4}-\d{2}-\d{2}/.test(t.arr || '') ? shortDateLabel(t.arr.slice(0, 10)) : ''].filter(Boolean).join(' · ')],
            ['Terminal / platform', t.terminal],
            ['Arrive at departure point', t.arriveAtAirport && /^\d{2}:\d{2}$/.test(t.arriveAtAirport) ? formatClock(t.arriveAtAirport) : t.arriveAtAirport],
            [t.mode === 'Flight' ? 'Flight number' : 'Service number', t.flightNum],
            ['Seat', t.seatNumber], ['Travel class', t.cabinClass], ['Baggage allowance', t.baggageAllowance],
          ].map(([label, value]) => value ? <div key={label} className="space-y-1">
            <span className="text-sm text-app-sub font-semibold">{label}</span>
            <p className="text-sm text-app-text whitespace-pre-wrap break-words">{value}</p>
          </div> : null)}
          {t.notes && <div className="space-y-1"><span className="text-sm text-app-sub font-semibold">Notes</span>
            <p className="text-app-sub text-sm font-normal whitespace-pre-wrap break-words">{renderWithLinks(t.notes)}</p></div>}
        </div>
      </ItemDetailsSection>
      {((!hideCosts && hasBookingContent(t)) || children) && <ItemDetailsSection id={bookingSectionId} label="Booking & payment">
        <AssignedTravellers ids={t.participantIds} travellers={travellers} />
        {!hideCosts && <><SavedBookingInformation item={t} /><BookingCosts item={t} sourceType="transport" expenses={expenses} hideExpenseLink={hideExpenseLink} /></>}
        {children}
      </ItemDetailsSection>}
      </div>
    </div>
  );
}

// Press-and-hold anywhere on the card to pick it up and drag-reorder it —
// no separate "Reorder" mode/button any more. Listeners live on this whole
// wrapper (not a small grip handle), so a plain tap still opens the card as
// before (dnd-kit's delay-based activation only takes over once you've held
// still past the delay; a quick tap/scroll releases before that and behaves
// normally), but a genuine long-press anywhere lifts the card the way an
// iOS home-screen icon does.
//
// `touch-action: pan-y` (not `none`) — this is what lets normal vertical
// scrolling still work when a swipe starts on a card. `none` was tried
// first and broke scrolling entirely: touch-action is decided by the
// browser once, at the very start of a touch, before any JS runs, so
// `none` withholds native scroll handling for the *whole* gesture even if
// dnd-kit's own delay timer later decides "this wasn't a drag" — there's no
// handing it back to native scrolling partway through. `pan-y` avoids that
// because during the hold itself the finger isn't moving yet (movement
// beyond `tolerance` cancels the hold), so there's nothing for native
// scroll to claim during that window; a fast swipe that never pauses is
// still recognised and scrolls immediately, and a genuine long-press that
// *does* activate a drag has dnd-kit preventDefault-ing further moves from
// that point on regardless of touch-action.
function SortableItem({ id, children }: { id: string; children: React.ReactNode; key?: any }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.5 : 1,
    touchAction: 'pan-y' as const,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`group relative ${isDragging ? 'shadow-2xl' : ''}`}
      id={`activity-${id}`}
    >
      <div className="py-4 first:pt-0 last:pb-0">
        {children}
      </div>
    </div>
  );
}

// Fired when a shortlist item is toggled INTO the plan — launches the little
// comet/spark animation (rendered in App.tsx's ShootingStarLayer, which is
// mounted once at the app root and listens globally) from the clicked
// button's on-screen position toward the Schedule nav tab. No import needed
// here since App.tsx is a listener, not a dependency — decoupled via a
// window CustomEvent the same way openItemModal decouples cross-tab links.
function fireScheduleFlight(el: HTMLElement) {
  const r = el.getBoundingClientRect();
  window.dispatchEvent(new CustomEvent('schedule-flight', {
    detail: { x: r.left + r.width / 2, y: r.top + r.height / 2 },
  }));
}

function AttractionsList({
  attractions,
  onUpdate,
  onAddNew,
  onDeleteLinkedExpense,
  linkedExpenseLock,
  user,
  variant = 'nightlife',
  isAdmin = false,
  tripId = '',
  tripTitle = '',
  allMemberIds = [],
  location = '',
  dayDate = '',
}: {
  attractions: Attraction[];
  onUpdate: (list: Attraction[]) => Promise<boolean>;
  /** Delete the expense this item created, when the traveller asks for it.
   *  The lists don't hold the expense themselves — DayDetail does. */
  onDeleteLinkedExpense?: (expenseId: string) => Promise<boolean>;
  /** Why the linked expense must stay (someone else settled against it), or null. */
  linkedExpenseLock?: (expenseId: string) => string | null;
  onAddNew: () => void;
  user: User;
  variant?: 'attraction' | 'nightlife' | 'shopping';
  isAdmin?: boolean;
  tripId?: string;
  tripTitle?: string;
  allMemberIds?: string[];
  location?: string;
  dayDate?: string;
}) {
  // Which item's schedule-toggle button is mid-"just added" confirmation —
  // it briefly expands to show "Added to Schedule" text next to the icon,
  // then collapses back to the plain circle. Only one at a time in practice
  // (a single tap), so a shared id here is simpler than per-card state
  // (renderCard is a plain closure, not its own component, so it can't
  // hold hooks of its own).
  const [justAddedId, setJustAddedId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const sensors = useSensors(
    // Delay-based (not distance-based) so a quick tap/scroll never triggers
    // a drag — only holding still for ~450ms does (bumped from 250ms after
    // testing — long enough that a normal tap/scroll never mistakenly
    // triggers a reorder), matching "press and hold
    // to reorder" rather than "any small drag reorders."
    useSensor(PointerSensor, { activationConstraint: { delay: 450, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const remove = (id: string) => onUpdate(attractions.filter(a => a.id !== id));

  const toggleInPlan = async (id: string) => {
    const item = attractions.find(a => a.id === id);
    if (!item) return;
    const cur = item.inPlan ?? (item.imported ? true : false);
    const nowInPlan = !cur;
    if (!await onUpdate(attractions.map(a => a.id === id ? { ...a, inPlan: nowInPlan } : a))) return;
    if (tripId) {
      const cats = variant === 'attraction' ? ATTRACTION_CATS : variant === 'nightlife' ? NIGHTLIFE_CATS : [];
      const cat = (cats as any).find((c: any) => c.name === normalizeSubcat(item.category));
      const emoji = variant === 'shopping' ? '🛍️' : cat?.emoji || (variant === 'attraction' ? '🏛️' : '🍸');
      if (nowInPlan) {
        notifyAddedToSchedule(
          tripId, tripTitle,
          user.id, getFirstName(user.name),
          allMemberIds, item.name, location, dayDate, 'attractions', emoji, item.id
        ).catch(console.error);
      } else {
        notifyShortlisted(
          tripId, tripTitle,
          user.id, getFirstName(user.name),
          allMemberIds, item.name, location, dayDate, 'attractions', emoji, item.id
        ).catch(console.error);
      }
    }
  };

  const cats = variant === 'attraction' ? ATTRACTION_CATS : variant === 'nightlife' ? NIGHTLIFE_CATS : [];
  const defaultEmoji = variant === 'attraction' ? '🎯' : variant === 'shopping' ? '🛍️' : '🌙';

  const withDefaults = attractions.map(a => ({ ...a, inPlan: a.inPlan ?? (a.imported ? true : false) }));
  const inPlanItems    = withDefaults.filter(a => a.inPlan);
  const shortlistItems = withDefaults.filter(a => !a.inPlan);

  // Voting is decommissioned — keep stored/manual order (was previously
  // sorted by net like/dislike score when not actively drag-reordering).
  const sortedInPlan    = byOrder(inPlanItems);
  const sortedShortlist = byOrder(shortlistItems);

  const handleDragEndGroup = (event: DragEndEvent, group: Attraction[], other: Attraction[]) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = group.findIndex(i => i.id === active.id);
    const newIndex = group.findIndex(i => i.id === over.id);
    // withOrder, not the bare array: the day's merge keeps the stored order
    // and applies field changes by id, so a reorder that changes no field is
    // discarded entirely. See utils/itemOrder.ts.
    onUpdate([...withOrder(arrayMove(group, oldIndex, newIndex)), ...other]);
  };

  const renderCard = (item: Attraction) => {
    const effectiveInPlan = item.inPlan ?? (item.imported ? true : false);
    // The schedule-toggle button's own visual state during its "just added"
    // confirmation window — true immediately on tap, ahead of the real
    // `effectiveInPlan` flip (and the card's resulting move to the
    // Scheduled section), which is deliberately delayed. See the button's
    // onClick below.
    const showingAsInPlan = justAddedId === item.id ? true : effectiveInPlan;
    return (
      <div
        onClick={() => openItemModal({ type: variant, itemId: item.id, dayDate, mode: 'view' })}
        role="button"
        tabIndex={0}
        className={`surface-solid relative overflow-hidden bg-white border border-app-border rounded-2xl p-3 shadow-sm space-y-2 cursor-pointer hover:border-gold/30 transition-colors ${item.booked ? 'pb-8' : 'pb-4'}`}
      >
        {/* Header row: avatar + imported badge | plan toggle + edit/delete */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <By userId={item.addedBy} />
            {item.imported && <ImportTag />}
          </div>
          <div className="flex items-center gap-1">
            {isAdmin && (
              <motion.button
                layout
                disabled={justAddedId !== null}
                onClick={async (e) => {
                  e.stopPropagation();
                  setJustAddedId(item.id);
                  try {
                    if (!effectiveInPlan) {
                      fireScheduleFlight(e.currentTarget);
                      // Keep the card in place until the flight finishes.
                      await new Promise(resolve => setTimeout(resolve, 1400));
                    }
                    await toggleInPlan(item.id);
                  } finally {
                    setJustAddedId(id => (id === item.id ? null : id));
                  }
                }}
                transition={{ layout: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } }}
                title={effectiveInPlan ? 'Remove from Schedule' : 'Commit to Schedule'}
                className={`relative h-7 rounded-full flex items-center justify-center transition-colors ${
                  justAddedId === item.id ? 'px-3 gap-1.5' : 'w-7'
                } ${
                  showingAsInPlan
                    ? 'bg-emerald-500 text-white'
                    : 'bg-transparent text-app-sub/40 hover:text-app-sub'
                }`}
              >
                <CalendarClock className="w-3.5 h-3.5 shrink-0" />
                <AnimatePresence>
                  {justAddedId === item.id && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.15 }}
                      className="text-[10px] font-black uppercase tracking-widest whitespace-nowrap overflow-hidden"
                    >
                      {effectiveInPlan ? 'Updating…' : 'Committing to Schedule…'}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.button>
            )}
            <button aria-label="Edit" onClick={(e) => { e.stopPropagation(); openItemModal({ type: variant, itemId: item.id, dayDate, mode: 'edit' }); }} className="p-1.5 text-app-sub/40 hover:text-gold transition-colors"><Edit2 className="w-3.5 h-3.5" /></button>
            {item.addedBy === user.id && (
              <>
                <button aria-label="Delete" onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(item.id); }} className="p-1.5 text-app-sub/40 hover:text-rose-500 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
              </>
            )}
          </div>
        </div>

        {/* Icon + name + category — all left-aligned in same row */}
        <div className="flex items-center gap-3 mt-1">
          <div className="w-9 h-9 rounded-xl bg-app-faint flex items-center justify-center text-lg flex-shrink-0">
            <SubcategoryIcon name={item.category} l1={variant === 'attraction' ? 'experiences' : variant} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="text-sm font-bold text-app-text leading-snug line-clamp-1">{item.name}</div>
            </div>
            <div className="ds-label mt-0.5 min-h-[1lh]">
              {normalizeSubcat(item.category)}
              <InlineRating rating={item.rating} total={item.ratingTotal} />
            </div>
          </div>
        </div>

        {item.notes && (
          <div className="pt-2 border-t border-app-border/10">
            <p className="text-xs text-app-sub/70 leading-relaxed line-clamp-3">{item.notes}</p>
          </div>
        )}
        {item.booked && <BookedStrip date={item.bookingDate && shortDateLabel(item.bookingDate)} time={item.time} />}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {/* Scheduled items — no subheader label; press and hold a card to drag-reorder it */}
      {sortedInPlan.length > 0 && (
        <div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={e => handleDragEndGroup(e, inPlanItems, shortlistItems)}>
            <SortableContext items={sortedInPlan.map(a => a.id)} strategy={verticalListSortingStrategy}>
              <AnimatePresence mode="popLayout" initial={false}>
                {sortedInPlan.map((item) => (
                  <motion.div
                    key={item.id}
                    layoutId={item.id}
                    layout="position"
                    initial={{ opacity: 0, y: -12, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.97 }}
                    transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
                    className="mb-3 last:mb-0"
                  >
                    <SortableItem id={item.id}>
                      {renderCard(item)}
                    </SortableItem>
                  </motion.div>
                ))}
              </AnimatePresence>
            </SortableContext>
          </DndContext>
        </div>
      )}

      {/* Shortlist items — no subheader label. */}
      {sortedShortlist.length > 0 && (
        <div className={inPlanItems.length > 0 ? 'mt-4' : ''}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={e => handleDragEndGroup(e, shortlistItems, inPlanItems)}>
            <SortableContext items={sortedShortlist.map(a => a.id)} strategy={verticalListSortingStrategy}>
              <AnimatePresence mode="popLayout" initial={false}>
                {sortedShortlist.map((item) => (
                  <motion.div
                    key={item.id}
                    layoutId={item.id}
                    layout="position"
                    initial={{ opacity: 0, y: -12, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.97 }}
                    transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
                    className="mb-3 last:mb-0"
                  >
                    <SortableItem id={item.id}>
                      {renderCard(item)}
                    </SortableItem>
                  </motion.div>
                ))}
              </AnimatePresence>
            </SortableContext>
          </DndContext>
        </div>
      )}
      {/* Add to Shortlist — always shown at the end of the cards (the
          section header no longer carries its own "+"; this is the one add
          entry point), right before the Explorer CTA/carousel the parent
          renders next. */}
      <button
        onClick={onAddNew}
        className={`w-full min-h-[112px] flex flex-col items-center justify-center gap-2 p-4 rounded-2xl border-2 border-dashed border-app-border/50 bg-app-faint/70 text-app-sub/60 transition-all active:scale-[0.98] hover:border-app-sub/40 hover:text-app-sub ${
          inPlanItems.length > 0 || shortlistItems.length > 0 ? 'mt-4' : ''
        }`}
      >
        <div className="w-7 h-7 rounded-full flex items-center justify-center bg-app-sub text-white shadow-lg shadow-app-sub/20">
          <Plus className="w-3.5 h-3.5 stroke-[3]" />
        </div>
        <span className="text-[11px] font-black uppercase tracking-widest">Add to Shortlist</span>
      </button>
      {confirmDeleteId && (() => {
        const item = attractions.find(a => a.id === confirmDeleteId);
        if (!item) return null;
        const expenseId = (item as any).expenseLogId as string | undefined;
        return (
          <ConfirmDeleteModal
            title="Delete this item?"
            message={expenseId
              ? `"${item.name}" will be permanently removed. Its logged expense stays in the Expenses tab unless you say otherwise.`
              : `"${item.name}" will be permanently removed.`}
            extraOption={expenseId ? {
              label: 'Delete its logged expense too',
              hint: 'Removes it from the Expenses tab and from everyone\u2019s balances.',
              disabledReason: linkedExpenseLock?.(expenseId),
            } : undefined}
            onCancel={() => setConfirmDeleteId(null)}
            onConfirm={async (alsoDeleteExpense) => {
              const saved = await remove(item.id);
              if (!saved) return false;
              // After the item, not with it: if this fails the expense is
              // still there to delete by hand, which is recoverable — an
              // expense deleted for an item that then failed to delete is not.
              if (alsoDeleteExpense && expenseId && onDeleteLinkedExpense) await onDeleteLinkedExpense(expenseId);
              setConfirmDeleteId(null);
              return saved;
            }}
          />
        );
      })()}
    </div>
  );
}

function EatsList({
  eats,
  onUpdate,
  onAddNew,
  onDeleteLinkedExpense,
  linkedExpenseLock,
  user,
  isAdmin = false,
  tripId = '',
  tripTitle = '',
  allMemberIds = [],
  location = '',
  dayDate = '',
}: {
  eats: Eat[];
  onUpdate: (list: Eat[]) => Promise<boolean>;
  onAddNew: () => void;
  /** See AttractionsList — the expense lives on DayDetail, not here. */
  onDeleteLinkedExpense?: (expenseId: string) => Promise<boolean>;
  /** Why the linked expense must stay (someone else settled against it), or null. */
  linkedExpenseLock?: (expenseId: string) => string | null;
  user: User;
  isAdmin?: boolean;
  tripId?: string;
  tripTitle?: string;
  allMemberIds?: string[];
  location?: string;
  dayDate?: string;
}) {
  // Which item's schedule-toggle button is mid-"just added" confirmation —
  // it briefly expands to show "Added to Schedule" text next to the icon,
  // then collapses back to the plain circle. Only one at a time in practice
  // (a single tap), so a shared id here is simpler than per-card state
  // (renderCard is a plain closure, not its own component, so it can't
  // hold hooks of its own).
  const [justAddedId, setJustAddedId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const eatEmoji: Record<string, string> = Object.fromEntries(DINING_CATS.map(c => [c.name, c.emoji]));

  const sensors = useSensors(
    // Delay-based (not distance-based) so a quick tap/scroll never triggers
    // a drag — only holding still for ~450ms does (bumped from 250ms after
    // testing — long enough that a normal tap/scroll never mistakenly
    // triggers a reorder), matching "press and hold
    // to reorder" rather than "any small drag reorders."
    useSensor(PointerSensor, { activationConstraint: { delay: 450, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const remove = (id: string) => onUpdate(eats.filter(e => e.id !== id));

  const toggleInPlan = async (id: string) => {
    const item = eats.find(e => e.id === id);
    if (!item) return;
    const cur = item.inPlan ?? (item.imported ? true : false);
    const nowInPlan = !cur;
    if (!await onUpdate(eats.map(e => e.id === id ? { ...e, inPlan: nowInPlan } : e))) return;
    if (tripId) {
      const emoji = eatEmoji[item.meal] || '🍽️';
      if (nowInPlan) {
        notifyAddedToSchedule(
          tripId, tripTitle,
          user.id, getFirstName(user.name),
          allMemberIds, item.name, location, dayDate, 'eats', emoji, item.id
        ).catch(console.error);
      } else {
        notifyShortlisted(
          tripId, tripTitle,
          user.id, getFirstName(user.name),
          allMemberIds, item.name, location, dayDate, 'eats', emoji, item.id
        ).catch(console.error);
      }
    }
  };

  const withDefaults = eats.map(e => ({ ...e, inPlan: e.inPlan ?? (e.imported ? true : false) }));
  const inPlanItems    = withDefaults.filter(e => e.inPlan);
  const shortlistItems = withDefaults.filter(e => !e.inPlan);

  // Voting is decommissioned — keep stored/manual order (was previously
  // sorted by net like/dislike score when not actively drag-reordering).
  const sortedInPlan    = byOrder(inPlanItems);
  const sortedShortlist = byOrder(shortlistItems);

  const handleDragEndGroup = (event: DragEndEvent, group: Eat[], other: Eat[]) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = group.findIndex(i => i.id === active.id);
    const newIndex = group.findIndex(i => i.id === over.id);
    // withOrder, not the bare array: the day's merge keeps the stored order
    // and applies field changes by id, so a reorder that changes no field is
    // discarded entirely. See utils/itemOrder.ts.
    onUpdate([...withOrder(arrayMove(group, oldIndex, newIndex)), ...other]);
  };

  const renderCard = (item: Eat) => {
    const effectiveInPlan = item.inPlan ?? (item.imported ? true : false);
    // The schedule-toggle button's own visual state during its "just added"
    // confirmation window — true immediately on tap, ahead of the real
    // `effectiveInPlan` flip (and the card's resulting move to the
    // Scheduled section), which is deliberately delayed. See the button's
    // onClick below.
    const showingAsInPlan = justAddedId === item.id ? true : effectiveInPlan;
    return (
      <div
        onClick={() => openItemModal({ type: 'eat', itemId: item.id, dayDate, mode: 'view' })}
        role="button"
        tabIndex={0}
        className={`surface-solid relative overflow-hidden bg-white border border-app-border rounded-2xl p-3 shadow-sm space-y-2 cursor-pointer hover:border-gold/30 transition-colors ${item.booked ? 'pb-8' : 'pb-4'}`}
      >
        {/* Header row */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <By userId={item.addedBy} />
            {item.imported && <ImportTag />}
          </div>
          <div className="flex items-center gap-1">
            {isAdmin && (
              <motion.button
                layout
                disabled={justAddedId !== null}
                onClick={async (e) => {
                  e.stopPropagation();
                  setJustAddedId(item.id);
                  try {
                    if (!effectiveInPlan) {
                      fireScheduleFlight(e.currentTarget);
                      // Keep the card in place until the flight finishes.
                      await new Promise(resolve => setTimeout(resolve, 1400));
                    }
                    await toggleInPlan(item.id);
                  } finally {
                    setJustAddedId(id => (id === item.id ? null : id));
                  }
                }}
                transition={{ layout: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } }}
                title={effectiveInPlan ? 'Remove from Schedule' : 'Commit to Schedule'}
                className={`relative h-7 rounded-full flex items-center justify-center transition-colors ${
                  justAddedId === item.id ? 'px-3 gap-1.5' : 'w-7'
                } ${
                  showingAsInPlan
                    ? 'bg-emerald-500 text-white'
                    : 'bg-transparent text-app-sub/40 hover:text-app-sub'
                }`}
              >
                <CalendarClock className="w-3.5 h-3.5 shrink-0" />
                <AnimatePresence>
                  {justAddedId === item.id && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.15 }}
                      className="text-[10px] font-black uppercase tracking-widest whitespace-nowrap overflow-hidden"
                    >
                      {effectiveInPlan ? 'Updating…' : 'Committing to Schedule…'}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.button>
            )}
            <button aria-label="Edit" onClick={(e) => { e.stopPropagation(); openItemModal({ type: 'eat', itemId: item.id, dayDate, mode: 'edit' }); }} className="p-1.5 text-app-sub/40 hover:text-gold transition-colors"><Edit2 className="w-3.5 h-3.5" /></button>
            {item.addedBy === user.id && (
              <>
                <button aria-label="Delete" onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(item.id); }} className="p-1.5 text-app-sub/40 hover:text-rose-500 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
              </>
            )}
          </div>
        </div>

        {/* Icon + name + category — all left-aligned in same row */}
        <div className="flex items-center gap-3 mt-1">
          <div className="w-9 h-9 rounded-xl bg-app-faint flex items-center justify-center text-lg flex-shrink-0">
            <SubcategoryIcon name={item.meal} l1="food" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="text-sm font-bold text-app-text leading-snug line-clamp-1">{item.name}</div>
            </div>
            <div className="ds-label mt-0.5 min-h-[1lh]">
              {normalizeSubcat(item.meal)}{item.cuisine && ` · ${item.cuisine}`}
              <InlineRating rating={item.rating} total={item.ratingTotal} />
            </div>
          </div>
        </div>

        {item.notes && (
          <div className="pt-2 border-t border-app-border/10">
            <p className="text-xs text-app-sub/70 leading-relaxed line-clamp-3">{item.notes}</p>
          </div>
        )}
        {item.booked && <BookedStrip label="Reserved" date={item.bookingDate && shortDateLabel(item.bookingDate)} time={item.time} />}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {/* Scheduled items — no subheader label; press and hold a card to drag-reorder it */}
      {sortedInPlan.length > 0 && (
        <div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={e => handleDragEndGroup(e, inPlanItems, shortlistItems)}>
            <SortableContext items={sortedInPlan.map(e => e.id)} strategy={verticalListSortingStrategy}>
              <AnimatePresence mode="popLayout" initial={false}>
                {sortedInPlan.map((item) => (
                  <motion.div
                    key={item.id}
                    layoutId={item.id}
                    layout="position"
                    initial={{ opacity: 0, y: -12, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.97 }}
                    transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
                    className="mb-3 last:mb-0"
                  >
                    <SortableItem id={item.id}>
                      {renderCard(item)}
                    </SortableItem>
                  </motion.div>
                ))}
              </AnimatePresence>
            </SortableContext>
          </DndContext>
        </div>
      )}

      {/* Shortlist items — no subheader label. */}
      {sortedShortlist.length > 0 && (
        <div className={inPlanItems.length > 0 ? 'mt-4' : ''}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={e => handleDragEndGroup(e, shortlistItems, inPlanItems)}>
            <SortableContext items={sortedShortlist.map(e => e.id)} strategy={verticalListSortingStrategy}>
              <AnimatePresence mode="popLayout" initial={false}>
                {sortedShortlist.map((item) => (
                  <motion.div
                    key={item.id}
                    layoutId={item.id}
                    layout="position"
                    initial={{ opacity: 0, y: -12, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.97 }}
                    transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
                    className="mb-3 last:mb-0"
                  >
                    <SortableItem id={item.id}>
                      {renderCard(item)}
                    </SortableItem>
                  </motion.div>
                ))}
              </AnimatePresence>
            </SortableContext>
          </DndContext>
        </div>
      )}
      {/* Add to Shortlist — always shown at the end of the cards (the
          section header no longer carries its own "+"; this is the one add
          entry point), right before the Explorer CTA/carousel the parent
          renders next. */}
      <button
        onClick={onAddNew}
        className={`w-full min-h-[112px] flex flex-col items-center justify-center gap-2 p-4 rounded-2xl border-2 border-dashed border-app-border/50 bg-app-faint/70 text-app-sub/60 transition-all active:scale-[0.98] hover:border-app-sub/40 hover:text-app-sub ${
          inPlanItems.length > 0 || shortlistItems.length > 0 ? 'mt-4' : ''
        }`}
      >
        <div className="w-7 h-7 rounded-full flex items-center justify-center bg-app-sub text-white shadow-lg shadow-app-sub/20">
          <Plus className="w-3.5 h-3.5 stroke-[3]" />
        </div>
        <span className="text-[11px] font-black uppercase tracking-widest">Add to Shortlist</span>
      </button>
      {confirmDeleteId && (() => {
        const item = eats.find(e => e.id === confirmDeleteId);
        if (!item) return null;
        const expenseId = (item as any).expenseLogId as string | undefined;
        return (
          <ConfirmDeleteModal
            title="Delete this item?"
            message={expenseId
              ? `"${item.name}" will be permanently removed. Its logged expense stays in the Expenses tab unless you say otherwise.`
              : `"${item.name}" will be permanently removed.`}
            extraOption={expenseId ? {
              label: 'Delete its logged expense too',
              hint: 'Removes it from the Expenses tab and from everyone\u2019s balances.',
              disabledReason: linkedExpenseLock?.(expenseId),
            } : undefined}
            onCancel={() => setConfirmDeleteId(null)}
            onConfirm={async (alsoDeleteExpense) => {
              const saved = await remove(item.id);
              if (!saved) return false;
              // After the item, not with it: if this fails the expense is
              // still there to delete by hand, which is recoverable — an
              // expense deleted for an item that then failed to delete is not.
              if (alsoDeleteExpense && expenseId && onDeleteLinkedExpense) await onDeleteLinkedExpense(expenseId);
              setConfirmDeleteId(null);
              return saved;
            }}
          />
        );
      })()}
    </div>
  );
}


export function GoogleMapPreview({ address, mapUrl, forceExpanded = false }: { address: string; mapUrl?: string; forceExpanded?: boolean }) {
  const [isOpen, setIsOpen] = useState(true);
  
  if (!address) return null;
  const hasMap = !!mapUrl;

  return (
    <div>
      <button 
        type="button" aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        disabled={forceExpanded && hasMap}
        className={`flex items-center justify-between w-full min-h-11 group ${forceExpanded && hasMap ? 'cursor-default' : 'cursor-pointer'}`}
      >
        <div className="flex items-center gap-2 text-gold transition-colors">
          <MapIcon className="w-3 h-3" />
          <span className="text-[11px] font-black uppercase tracking-widest leading-none">Location</span>
        </div>
        {(!forceExpanded || !hasMap) && (
          <ChevronDown className={`w-3.5 h-3.5 text-gold transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={forceExpanded ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden space-y-4"
          >
            {hasMap && (
              <div className="aspect-[16/11] w-full rounded-2xl overflow-hidden bg-app-faint group relative border border-app-border">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  allowFullScreen
                  src={`https://www.google.com/maps/embed/v1/place?key=${(import.meta as any).env.VITE_GOOGLE_MAPS_API_KEY || ''}&q=${encodeURIComponent(address)}`}
                  className="border-0 grayscale-[0.2] opacity-90 hover:grayscale-0 transition duration-200"
                />
                {!(import.meta as any).env.VITE_GOOGLE_MAPS_API_KEY && (
                  <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-balance text-xs bg-white/95 text-app-sub font-medium leading-relaxed">
                    Google Maps API required for live preview.
                  </div>
                )}
              </div>
            )}
            
            <div className="flex gap-2">
              <div className="flex-1 bg-white p-3 rounded-xl border border-app-border/40 min-h-[44px] flex items-center">
                <address className="not-italic text-sm text-app-sub leading-snug font-normal break-words">
                  {address}
                </address>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function AccommodationSection({ accommodation, accent, expenses, hideExpenseLink = false, hideCosts = false, children, bookingSectionId, travellers }: { accommodation: Day['accommodation']; accent: string; expenses?: Expense[]; hideExpenseLink?: boolean; hideCosts?: boolean; children?: React.ReactNode; bookingSectionId?: string; travellers?: User[] }) {

  // Extract the HH:MM portion. If checkIn/checkOut is date-only (no 'T'),
  // there is no time to show — return null so the slot renders "—".
  const formattedCheckInTime = accommodation.checkIn && accommodation.checkIn.includes('T')
    ? formatClock(accommodation.checkIn)
    : null;
  const formattedCheckOutTime = accommodation.checkOut && accommodation.checkOut.includes('T')
    ? formatClock(accommodation.checkOut)
    : null;
  const inDate = accommodation.checkIn ? calendarDate(accommodation.checkIn) : null;
  const outDate = accommodation.checkOut ? calendarDate(accommodation.checkOut) : null;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="text-base font-bold text-app-text tracking-tight leading-tight">{accommodation.name}</div>
                {accommodation.imported && (
                  <div className="text-[10px] font-semibold text-gold/70 leading-none flex items-center gap-0.5 shrink-0 mt-0.5">
                    <FileInput className="w-2.5 h-2.5" />
                    <span>Import</span>
                  </div>
                )}
              </div>
              {accommodation.category && (
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <SubcategoryIcon name={accommodation.category} l1="accommodation" />
                    <span className="ds-label">{normalizeSubcat(accommodation.category)}</span>
                  </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="bg-app-faint rounded-2xl py-3 px-4 border border-app-border shadow-inner text-center">
              <div className="flex items-center justify-center gap-1 mb-1.5">
                <LogIn className="w-3 h-3 text-app-sub/50" />
                <div className="text-sm text-app-sub">Check-in</div>
              </div>
              <div className="text-2xl font-serif font-black text-app-text leading-none">{formattedCheckInTime || '--:--'}</div>
              {inDate && !isNaN(inDate.getTime()) && (
                  <div className="text-sm text-app-sub mt-1.5">
                  {inDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
              )}
            </div>
            <div className="bg-app-faint rounded-2xl py-3 px-4 border border-app-border shadow-inner text-center">
              <div className="flex items-center justify-center gap-1 mb-1.5">
                <LogOut className="w-3 h-3 text-app-sub/50" />
                <div className="text-sm text-app-sub">Check-out</div>
              </div>
              <div className="text-2xl font-serif font-black text-app-text leading-none">{formattedCheckOutTime || '--:--'}</div>
              {outDate && !isNaN(outDate.getTime()) && (
                <div className="text-sm text-app-sub mt-1.5">
                  {outDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-0">
      <GoogleMapPreview address={accommodation.address} mapUrl={accommodation.mapUrl} />
      {(accommodation.area || accommodation.stars != null || accommodation.notes) && <ItemDetailsSection>
        {accommodation.area && <p className="text-sm text-app-text"><span className="text-app-sub">Area: </span>{accommodation.area}</p>}
        {accommodation.stars != null && <p className="text-sm text-app-text"><span className="text-app-sub">Property stars: </span>{accommodation.stars}</p>}
        {accommodation.notes && <div className="space-y-1"><span className="text-sm text-app-sub font-semibold">Notes</span>
          <p className="text-app-sub text-sm font-normal whitespace-pre-wrap break-words">{renderWithLinks(accommodation.notes)}</p></div>}
      </ItemDetailsSection>}
      {((!hideCosts && hasBookingContent(accommodation)) || children) && <ItemDetailsSection id={bookingSectionId} label="Booking & payment">
        <AssignedTravellers ids={accommodation.participantIds} travellers={travellers} />
        {!hideCosts && <><SavedBookingInformation item={accommodation} /><BookingCosts item={accommodation} sourceType="accommodation" expenses={expenses} hideExpenseLink={hideExpenseLink} /></>}
        {children}
      </ItemDetailsSection>}
      </div>
    </div>
  );
}

/** The same entered fields appear when opened from Shortlist or a linked expense. */
export function ShortlistDetails({ item }: { item: { id?: string; cuisine?: string; tip?: string; link?: string; durationMinutes?: number; bookingDate?: string; time?: string } }) {
  if (!item.cuisine && !item.tip && !item.link && item.durationMinutes == null) return null;
  return <ItemDetailsSection>
    {item.cuisine && <p className="text-sm text-app-text"><span className="text-app-sub">Cuisine: </span>{item.cuisine}</p>}
    {item.tip && <p className="text-sm text-app-sub whitespace-pre-wrap">{renderWithLinks(item.tip)}</p>}
    {item.link && <p className="text-sm text-app-sub break-words">{renderWithLinks(item.link)}</p>}
    {item.durationMinutes != null && <p className="text-sm text-app-sub">Duration: {formatMinutes(item.durationMinutes)}</p>}
  </ItemDetailsSection>;
}

// ─── NotesList with drag-to-reorder ─────────────────────────────────────────

type NoteEntry = {
  id: string;
  name: string;
  booked?: boolean;
  ref?: string;
  bookingService?: string;
  bookingNotes?: string;
  notes?: string;
  cuisine?: string;
  tip?: string;
  link?: string;
  durationMinutes?: number;
  address?: string;
  mapUrl?: string;
  costAmount?: number;
  costCurrency?: string;
  costKind?: BookingCostKind;
  paidStatus?: string;
  depositAmount?: number;
  depositCurrency?: string;
  /** Linked Expense doc id, once "Log as expense" is used — same pattern as
   *  attraction/eat/nightlife items (see upsertExpenseFromItem). */
  expenseLogId?: string;
  addedBy: string;
  createdAt: string;
  order?: number;
};

function NotesList({
  notes,
  currentUserId,
  onReorder,
  onDeleteLinkedExpense,
  linkedExpenseLock,
  onView,
  onEdit,
  onDelete,
}: {
  notes: NoteEntry[];
  currentUserId: string;
  onReorder: (notes: NoteEntry[]) => void;
  /** See AttractionsList — the expense lives on DayDetail, not here. */
  onDeleteLinkedExpense?: (expenseId: string) => Promise<boolean>;
  /** Why the linked expense must stay (someone else settled against it), or null. */
  linkedExpenseLock?: (expenseId: string) => string | null;
  onView: (note: NoteEntry) => void;
  onEdit: (note: NoteEntry) => void;
  onDelete: (id: string) => Promise<boolean>;
}) {
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const sensors = useSensors(
    // Delay-based (not distance-based) so a quick tap/scroll never triggers
    // a drag — only holding still for ~450ms does (bumped from 250ms after
    // testing — long enough that a normal tap/scroll never mistakenly
    // triggers a reorder), matching "press and hold
    // to reorder" rather than "any small drag reorders."
    useSensor(PointerSensor, { activationConstraint: { delay: 450, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = notes.findIndex(n => n.id === active.id);
    const newIdx = notes.findIndex(n => n.id === over.id);
    onReorder(withOrder(arrayMove(notes, oldIdx, newIdx)));
  };

  if (notes.length === 0) return null;

  return (
    <>
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={notes.map(n => n.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-3 mb-3">
          {notes.map((note) => (
            <SortableItem key={note.id} id={note.id}>
              <div
                id={`activity-${note.id}`}
                onClick={() => onView(note)}
                role="button"
                tabIndex={0}
                className="bg-white border border-app-border rounded-2xl p-3 pb-4 shadow-sm space-y-2 cursor-pointer hover:border-gold/30 transition-colors"
              >
                <div className="flex justify-between items-center">
                  <By userId={note.addedBy} />
                  {note.addedBy === currentUserId && (
                    <div className="flex items-center gap-1">
                      <button aria-label="Edit" onClick={(e) => { e.stopPropagation(); onEdit(note); }} className="p-1.5 text-app-sub/40 hover:text-gold hover:bg-gold/5 rounded-lg transition-colors">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button aria-label="Delete" onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(note.id); }} className="p-1.5 text-app-sub/40 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="text-sm font-bold text-app-text leading-snug line-clamp-1">{note.name}</div>
                {note.notes && (
                  <p className="text-xs text-app-sub/70 leading-relaxed line-clamp-3">{note.notes}</p>
                )}
              </div>
            </SortableItem>
          ))}
        </div>
      </SortableContext>
    </DndContext>
    {confirmDeleteId && (() => {
      const note = notes.find(n => n.id === confirmDeleteId);
      if (!note) return null;
      const expenseId = note.expenseLogId;
      return (
        <ConfirmDeleteModal
          title="Delete this note?"
          message={expenseId
            ? `"${note.name}" will be permanently removed. Its logged expense stays in the Expenses tab unless you say otherwise.`
            : `"${note.name}" will be permanently removed.`}
          extraOption={expenseId ? {
            label: 'Delete its logged expense too',
            hint: 'Removes it from the Expenses tab and from everyone\u2019s balances.',
            disabledReason: linkedExpenseLock?.(expenseId),
          } : undefined}
          onCancel={() => setConfirmDeleteId(null)}
          onConfirm={async (alsoDeleteExpense) => {
            const saved = await onDelete(note.id);
            if (!saved) return false;
            if (alsoDeleteExpense && expenseId && onDeleteLinkedExpense) await onDeleteLinkedExpense(expenseId);
            setConfirmDeleteId(null);
            return saved;
          }}
        />
      );
    })()}
    </>
  );
}


function DayDetail({ day, displayVisit, onUpdate: persistDay, onUpdateConfig, currentUser, tripConfig, onOpenImport, mode = 'full', hasPrevDest, hasNextDest, onNavigateDest }: { day: Day; displayVisit?: Destination; onUpdate: (d: Day, previous: Day, expense?: Expense) => Promise<void>; onUpdateConfig?: (c: TripConfig) => void; currentUser: User; tripConfig: TripConfig; onOpenImport?: () => void; mode?: 'full' | 'shortlist'; hasPrevDest?: boolean; hasNextDest?: boolean; onNavigateDest?: (dir: 'prev' | 'next') => void }) {
  const creationDates = useRef<Record<string, string>>({});
  const [savingItem, setSavingItem] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const onUpdate = async (updated: Day, previous = day, expense?: Expense): Promise<boolean> => {
    try {
      await persistDay(updated, previous, expense);
      setSaveError(null);
      return true;
    } catch (error) {
      setSaveError(userMessage(error, 'Could not save. Check your connection and try again.'));
      return false;
    }
  };
  const formRef = useRef<HTMLDivElement>(null);

  const [showAdd, setShowAdd] = useState<{ type: string | null; data?: any }>({ type: null });

  useEffect(() => {
    if (showAdd.type && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showAdd.type]);

  // "Add new shortlist item" — a two-step chooser (Add / Import) then the
  // actual form, all inside one portal modal, matching the Add Accommodation/
  // Add Transport pattern in TripOverview.
  const [addItemModal, setAddItemModal] = useState<{ type: 'attractions' | 'food' | 'nightlife' | 'shopping' | 'notes'; step: 'choose' | 'form' } | null>(null);

  // Portal-modal viewer/editor for attraction/eat/nightlife/note cards —
  // opened either by clicking a card (or its edit pencil) in this day
  // (AttractionsList/EatsList/NotesList dispatch the event directly), or by
  // a cross-tab link (handleNavDay switches to this day, then dispatches
  // the same event once DayDetail has mounted with it).
  const [viewingItem, setViewingItem] = useState<{ type: 'attraction' | 'eat' | 'nightlife' | 'shopping' | 'note'; itemId: string; mode: 'view' | 'edit'; baseline?: Day } | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as ItemModalRequest | undefined;
      if (!detail || detail.dayDate !== day.date) return;
      if (detail.type !== 'attraction' && detail.type !== 'eat' && detail.type !== 'nightlife' && detail.type !== 'shopping' && detail.type !== 'note') return;
      setViewingItem(current => current?.itemId === detail.itemId && current.type === detail.type && current.mode === (detail.mode ?? 'view')
        ? current
        : { type: detail.type as 'attraction' | 'eat' | 'nightlife' | 'shopping' | 'note', itemId: detail.itemId, mode: detail.mode ?? 'view', baseline: detail.mode === 'edit' ? day : undefined });
    };
    window.addEventListener('open-item-modal', handler);
    return () => window.removeEventListener('open-item-modal', handler);
  }, [day]);

  // Which sections currently have the Talisman Explorer carousel open.
  // Replaces the old per-section Plan/Explore toggle: the saved list is now
  // always what a section shows, and Explorer opens *underneath* it as an
  // additional carousel rather than swapping the section's contents out.
  const [explorerOpen, setExplorerOpen] = useState<Record<string, boolean>>({
    attractions: false,
    food: false,
    nightlife: false,
  });
  const [editingEmoji, setEditingEmoji] = useState(false);
  const isAdmin = isTripAdmin(tripConfig, currentUser.id);

  const destinations = tripConfig.destinations || [];
  const destIdx = destinations.findIndex(d => {
    const dId = d.name.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, '-');
    return dId === day.destinationId;
  });

  const updateDestEmoji = (emoji: string) => {
    if (destIdx !== -1 && onUpdateConfig) {
      const newDests = [...destinations];
      newDests[destIdx] = { ...newDests[destIdx], emoji };
      onUpdateConfig({ ...tripConfig, destinations: newDests });
      setEditingEmoji(false);
    }
  };


  // Preparation performs no writes. The day transaction owns creation and metadata sync.
  /**
   * Delete the expense an item created, when the traveller explicitly asks
   * for it on the delete confirmation.
   *
   * Removing an item has never destroyed its expense — the money was spent
   * whatever happened to the plan, so it's soft-flagged as no longer linked
   * and stays in the Expenses tab. That's the right default and it stays the
   * default; this is the other half, for when the item was the only reason
   * the expense existed.
   *
   * Goes through saveExpenseDelete, the same transaction the Expenses tab
   * uses, so a deletion from here is subject to the same checks as one from
   * there rather than being a second way to remove the same thing.
   */
  const lockForLinkedExpense = (expenseId: string) =>
    linkedExpenseLock((tripConfig.expenses || []).find(e => e.id === expenseId), currentUser.id, tripConfig.travellers || []);
  const deleteLinkedExpense = async (expenseId: string): Promise<boolean> => {
    const expense = (tripConfig.expenses || []).find(e => e.id === expenseId);
    if (!expense) return true; // Already gone; nothing to do.
    // Re-checked here, not just in the dialog: a settlement can land while it's open.
    if (lockForLinkedExpense(expenseId)) return true;
    try {
      await saveExpenseDelete(tripConfig.id, expense);
      return true;
    } catch (err) {
      // Non-fatal: the item is already deleted, and the expense is still
      // there to remove by hand.
      console.error('Could not delete the linked expense:', err);
      return false;
    }
  };

  const prepareExpenseFromItem = (item: any, sourceType: string, itemId: string, desc: string, cat: string, catName: string, date: string) =>
    prepareBookingExpense(tripConfig, { ...item, id: itemId }, currentUser.id,
      { desc, cat, catName, date, sourceType }, getExchangeRate);




  const [isAddingAccommodation, setIsAddingAccommodation] = useState(false);
  const accommodationFormRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isAddingAccommodation && accommodationFormRef.current) {
      accommodationFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isAddingAccommodation]);

  // Fallback for single accommodation (backward compatibility)

  // Write only the accommodations field — targeted updateDoc so the Firestore diff
  // is limited to ['accommodations'], which any accepted member is allowed to update.
  // Using the full setDoc path (onUpdateConfig) would diff all config fields and fail
  // the member update rule if any unrelated field has drifted.




  /**
   * Bring the card the traveller just saved into view and flash it.
   *
   * The save is committed by the time this runs, but the card only exists
   * once the itinerary snapshot has come back with it — usually immediate
   * from Firestore's local cache, occasionally not. Either way, saving into a
   * long list that's scrolled elsewhere looks like nothing happened. So this
   * waits for the card to appear rather than assuming it already has, then
   * brings it into view.
   */
  const revealSavedItem = (itemId: string) => {
    if (typeof document === 'undefined') return;
    const deadline = Date.now() + 5000;
    const find = () => {
      const el = document.getElementById(`activity-${itemId}`);
      if (!el) {
        if (Date.now() < deadline) setTimeout(find, 120);
        return;
      }
      // Scroll only. A gold ring used to flash here too; bringing the card
      // into view is the part that says "here it is", and the flash on top of
      // it read as an alarm rather than a confirmation.
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    setTimeout(find, 60);
  };

  const addItem = async (type: 'attractions' | 'food' | 'nightlife' | 'shopping', item: any, fromExplore = false) => {
    const itemId = item._entryId || Math.random().toString(36).substr(2, 9);
    const newItem: any = {
      id: itemId,
      name: item.name,
      booked: !!item.booked,
      ...(item.ref ? { ref: item.ref } : {}),
      ...(item.time ? { time: item.time } : {}),
      ...(item.bookingDate ? { bookingDate: item.bookingDate } : {}),
      ...(item.bookingService ? { bookingService: item.bookingService } : {}),
      ...(item.bookingNotes ? { bookingNotes: item.bookingNotes } : {}),
      ...(item.cuisine ? { cuisine: item.cuisine } : {}),
      ...(item.tip ? { tip: item.tip } : {}),
      ...(item.link ? { link: item.link } : {}),
      ...(item.durationMinutes != null ? { durationMinutes: item.durationMinutes } : {}),
      ...(item.participantIds ? { participantIds: item.participantIds } : {}),
      addedBy: item.addedBy || currentUser.id,
      notes: item.notes || item.desc || "",
      address: item.address || "",
      mapUrl: item.mapUrl || "",
      inPlan: false, // manual entries go to Shortlist by default
      createdAt: creationDates.current[itemId] ||= new Date().toISOString(),
      ...(item.costAmount !== undefined ? { costAmount: item.costAmount } : {}),
      ...(item.costCurrency ? { costCurrency: item.costCurrency } : {}),
      ...(item.costKind ? { costKind: item.costKind } : {}),
      ...(item.paidStatus ? { paidStatus: item.paidStatus } : {}),
      ...(item.depositAmount !== undefined ? { depositAmount: item.depositAmount } : {}),
      ...(item.depositCurrency ? { depositCurrency: item.depositCurrency } : {}),
      // Google rating, when the item came from Talisman Explorer (which
      // attaches it via Places). Carried onto the saved item so the card
      // can show "RESTAURANT · ★ 4.6 (1,234)" without re-querying Places.
      ...(item.rating != null ? { rating: item.rating } : {}),
      ...(item.ratingTotal != null ? { ratingTotal: item.ratingTotal } : {}),
    };

    if (type === 'attractions') {
      const cat = ATTRACTION_CATS.find(c => c.name === normalizeSubcat(item.category));
      const expense = await prepareExpenseFromItem(item, 'attraction', itemId, item.name, cat?.emoji || '🏛️', item.category || 'Attraction', day.date);
      if (expense) newItem.expenseLogId = expense.id;
      if (!await onUpdate({ ...day, attractions: [...day.attractions, { ...newItem, category: item.category || undefined } as any] }, day, expense)) return false;
      if (tripConfig.id) {
        notifyShortlisted(
          tripConfig.id, tripConfig.title, currentUser.id, getFirstName(currentUser.name),
          (day.travellers?.length ? day.travellers : tripConfig.memberIds) || [],
          newItem.name, day.city || day.country, day.date, 'attractions',
          cat?.emoji || '🏛️', itemId
        ).catch(console.error);
      }
    } else if (type === 'food') {
      // Explore recs carry the subcategory in `category`; manual adds use `meal`.
      // Honour either so an Explore Café/Bar/Snacks maps correctly (not Restaurant).
      const mealName = item.meal || item.category || 'Restaurant';
      const mealEmoji = subcatEmoji(mealName, 'food');
      const expense = await prepareExpenseFromItem(item, 'food', itemId, item.name, mealEmoji, mealName, day.date);
      if (expense) newItem.expenseLogId = expense.id;
      if (!await onUpdate({ ...day, eats: [...day.eats, { ...newItem, meal: mealName } as any] }, day, expense)) return false;
      if (tripConfig.id) {
        notifyShortlisted(
          tripConfig.id, tripConfig.title, currentUser.id, getFirstName(currentUser.name),
          (day.travellers?.length ? day.travellers : tripConfig.memberIds) || [],
          newItem.name, day.city || day.country, day.date, 'eats',
          mealEmoji, itemId
        ).catch(console.error);
      }
    } else if (type === 'nightlife') {
      // Bar moved to Food & Drink, so Entertainment defaults to 'Other' (not 'Bar').
      const catName = item.category || 'Other';
      const catEmoji = subcatEmoji(catName, 'nightlife');
      const expense = await prepareExpenseFromItem(item, 'nightlife', itemId, item.name, catEmoji, catName, day.date);
      if (expense) newItem.expenseLogId = expense.id;
      if (!await onUpdate({ ...day, nightlife: [...(day.nightlife || []), { ...newItem, category: catName } as any] }, day, expense)) return false;
      if (tripConfig.id) {
        notifyShortlisted(
          tripConfig.id, tripConfig.title, currentUser.id, getFirstName(currentUser.name),
          (day.travellers?.length ? day.travellers : tripConfig.memberIds) || [],
          newItem.name, day.city || day.country, day.date, 'attractions',
          catEmoji, itemId
        ).catch(console.error);
      }
    } else {
      // Shopping has no subcategory — a single fixed emoji, like Other/notes.
      const expense = await prepareExpenseFromItem(item, 'shopping', itemId, item.name, '🛍️', 'Shopping', day.date);
      if (expense) newItem.expenseLogId = expense.id;
      if (!await onUpdate({ ...day, shopping: [...(day.shopping || []), newItem] }, day, expense)) return false;
      if (tripConfig.id) {
        // notifyShortlisted's `section` union only covers 'attractions'|'eats'
        // (nightlife reuses 'attractions' too, above) — the actual navigation
        // re-derives the true section by scanning for the itemId (see
        // handleNavDay's day.shopping check), so this coarse value only
        // matters as a type-check placeholder.
        notifyShortlisted(
          tripConfig.id, tripConfig.title, currentUser.id, getFirstName(currentUser.name),
          (day.travellers?.length ? day.travellers : tripConfig.memberIds) || [],
          newItem.name, day.city || day.country, day.date, 'attractions',
          '🛍️', itemId
        ).catch(console.error);
      }
    }
    // Adding from the Explore tab keeps the user on Explore (so they can keep
    // browsing/adding). Manual form adds switch to the Plan tab.
    if (!fromExplore) {
      setShowAdd({ type: null });
      setExplorerOpen(prev => ({ ...prev, [type]: false }));
    }
    revealSavedItem(itemId);
    return true;
  };

  // item shape matches ItemAddForm's onSave payload (name/notes/address/mapUrl/
  // cost fields) — notes are now full shortlist items, just without a category.
  // sourceType 'other' resolves to the taxonomy's Other L1 (taxonomy.ts
  // l1KeyForType's default case) — the same bucket Expenses.tsx's own
  // CATEGORIES list uses for anything with no natural category.
  const addNote = async (item: any) => {
    const noteId = item._entryId || Math.random().toString(36).substr(2, 9);
    const expense = await prepareExpenseFromItem(item, 'other', noteId, item.name, '💲', 'Other', day.date);
    const expenseLogId = expense?.id;
    const newNote: NoteEntry = {
      cuisine: item.cuisine,
      tip: item.tip,
      link: item.link,
      durationMinutes: item.durationMinutes,
      booked: !!item.booked,
      ref: item.ref || undefined,
      bookingService: item.bookingService || undefined,
      bookingNotes: item.bookingNotes || undefined,
      id: noteId,
      name: item.name,
      notes: item.notes || undefined,
      address: item.address || undefined,
      mapUrl: item.mapUrl || undefined,
      costAmount: item.costAmount,
      costCurrency: item.costCurrency,
      costKind: item.costKind,
      paidStatus: item.paidStatus,
      depositAmount: item.depositAmount,
      depositCurrency: item.depositCurrency,
      ...(expenseLogId ? { expenseLogId } : {}),
      addedBy: currentUser.id,
      createdAt: creationDates.current[noteId] ||= new Date().toISOString()
    };
    if (!await onUpdate({ ...day, notes: [...(day.notes || []), newNote] }, day, expense)) return false;

    notifyDayNoteAdded(
      tripConfig.id, tripConfig.title,
      currentUser.id, getFirstName(currentUser.name),
      (day.travellers?.length ? day.travellers : tripConfig.memberIds) || [],
      day.city || day.country,
      day.date,
      newNote.id
    ).catch(console.error);
    return true;
  };

  const editNote = async (id: string, item: any, baseline = day) => {
    // Mirrors editItem's pattern exactly: create the linked expense once
    // (never re-derive amount/split on a later edit — same limitation as
    // attraction/food/nightlife), otherwise just keep its name/icon in sync.
    const existing = (baseline.notes || []).find(n => n.id === id);
    let expenseLogId: string | undefined = existing?.expenseLogId;
    let expense: Expense | undefined;
    if (!expenseLogId) {
      expense = await prepareExpenseFromItem(item, 'other', id, item.name, '💲', 'Other', day.date);
      if (expense) expenseLogId = expense.id;
    }
    const newNotes = (baseline.notes || []).map(n => n.id === id ? {
      ...n,
      cuisine: item.cuisine,
      tip: item.tip,
      link: item.link,
      durationMinutes: item.durationMinutes,
      name: item.name,
      booked: !!item.booked,
      ref: item.ref || '',
      bookingService: item.bookingService || '',
      bookingNotes: item.bookingNotes || '',
      notes: item.notes || undefined,
      address: item.address || undefined,
      mapUrl: item.mapUrl || undefined,
      costAmount: item.costAmount,
      costCurrency: item.costCurrency,
      costKind: item.costKind,
      paidStatus: item.paidStatus,
      depositAmount: item.depositAmount,
      depositCurrency: item.depositCurrency,
      ...(expenseLogId ? { expenseLogId } : {}),
    } : n);
    return onUpdate({ ...baseline, notes: newNotes }, baseline, expense);
  };

  const deleteNote = async (id: string) => {
    const newNotes = (day.notes || []).filter(n => n.id !== id);
    if (!await onUpdate({ ...day, notes: newNotes })) return false;
    return true;
  };

  const editItem = async (type: 'attractions' | 'food' | 'nightlife' | 'shopping', item: any, baseline = day) => {
    const itemId = item.id;
    const previous = (type === 'food' ? baseline.eats : baseline[type] || []).find(entry => entry.id === itemId);
    // Resolve this item's category emoji + name once (used for create AND sync).
    let catEmoji = '🏛️', catName = 'Attraction', sourceType = 'attraction';
    if (type === 'food') {
      catEmoji = Object.fromEntries(DINING_CATS.map(c => [c.name, c.emoji]))[item.meal || 'Restaurant'] || '🍽️';
      catName = item.meal || 'Restaurant'; sourceType = 'food';
    } else if (type === 'nightlife') {
      // 'Bar' is a Food & Drink subcategory, so this lookup never matched and
      // every uncategorised Entertainment item fell back to a cocktail glass.
      const c = NIGHTLIFE_CATS.find((c: any) => c.name === normalizeSubcat(item.category || 'Other'));
      catEmoji = (c as any)?.emoji || subcatEmoji('Other', 'nightlife'); catName = item.category || 'Other'; sourceType = 'nightlife';
    } else if (type === 'shopping') {
      // No subcategory — a single fixed emoji, like Other/notes.
      catEmoji = '🛍️'; catName = 'Shopping'; sourceType = 'shopping';
    } else {
      const c = ATTRACTION_CATS.find(c => c.name === normalizeSubcat(item.category));
      catEmoji = c?.emoji || '🏛️'; catName = item.category || 'Attraction';
    }

    let expenseLogId: string | undefined = item.expenseLogId;
    let expense: Expense | undefined;
    if (!expenseLogId) {
      expense = await prepareExpenseFromItem(item, sourceType, itemId, item.name, catEmoji, catName, day.date);
      if (expense) expenseLogId = expense.id;
    }
    const savedItem = Object.fromEntries(Object.entries(item).filter(([key]) => !key.startsWith('_')));
    const finalItem = { ...savedItem, ...(expenseLogId ? { expenseLogId } : {}) };
    if (type === 'attractions') {
      const newList = baseline.attractions.map(a => a.id === itemId ? { ...a, ...finalItem } : a);
      if (!await onUpdate({ ...baseline, attractions: newList }, baseline, expense)) return false;
    } else if (type === 'food') {
      const newList = baseline.eats.map(e => e.id === itemId ? { ...e, ...finalItem } : e);
      if (!await onUpdate({ ...baseline, eats: newList }, baseline, expense)) return false;
    } else if (type === 'shopping') {
      const newList = (baseline.shopping || []).map(s => s.id === itemId ? { ...s, ...finalItem } : s);
      if (!await onUpdate({ ...baseline, shopping: newList }, baseline, expense)) return false;
    } else {
      const newList = (baseline.nightlife || []).map(n => n.id === itemId ? { ...n, ...finalItem } : n);
      if (!await onUpdate({ ...baseline, nightlife: newList }, baseline, expense)) return false;
    }
    if (previous?.booked && item.booked && item.bookingDate
      && item.bookingDate !== (previous.bookingDate || baseline.date)
      && previous.addedBy && previous.addedBy !== currentUser.id) {
      notifyItemDateChanged(
        tripConfig.id, tripConfig.title, currentUser.id, getFirstName(currentUser.name), [previous.addedBy],
        item.name, fmtShortDate(item.bookingDate), baseline.date, type === 'food' ? 'eats' : 'attractions', catEmoji, itemId,
      ).catch(console.error);
    }
    setShowAdd({ type: null });
    return true;
  };

  if (day.city === "Gap in Itinerary") {
    return (
      <div className="pb-32 px-1 space-y-6">
        <Card className="p-10 text-center text-balance bg-white border-dashed border-2 border-app-border/40 rounded-2xl shadow-sm">
          <div className="w-16 h-16 bg-app-faint rounded-full flex items-center justify-center mx-auto text-3xl mb-6 grayscale opacity-40">🗺️</div>
          <p className="text-app-text font-serif text-xl font-bold mb-1">No destination added</p>
          <p className="text-app-sub/60 text-sm mb-8">Nothing is planned for {day.dateFull}.</p>
          <button
            onClick={() => onUpdate({ ...day, city: "MANAGE_TRIP_TRIGGER" })}
            className="w-full py-4 bg-gold text-white rounded-xl text-[11px] font-black uppercase tracking-widest shadow-lg shadow-gold/20 hover:brightness-110 active:scale-95 transition flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add a destination
          </button>
        </Card>
      </div>
    );
  }

  return (
    <motion.div
      key={day.id}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="pb-32 space-y-4"
    >
      {saveError && <div role="alert" className={ERROR_BOX}>Changes were not saved. {saveError}</div>}
      {/* Clearance below the hero card before the first section header.
          The header's sticky wrapper paints an opaque bg-app-bg band, which
          cuts off the hero card's drop shadow where the two meet — so this
          needs enough room for the shadow to fall away completely, not just
          enough to look un-cramped. mb-2 then mb-6 both still clipped it. */}
      <div className="mb-10">
      <DestinationHeroCard
        dateLabel={
          displayVisit ? fmtDateRange(displayVisit.arrivalDate, displayVisit.departureDate) : destinations[destIdx]?.arrivalDate && destinations[destIdx]?.departureDate
            ? fmtDateRange(destinations[destIdx].arrivalDate!, destinations[destIdx].departureDate!)
            : day.dateFull
        }
        name={day.city}
        flag={day.flag}
        country={day.country}
        cityBg={day.cityBg}
        accent={day.accent}
        emoji={day.emoji}
        canEditEmoji={isAdmin}
        editingEmoji={editingEmoji}
        onEditEmoji={() => isAdmin && setEditingEmoji(true)}
        onSelectEmoji={(emoji) => updateDestEmoji(emoji)}
        onCloseEmojiPicker={() => setEditingEmoji(false)}
        hasPrev={hasPrevDest}
        hasNext={hasNextDest}
        onNavigateDest={onNavigateDest}
        weatherSlot={
          <DestinationForecastRange
            city={day.city}
            startDate={displayVisit?.arrivalDate || destinations[destIdx]?.arrivalDate || day.date}
            endDate={displayVisit?.departureDate || destinations[destIdx]?.departureDate || day.date}
            fallbackWeather={day.weather}
          />
        }
        nightsLabel={displayVisit ? `${nightsBetween(displayVisit.arrivalDate, displayVisit.departureDate)} nights` : `${day.nights} ${day.nights === 1 ? 'Night' : 'Nights'}`}
      />
      </div>

      {/* Transport Section — owned by the Trips tab now; only shown outside Shortlists mode */}

      {/* Accommodation Section — owned by the Trips tab now; only shown outside Shortlists mode */}

      {/* Attractions Section */}
      <AccordionSection id="section-attractions" icon={React.createElement(L1_BY_KEY.experiences.icon)} title={L1_BY_KEY.experiences.label} accent={day.accent} defaultOpen
        stickyHeader
      >
          <div className="space-y-4">
            <AttractionsList
              onDeleteLinkedExpense={deleteLinkedExpense}
              linkedExpenseLock={lockForLinkedExpense}
              attractions={day.attractions}
              onUpdate={async (list) => {
                if (!await onUpdate({ ...day, attractions: list })) return false;
                return true;
              }}
              onAddNew={() => setAddItemModal({ type: 'attractions', step: 'choose' })}
              user={currentUser}
              variant="attraction"
              isAdmin={isAdmin}
              tripId={tripConfig.id}
              tripTitle={tripConfig.title}
              allMemberIds={tripConfig.memberIds || []}
              location={day.city}
              dayDate={day.date}
            />
          <AnimatePresence initial={false}>
            {explorerOpen.attractions && (
              <ExplorerCarousel
                city={day.city}
                type="attractions"
                onAdd={(i) => addItem('attractions', i, true)}
                tripId={tripConfig.id}
                dayDate={day.date}
                userId={currentUser.id}
                existingNames={day.attractions.map(a => a.name)}
                onClose={() => setExplorerOpen(prev => ({ ...prev, attractions: false }))}
              />
            )}
          </AnimatePresence>
          {!explorerOpen.attractions && (
            <ExplorerCta onClick={() => setExplorerOpen(prev => ({ ...prev, attractions: true }))} />
          )}
          </div>
      </AccordionSection>

      {/* Dining Section */}
      <AccordionSection id="section-eats" icon={React.createElement(L1_BY_KEY.food.icon)} title={L1_BY_KEY.food.label} accent="#f59e0b" defaultOpen
        stickyHeader
      >
          <div className="space-y-4">
            <EatsList
              onDeleteLinkedExpense={deleteLinkedExpense}
              linkedExpenseLock={lockForLinkedExpense}
              eats={day.eats}
              onUpdate={async (list) => {
                if (!await onUpdate({ ...day, eats: list })) return false;
                return true;
              }}
              onAddNew={() => setAddItemModal({ type: 'food', step: 'choose' })}
              user={currentUser}
              isAdmin={isAdmin}
              tripId={tripConfig.id}
              tripTitle={tripConfig.title}
              allMemberIds={tripConfig.memberIds || []}
              location={day.city}
              dayDate={day.date}
            />
          <AnimatePresence initial={false}>
            {explorerOpen.food && (
              <ExplorerCarousel
                city={day.city}
                type="food"
                onAdd={(i) => addItem('food', i, true)}
                tripId={tripConfig.id}
                dayDate={day.date}
                userId={currentUser.id}
                existingNames={day.eats.map(e => e.name)}
                onClose={() => setExplorerOpen(prev => ({ ...prev, food: false }))}
              />
            )}
          </AnimatePresence>
          {!explorerOpen.food && (
            <ExplorerCta onClick={() => setExplorerOpen(prev => ({ ...prev, food: true }))} />
          )}
          </div>
      </AccordionSection>

      {/* Nightlife Section */}
      <AccordionSection id="section-nightlife" icon={React.createElement(L1_BY_KEY.nightlife.icon)} title={L1_BY_KEY.nightlife.label} accent="#818cf8" defaultOpen
        stickyHeader
      >
          <div className="space-y-4">
            <AttractionsList
              onDeleteLinkedExpense={deleteLinkedExpense}
              linkedExpenseLock={lockForLinkedExpense}
              attractions={day.nightlife || []}
              onUpdate={async (list) => {
                if (!await onUpdate({ ...day, nightlife: list })) return false;
                return true;
              }}
              onAddNew={() => setAddItemModal({ type: 'nightlife', step: 'choose' })}
              user={currentUser}
              variant="nightlife"
              isAdmin={isAdmin}
              tripId={tripConfig.id}
              tripTitle={tripConfig.title}
              allMemberIds={tripConfig.memberIds || []}
              location={day.city}
              dayDate={day.date}
            />
          <AnimatePresence initial={false}>
            {explorerOpen.nightlife && (
              <ExplorerCarousel
                city={day.city}
                type="nightlife"
                onAdd={(i) => addItem('nightlife', i, true)}
                tripId={tripConfig.id}
                dayDate={day.date}
                userId={currentUser.id}
                existingNames={(day.nightlife || []).map(n => n.name)}
                onClose={() => setExplorerOpen(prev => ({ ...prev, nightlife: false }))}
              />
            )}
          </AnimatePresence>
          {!explorerOpen.nightlife && (
            <ExplorerCta onClick={() => setExplorerOpen(prev => ({ ...prev, nightlife: true }))} />
          )}
          </div>
      </AccordionSection>

      {/* Shopping Section — same portal add/edit setup as the others, no
          subcategory picker (ItemAddForm only shows one for
          attraction/food/nightlife). */}
      <AccordionSection id="section-shopping" icon={React.createElement(L1_BY_KEY.shopping.icon)} title={L1_BY_KEY.shopping.label} accent="#059669" defaultOpen
        stickyHeader
      >
          <div className="space-y-4">
            <AttractionsList
              onDeleteLinkedExpense={deleteLinkedExpense}
              linkedExpenseLock={lockForLinkedExpense}
              attractions={day.shopping || []}
              onUpdate={async (list) => {
                if (!await onUpdate({ ...day, shopping: list })) return false;
                return true;
              }}
              onAddNew={() => setAddItemModal({ type: 'shopping', step: 'choose' })}
              user={currentUser}
              variant="shopping"
              isAdmin={isAdmin}
              tripId={tripConfig.id}
              tripTitle={tripConfig.title}
              allMemberIds={tripConfig.memberIds || []}
              location={day.city}
              dayDate={day.date}
            />
          <AnimatePresence initial={false}>
            {explorerOpen.shopping && (
              <ExplorerCarousel
                city={day.city}
                type="shopping"
                onAdd={(i) => addItem('shopping', i, true)}
                tripId={tripConfig.id}
                dayDate={day.date}
                userId={currentUser.id}
                existingNames={(day.shopping || []).map(s => s.name)}
                onClose={() => setExplorerOpen(prev => ({ ...prev, shopping: false }))}
              />
            )}
          </AnimatePresence>
          {!explorerOpen.shopping && (
            <ExplorerCta onClick={() => setExplorerOpen(prev => ({ ...prev, shopping: true }))} />
          )}
          </div>
      </AccordionSection>

      <AccordionSection id="section-notes" icon={<StickyNote />} title="Other" accent="#38bdf8" defaultOpen>
        <div className="space-y-4">
          <button
            onClick={() => setAddItemModal({ type: 'notes', step: 'form' })}
            className="w-full min-h-[112px] flex flex-col items-center justify-center gap-2 p-4 rounded-2xl border-2 border-dashed border-app-border/50 bg-app-faint/70 text-app-sub/60 transition active:scale-[0.98] hover:border-app-sub/40 hover:text-app-sub"
          >
            <div className="w-7 h-7 rounded-full flex items-center justify-center bg-app-sub text-white shadow-lg shadow-app-sub/20">
              <Plus className="w-3.5 h-3.5 stroke-[3]" />
            </div>
            <span className="text-[11px] font-black uppercase tracking-widest">Add to Shortlist</span>
          </button>
          <NotesList
              onDeleteLinkedExpense={deleteLinkedExpense}
              linkedExpenseLock={lockForLinkedExpense}
            notes={day.notes || []}
            currentUserId={currentUser.id}
            onReorder={(reordered) => onUpdate({ ...day, notes: reordered })}
            onView={(note) => openItemModal({ type: 'note', itemId: note.id, dayDate: day.date, mode: 'view' })}
            onEdit={(note) => openItemModal({ type: 'note', itemId: note.id, dayDate: day.date, mode: 'edit' })}
            onDelete={deleteNote}
          />
        </div>
      </AccordionSection>

      {addItemModal && createPortal(
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => { if (!savingItem) setAddItemModal(null); }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-app-bg/80 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-app-bg border border-app-border rounded-3xl p-6 max-w-xl w-full max-h-[85vh] overflow-y-auto overflow-x-hidden shadow-2xl"
          >
            <div className="flex justify-between items-center mb-6">
              <div className="font-serif text-2xl font-bold text-app-text">Add to Shortlist</div>
              <button aria-label="Close" disabled={savingItem} onClick={() => setAddItemModal(null)} className="p-2 text-app-sub/40 hover:text-gold transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>
            {addItemModal.step === 'choose' ? (
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setAddItemModal({ ...addItemModal, step: 'form' })}
                  className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-gold/30 bg-gold/5 rounded-2xl text-[11px] font-black uppercase tracking-widest text-gold transition active:scale-[0.98]"
                >
                  <Plus className="w-5 h-5" /> Add
                </button>
                <button
                  onClick={() => { setAddItemModal(null); onOpenImport?.(); }}
                  className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-gold/30 bg-gold/5 rounded-2xl text-[11px] font-black uppercase tracking-widest text-gold transition active:scale-[0.98]"
                >
                  <Download className="w-5 h-5" /> Import
                </button>
              </div>
            ) : (
              <ItemAddForm
                type={addItemModal.type === 'attractions' ? 'attraction' : addItemModal.type === 'notes' ? 'note' : addItemModal.type}
                onSavingChange={setSavingItem}
                onCancel={() => setAddItemModal(null)}
                onSave={async (i) => {
                  const saved = addItemModal.type === 'notes' ? await addNote(i) : await addItem(addItemModal.type, i);
                  if (saved) setAddItemModal(null);
                  return saved;
                }}
                country={day.country}
                tripConfig={tripConfig}
                currentUser={currentUser}
                dayDate={day.date}
              />
            )}
          </motion.div>
        </motion.div>,
        document.getElementById('import-modal-root') ?? document.body
      )}

      {viewingItem && (() => {
        const baseline = viewingItem.baseline ?? day;
        const list = viewingItem.type === 'attraction' ? baseline.attractions
          : viewingItem.type === 'eat' ? baseline.eats
          : viewingItem.type === 'nightlife' ? (baseline.nightlife || [])
          : viewingItem.type === 'shopping' ? (baseline.shopping || [])
          : (baseline.notes || []);
        const item = (list as any[]).find(i => i.id === viewingItem.itemId);
        if (!item) return null;
        const close = () => { if (!savingItem) setViewingItem(null); };
        const isNote = viewingItem.type === 'note';
        const isShopping = viewingItem.type === 'shopping';
        const canEditItem = item.addedBy === currentUser.id;
        const catLabel = isNote || isShopping ? '' : (viewingItem.type === 'eat' ? item.meal : item.category) || '';
        const addItemType: 'attraction' | 'food' | 'nightlife' | 'shopping' | 'note' = viewingItem.type === 'eat' ? 'food' : viewingItem.type === 'attraction' ? 'attraction' : viewingItem.type === 'nightlife' ? 'nightlife' : viewingItem.type === 'shopping' ? 'shopping' : 'note';

        return createPortal(
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} onClick={close} className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-app-bg/80 backdrop-blur-sm">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={(e) => e.stopPropagation()} className="bg-app-bg border border-app-border rounded-3xl max-w-xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl">
              {viewingItem.mode === 'view' && item.booked && <BookingBanner targetId="shortlist-booking-details" label={[viewingItem.type === 'eat' ? 'Reserved' : 'Booked', item.bookingDate && shortDateLabel(item.bookingDate), item.time && formatClock(item.time)].filter(Boolean).join(' · ')} />}
              <div className="p-6 overflow-y-auto overflow-x-hidden min-h-0">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-2"><By userId={item.addedBy} />{item.imported && <ImportTag />}</div>
                <div className="flex items-center gap-1">
                  {viewingItem.mode === 'view' && (canEditItem || !isNote) && (
                    <button aria-label="Edit" onClick={() => setViewingItem(v => v && { ...v, mode: 'edit', baseline: day })} className="p-2 text-app-sub/40 hover:text-gold transition-colors">
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                  <button aria-label="Close" disabled={savingItem} onClick={close} className="p-2 text-app-sub/40 hover:text-gold transition-colors"><X className="w-6 h-6" /></button>
                </div>
              </div>
              {viewingItem.mode === 'edit' && !canEditItem && !isNote ? (
                <ItemParticipationForm key={`${viewingItem.type}:${item.id}`} scopeId={tripConfig.id}
                  type={viewingItem.type as 'attraction' | 'eat' | 'nightlife' | 'shopping'} item={item} title={item.name}
                  date={day.date} userId={currentUser.id} onSavingChange={setSavingItem} onClose={() => setViewingItem(null)} />
              ) : viewingItem.mode === 'edit' && canEditItem ? (
                <ItemAddForm
                  type={addItemType}
                  onSavingChange={setSavingItem}
                  initialData={item}
                  onCancel={close}
                  onSave={async (i) => {
                    const saved = isNote ? await editNote(item.id, i, baseline)
                      : await editItem(viewingItem.type === 'eat' ? 'food' : viewingItem.type === 'attraction' ? 'attractions' : viewingItem.type === 'shopping' ? 'shopping' : 'nightlife', { ...item, ...i }, baseline);
                    if (saved) setViewingItem(null);
                    return saved;
                  }}
                  country={day.country}
                  tripConfig={tripConfig}
                  currentUser={currentUser}
                  dayDate={day.date}
                />
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-app-faint flex items-center justify-center text-xl flex-shrink-0">
                      <SubcategoryIcon name={catLabel} icon={isNote ? StickyNote : undefined} l1={viewingItem.type === 'eat' ? 'food' : viewingItem.type === 'attraction' ? 'experiences' : viewingItem.type} />
                    </div>
                    <div>
                      <div className="ds-title">{item.name}</div>
                      {catLabel && (
                        <div className="ds-label mt-0.5">
                          {normalizeSubcat(catLabel)}{viewingItem.type === 'eat' && item.cuisine ? ` · ${item.cuisine}` : ''}
                        </div>
                      )}
                    </div>
                  </div>
                  {(item.notes ?? item.desc) && <p className="text-app-sub text-sm font-normal whitespace-pre-wrap">{renderWithLinks(item.notes ?? item.desc)}</p>}
                  <div className="space-y-0">
                  {(item.address || item.mapUrl) && <GoogleMapPreview address={item.address || item.name} mapUrl={item.mapUrl} />}
                  <ShortlistDetails item={item} />
                  <AssignedTravellers ids={item.participantIds} travellers={tripConfig.travellers} />
                  {hasBookingContent(item) && <ItemDetailsSection id="shortlist-booking-details" label="Booking & payment">
                    <SavedBookingInformation item={item} />
                    <BookingCosts item={item} sourceType={viewingItem.type === 'eat' ? 'food' : viewingItem.type === 'note' ? 'other' : viewingItem.type} expenses={tripConfig.expenses} />
                  </ItemDetailsSection>}
                  </div>
                </div>
              )}
              </div>
            </motion.div>
          </motion.div>,
          document.getElementById('import-modal-root') ?? document.body
        );
      })()}
    </motion.div>
  );
}

function DestinationSearchInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  const [localVal, setLocalVal] = useState(value);
  /** Set when typed text was discarded because it was never resolved. */
  const [unresolved, setUnresolved] = useState(false);
  useEffect(() => {
    setLocalVal(value);
  }, [value]);

  // Picking a suggestion from Google's dropdown blurs this input on the way
  // there (focus briefly leaves for the pac-item click) before/around
  // onPlaceSelected firing for that same pick. Without this guard the two
  // race and both call onChange — harmless for callers that just store the
  // text, but GapDestinationCard's onChange creates a destination on every
  // call, so a real selection could commit twice.
  const justSelectedRef = useRef(false);

  const { ref } = usePlacesWidget({
    onPlaceSelected: (place) => {
      const val = formatPlaceLabel(place) || place.name || '';
      justSelectedRef.current = true;
      setUnresolved(false);
      setLocalVal(val);
      onChange(val);
    },
    options: {
      types: ['geocode'],
      fields: ['address_components', 'name', 'types']
    }
  });

  const input = (
    <input
      ref={ref as any}
      value={localVal}
      onChange={(e) => { setLocalVal(e.target.value); setUnresolved(false); }}
      onBlur={() => {
        if (justSelectedRef.current) { justSelectedRef.current = false; setUnresolved(false); return; }
        // A destination has to be a real place, picked from the list. It
        // drives the country, the currency, the weather, the generated emoji
        // and blurb, and every prompt that mentions where you are — none of
        // which can be derived from typed text.
        //
        // Committing that text is also where "Daylesford VIC" came from:
        // Google's widget writes its own description into this input while a
        // suggestion is highlighted, and blur could beat onPlaceSelected to
        // the commit. Putting it back rather than saving it closes both.
        if (localVal.trim() !== (value || '').trim()) {
          setLocalVal(value);
          setUnresolved(true);
        }
      }}
      className={`w-full bg-white border rounded-xl px-5 py-3 text-app-text font-normal focus:outline-none focus:ring-4 focus:ring-gold/5 transition-all text-sm appearance-none ${unresolved ? 'border-amber-300 focus:border-amber-400' : 'border-app-border/60 focus:border-gold/40'}`}
      placeholder={placeholder || "Search city, region..."}
    />
  );

  return unresolved ? (
    <div className="space-y-1.5">
      {input}
      {/* Says what happened to the typing rather than letting the field
          silently snap back. */}
      <p className="ds-body-sm text-amber-700/80 px-1">
        Pick a place from the list — a destination needs a real location for its
        country, currency and weather.
      </p>
    </div>
  ) : input;
}

interface MemberItemProps {
  key?: string;
  u: User;
  /** This row is a Trip Admin (or, while pending, invited as one). */
  isAdmin: boolean;
  canRemove: boolean;
  onRemove: () => void;
  /** Show the permissions cog (admins only, and only when there's someone else on the trip). */
  onManage?: () => void;
}

function MemberItem({ u, isAdmin, canRemove, onRemove, onManage }: MemberItemProps) {
  const { profiles } = useProfiles();
  const liveProf = profiles[u.id];
  const displayName = liveProf?.name || u.name;
  const pending = u.inviteStatus === 'pending';

  return (
    <div className="flex items-center justify-between p-3 bg-white border border-app-border/40 rounded-xl shadow-sm group relative">
      <div className="flex items-center gap-3 min-w-0">
        <UserAvatar user={u} size={32} />
        <div className="min-w-0">
          <div className="text-[11px] font-black uppercase tracking-tight text-app-text leading-tight truncate">
            {displayName}
          </div>
          {u.email && <div className="text-xs text-app-sub/60 leading-tight truncate w-full mb-1">{u.email}</div>}

          <TravellerRoleBadges role={isAdmin ? 'admin' : 'traveller'} pending={pending} />
        </div>
      </div>
      <div className="flex items-center shrink-0">
        {onManage && (
          <button aria-label={`Permissions for ${displayName}`} title="Permissions"
            onClick={onManage}
            className="p-2 text-app-sub/40 hover:text-gold transition-colors"
          >
            <Settings className="w-4 h-4" />
          </button>
        )}
        {canRemove && (
          <button aria-label="Remove traveller"
            onClick={onRemove}
            className="p-2 text-app-sub/40 hover:text-rose-500 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Change one traveller's permissions. The last Trip Admin stepping down has to
 * hand over to someone who has joined — the trip is never left without one.
 */
function RoleSheet({ trip, target, currentUserId, onClose, onChanged }: {
  trip: TripConfig; target: User; currentUserId: string;
  onClose: () => void; onChanged: (changes: Partial<TripConfig>) => void;
}) {
  const pending = target.inviteStatus === 'pending';
  const rowAdmin = pending ? target.pendingRole === 'admin' || isAdminInvite(trip, target.email) : tripAdminIds(trip).includes(target.id);
  const [role, setRole] = useState<TripRole>(rowAdmin ? 'admin' : 'traveller');
  const [successor, setSuccessor] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const needsSuccessor = !pending && rowAdmin && role === 'traveller' && tripAdminIds(trip).length === 1;
  const candidates = trip.travellers.filter(t =>
    t.id !== target.id && t.inviteStatus !== 'pending' && (trip.memberIds || []).includes(t.id));
  const name = target.name?.split(' ')[0] || 'this traveller';
  const unchanged = role === (rowAdmin ? 'admin' : 'traveller');

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      let changes: Partial<TripConfig>;
      if (pending && !target.email) {
        await httpsCallable(functions, 'manageTripInvitation')({
          tripId: trip.id, placeholderId: target.id, action: 'role', role,
        });
        changes = { travellers: trip.travellers.map(t => t.id === target.id ? { ...t, pendingRole: role } : t) };
      } else {
        changes = await writeTripRole(db, trip.id, currentUserId,
          pending ? { email: target.email || '' } : { id: target.id }, role, needsSuccessor ? successor : undefined);
      }
      onChanged(changes);
      onClose();
    } catch (err) {
      setError(userMessage(err, 'Could not change permissions. Nothing was changed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Permissions" onClose={onClose} size="md">
      <div className="space-y-5">
        <p className="text-sm text-app-sub">
          {target.id === currentUserId ? 'Your role on this trip.' : `${target.name}'s role on this trip.`}
          {pending && ' Takes effect when they accept the invitation.'}
        </p>
        <TripRoleSelect value={role} onChange={setRole} disabled={busy} />
        {needsSuccessor && (
          <div className="space-y-2">
            <p className="text-sm text-app-text font-medium">
              {target.id === currentUserId ? 'You are' : `${name} is`} the only Trip Admin. Who should take over?
            </p>
            {candidates.length ? (
              <div className="space-y-1.5">
                {candidates.map(t => (
                  <button key={t.id} type="button" onClick={() => setSuccessor(t.id)} aria-pressed={successor === t.id}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-xl border transition-all ${successor === t.id ? 'border-gold bg-gold/5' : 'border-app-border/50 hover:bg-app-faint'}`}>
                    <UserAvatar user={t} size={24} />
                    <span className="text-sm text-app-text">{t.name}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-app-sub">Nobody else has joined yet. Once someone accepts their invitation, you can hand over to them.</p>
            )}
          </div>
        )}
        {error && <p role="alert" className="text-sm text-rose-600">{error}</p>}
        <button onClick={save}
          disabled={busy || unchanged || (needsSuccessor && !successor)}
          className="w-full py-3.5 bg-gold text-white rounded-xl text-[11px] font-black uppercase tracking-widest shadow-lg shadow-gold/20 hover:brightness-110 active:scale-[0.98] transition disabled:opacity-30">
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

// The "Missing Dates" gap card lets a date range be adjusted before a location is
// chosen. Those dates are held as local pending state only — they're never written to
// the trip's destinations array until a location is actually picked, and that's what
// creates the entry (using these pending dates, not the gap's original bounds).
// Without this, editing the date fields alone appended a new blank-named destination
// to the trip on every commit.
function GapDestinationCard({
  gapStart,
  gapEnd,
  onCreate,
}: {
  key?: string;
  gapStart: string;
  gapEnd: string;
  onCreate: (draft: { name: string; arrivalDate: string; departureDate: string }) => void | Promise<void>;
}) {
  const [pendingStart, setPendingStart] = useState(gapStart);
  const [pendingEnd, setPendingEnd] = useState(gapEnd);

  return (
    <div className="bg-app-faint/30 border-2 border-dashed border-app-border/40 rounded-2xl p-6 relative shadow-inner space-y-4 mt-2 mb-2">
      <div className="flex gap-4">
        <div className="flex-1 space-y-1.5">
          <label className="ds-label block">Location</label>
          <DestinationSearchInput
            value=""
            onChange={(v) => {
              if (!v.trim()) return;
              onCreate({ name: v, arrivalDate: pendingStart, departureDate: pendingEnd });
            }}
            placeholder="Add destination..."
          />
        </div>
      </div>
      <div className="space-y-4">
        <DateRangePicker
          label="Dates *"
          startLabel="Arriving"
          endLabel="Departing"
          startDate={pendingStart}
          endDate={pendingEnd}
          onChange={(start, end) => { setPendingStart(start); setPendingEnd(end); }}
          stacked
        />
      </div>
    </div>
  );
}

type WizardStep = 'dates' | 'destinations' | 'travellers';

export function TripManagementForm({ config, onSave, onPersist, onCancel, onChange, mode = 'full', editingDestIndex, newDestinationDates, currentUser, onOpenImport, onWizardStepChange, onSavingChange, branchSetup }: { key?: string; config: TripConfig; onSave: (c: TripConfig) => Promise<boolean>;
  /** Reuse the wizard for a branch draft; no invitations, enrichment or remote writes. */
  branchSetup?: React.ReactNode;
  /** Write the trip without the side effects `onSave` carries — every caller
   *  wraps that one to close the form on success, which is wrong in the
   *  middle of an action that has more to do afterwards. */
  onPersist?: (c: TripConfig) => Promise<boolean>;
  onCancel: () => void; onChange?: (c: TripConfig) => void; mode?: 'full' | 'info' | 'destinations' | 'destination' | 'members' | 'wizard'; editingDestIndex?: number; newDestinationDates?: { arrivalDate: string; departureDate: string }; currentUser?: User; onOpenImport?: (files: File[]) => void; onWizardStepChange?: (step: WizardStep) => void; onSavingChange?: (saving: boolean) => void }) {
  const originalConfig = useRef(config).current;
  // Editing a not-yet-created destination (from an "Add destinations" gap CTA):
  // seed one blank destination pre-filled with the gap's dates so the single
  // destination edit panel has something to point at, same as editing an
  // existing one.
  const [data, setData] = useState<TripConfig>(() => {
    if (mode === 'destination' && editingDestIndex === undefined && newDestinationDates) {
      return {
        ...config,
        destinations: [...config.destinations, { name: '', ...newDestinationDates }],
      };
    }
    return { ...config };
  });
  // Identity for a destination, independent of array position — used below
  // (and by the delete-confirmation flow further down) since updateData()
  // re-sorts destinations by arrival date on every edit.
  const destKey = (d: { arrivalDate?: string; name: string }) => `${d.arrivalDate || ''}::${d.name}`;
  // For the "add a new destination" case, find the seeded entry by identity
  // rather than trusting a fixed config.destinations.length: updateData()
  // sorts by arrival date on every edit, so a gap that isn't chronologically
  // last gets moved to an earlier index the moment its name or dates
  // change — a fixed index would then point at whatever destination
  // happened to land there instead, going blank/wrong the instant you
  // interact with the field. The seeded entry is the one whose key doesn't
  // match any entry from the original (unsorted, unmutated) config prop.
  const effectiveEditIndex = editingDestIndex ?? (() => {
    if (!(mode === 'destination' && newDestinationDates)) return undefined;
    const originalKeys = new Set(originalConfig.destinations.map(destKey));
    const idx = data.destinations.findIndex(d => !originalKeys.has(destKey(d)));
    return idx === -1 ? undefined : idx;
  })();
  const dataRef = useRef(data);
  useEffect(() => { dataRef.current = data; }, [data]);
  const [wizardStep, setWizardStep] = useState<WizardStep>('dates');
  useEffect(() => {
    if (mode === 'wizard') onWizardStepChange?.(wizardStep);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardStep, mode]);
  const [overlaps, setOverlaps] = useState<number[]>([]);
  // Destinations whose dates fall outside the trip's own start/end dates.
  // Trip dates are the source of truth — editing a destination is never
  // allowed to silently widen them; the user has to change trip dates first.
  const [outsideTripRange, setOutsideTripRange] = useState<number[]>([]);
  const importFileRef = useRef<HTMLInputElement>(null);

  const validateDates = (newData: TripConfig) => {
    const overlappingIndices: number[] = [];
    const outsideIndices: number[] = [];
    const tripStart = newData.startDate ? new Date(newData.startDate).getTime() : null;
    const tripEnd = newData.endDate ? new Date(newData.endDate).getTime() : null;
    const dests = [...newData.destinations].sort((a, b) => {
      const aStart = new Date(a.arrivalDate || '1970-01-01').getTime();
      const bStart = new Date(b.arrivalDate || '1970-01-01').getTime();
      return aStart - bStart;
    });

    for (let i = 0; i < newData.destinations.length; i++) {
        const current = newData.destinations[i];
        if (!current.arrivalDate || !current.departureDate) continue;

        const currentStart = new Date(current.arrivalDate).getTime();
        const currentEnd = new Date(current.departureDate).getTime();

        // Basic check: departure before arrival
        if (currentEnd < currentStart) {
            overlappingIndices.push(i);
            continue;
        }

        // Outside the trip's own dates?
        if (tripStart !== null && tripEnd !== null && !isNaN(tripStart) && !isNaN(tripEnd)) {
          if (currentStart < tripStart || currentEnd > tripEnd) {
            outsideIndices.push(i);
          }
        }

        // Overlap with others
        for (let j = 0; j < newData.destinations.length; j++) {
            if (i === j) continue;
            const other = newData.destinations[j];
            if (!other.arrivalDate || !other.departureDate) continue;

            const otherStart = new Date(other.arrivalDate).getTime();
            const otherEnd = new Date(other.departureDate).getTime();

            // "fine for the end date of a destination to be the same as the start date of a proceeding destination, but date ranges should not overlap further"
            // Overlap happens if currentStart < otherEnd AND currentEnd > otherStart
            // BUT we allow currentStart === otherEnd OR currentEnd === otherStart

            if (currentStart < otherEnd && currentEnd > otherStart) {
                // If they just touch at boundaries, it's okay
                if (currentStart === otherEnd || currentEnd === otherStart) {
                    continue;
                }
                if (!overlappingIndices.includes(i)) overlappingIndices.push(i);
                break;
            }
        }
    }
    setOverlaps(overlappingIndices);
    setOutsideTripRange(outsideIndices);
    return overlappingIndices.length === 0;
  };

  const updateData = (newData: TripConfig) => {
    newData.destinations.sort((a, b) => {
      const aStart = new Date(a.arrivalDate || '1970-01-01').getTime();
      const bStart = new Date(b.arrivalDate || '1970-01-01').getTime();
      return aStart - bStart;
    });
    setData(newData);
    validateDates(newData);
    onChange?.(newData);
  };

  const [newTraveller, setNewTraveller] = useState({ name: '', email: '' });
  const [newTravellerRole, setNewTravellerRole] = useState<TripRole>('traveller');
  const [newTravellerDates, setNewTravellerDates] = useState<TripParticipation | null>(null);
  const [inviteDatesError, setInviteDatesError] = useState('');
  const [showMyTripDates, setShowMyTripDates] = useState(false);
  const [roleTarget, setRoleTarget] = useState<User | null>(null);
  const iAmAdmin = isTripAdmin(data, currentUser?.id);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  useEffect(() => {
    onSavingChange?.(isSaving);
    return () => onSavingChange?.(false);
  }, [isSaving, onSavingChange]);
  const [confirmingRemovalId, setConfirmingRemovalId] = useState<string | null>(null);
  const [showAddTravellerModal, setShowAddTravellerModal] = useState(false);
  /**
   * Travellers added in the creation wizard, where there is no trip document
   * to invite them into yet. They go straight into `data` so the step shows
   * them — with their real name and picture when they already have an account
   * — and their invitations are sent once the wizard finishes and the trip
   * actually exists. Nothing is written before then, so abandoning the wizard
   * leaves no half-created trip and no invitations to a trip that never was.
   */
  const [queuedInvites, setQueuedInvites] = useState<{
    email: string; name: string; role: TripRole; placeholderId: string; userId?: string; participation: TripParticipation | null;
  }[]>([]);

  const [finishingWizard, setFinishingWizard] = useState(false);
  const handleSave = async () => {
    if (isSaving) return;
    setFinishingWizard(mode === 'wizard');
    const finalData = data;

    setSaveError(null);
    setIsSaving(true);
    try {
      if (branchSetup) {
        await onSave(finalData);
        return;
      }
      // ── Regenerate emoji/blurb only for destinations that changed or are new ──
      // A destination needs regeneration if:
      //   • its name is not in the original config (newly added or renamed)
      //   • or it is still missing a blurb (e.g. old trip / import without blurb)
      // All other destinations keep their existing emoji/blurb untouched.
      const originalNames = new Set(originalConfig.destinations.map(d => d.name));
      const regenIdxs: number[] = [];
      finalData.destinations.forEach((dest, idx) => {
        const isNew    = !originalNames.has(dest.name);
        const noBlurb  = !dest.blurb;
        const noEmoji  = !dest.emoji || dest.emoji === '📍';
        if (isNew || noBlurb || noEmoji) regenIdxs.push(idx);
      });

      let updatedDests = [...finalData.destinations];
      if (regenIdxs.length > 0) {
        const toRegen = regenIdxs.map(idx => ({
          city: updatedDests[idx].name,
          country: updatedDests[idx].country || null,
        }));
        try {
          const details = await generateDestinationDetails(toRegen);
          regenIdxs.forEach((destIdx, i) => {
            const d = details[i];
            if (d) {
              updatedDests[destIdx] = {
                ...updatedDests[destIdx],
                emoji: d.emoji || updatedDests[destIdx].emoji,
                blurb: d.blurb || updatedDests[destIdx].blurb,
              };
            }
          });
        } catch (error) {
          // Optional descriptions must not prevent saving the user's trip.
          console.warn('Destination descriptions unavailable:', error);
        }
      }

      const finalConfig = { ...finalData, destinations: updatedDests };

      // A new trip must be saved alone. Pending addresses and placeholders are
      // added by the server only after its document exists.
      const toSave = queuedInvites.length ? {
        ...finalConfig,
        travellers: finalConfig.travellers.filter(t => !queuedInvites.some(i => i.placeholderId === t.id)),
        inviteEmails: [], adminInviteEmails: [],
      } : finalConfig;
      // Day display metadata is derived by getExpandedDays, not persisted.
      if (!await onSave(toSave)) {
        setSaveError('Could not save your trip. Your changes are still here; please try again.');
        return;
      }

      // Invitations queued during the creation wizard. The trip exists as of
      // the save above. Create each server-owned invitation before delivery.
      if (queuedInvites.length > 0) {
        const failed: string[] = [];
        for (const invite of queuedInvites) {
          try {
            await httpsCallable(functions, 'createTripInvitation')({
              tripId: finalConfig.id, email: invite.email, name: invite.name, role: invite.role,
              participation: invite.participation,
            });
            if (!(await sendInvite(finalConfig.id, finalConfig.title, finalConfig.ownerId, invite))) failed.push(invite.email);
          } catch (err) {
            console.error('Queued invite failed:', { invite, err });
            failed.push(invite.email);
          }
        }
        setQueuedInvites([]);
        if (failed.length > 0) {
          alert(
            `Your trip is saved, but ${failed.length === 1 ? 'this invitation' : 'these invitations'} could not be completed — ` +
            `${failed.join(', ')}. Check the traveller list and retry any missing invitation.`,
          );
        }
      }

      // Notifications — only fire if we have a user to attribute them to
      if (currentUser) {
        const allMembers = finalConfig.memberIds || [];
        const senderName = getFirstName(currentUser.name);

        // Trip renamed?
        if (finalConfig.title !== originalConfig.title) {
          notifyTripRenamed(
            config.id, finalConfig.title,
            currentUser.id, senderName,
            allMembers, finalConfig.title
          ).catch(console.error);
        }

        // Destination added?
        const origDestNames = new Set(originalConfig.destinations.map(d => d.name));
        finalConfig.destinations.forEach(d => {
          if (!origDestNames.has(d.name)) {
            notifyDestinationAdded(
              config.id, finalConfig.title,
              currentUser.id, senderName,
              allMembers, d.name, d.emoji || '📍'
            ).catch(console.error);
          }
        });

        // Trip dates changed?
        if (finalConfig.startDate !== originalConfig.startDate || finalConfig.endDate !== originalConfig.endDate) {
          const fmt = (d: string) => calendarDate(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
          notifyTripDatesChanged(
            config.id, finalConfig.title,
            currentUser.id, senderName,
            allMembers,
            `${fmt(finalConfig.startDate)} – ${fmt(finalConfig.endDate)}`,
          ).catch(console.error);
        }
      }
    } catch (error) {
      console.error("Failed to save trip config:", error);
      setSaveError(userMessage(error, 'Could not save your trip. Please try again.'));
    } finally {
      setFinishingWizard(false);
      setIsSaving(false);
    }
  };

  // Stable key (arrivalDate::name) so sorting reorders don't point at wrong
  // dest — destKey itself is declared above, alongside effectiveEditIndex,
  // since that needs it too.
  const [confirmingDeleteDest, setConfirmingDeleteDest] = useState<string | null>(null);

  const deleteDestinationWithData = async (key: string) => {
    if (isSaving) return;
    const idx = data.destinations.findIndex(d => destKey(d) === key);
    if (idx === -1) { setConfirmingDeleteDest(null); return; }
    const dest = data.destinations[idx];
    setSaveError(null);
    if (branchSetup) {
      updateData({ ...data, destinations: data.destinations.filter((_, i) => i !== idx) });
      setConfirmingDeleteDest(null);
      return;
    }
    const identity = (d: Destination) => JSON.stringify([d.name, d.arrivalDate, d.departureDate]);
    const saved = originalConfig.destinations.find(d => identity(d) === identity(dest));
    const onlyAdded = originalConfig.destinations.every(d => data.destinations.some(next => identity(d) === identity(next)));
    if (!saved && onlyAdded) {
      updateData({ ...data, destinations: data.destinations.filter((_, i) => i !== idx) });
      setConfirmingDeleteDest(null);
      if (mode === 'destination') onCancel();
      return;
    }
    // Never silently commit or discard other edits as part of a destructive action.
    if (!saved || JSON.stringify(data) !== JSON.stringify(originalConfig)) {
      setSaveError('Save your current edits, then reopen this destination to delete it.');
      return;
    }
    if (!currentUser) { setSaveError('Sign in before deleting a destination.'); return; }
    setIsSaving(true);
    try {
      const result = await saveDestinationRemoval(config.id, originalConfig, dest, currentUser.id);
      updateData(result);
      setConfirmingDeleteDest(null);
      onCancel();
      notifyDestinationRemoved(
        config.id, result.title, currentUser.id, getFirstName(currentUser.name),
        result.memberIds || [], dest.name, dest.emoji || '📍'
      ).catch(console.error);
    } catch (err) {
      console.error('Failed to delete destination data:', err);
      setSaveError(userMessage(err, 'Could not delete this destination. Nothing was deleted; please try again.'));
    } finally {
      setIsSaving(false);
    }
  };

  /** Notification + email for one invitee. Used immediately on an existing
   *  trip, and after the fact for everyone queued during the wizard. */
  const sendInvite = async (tripId: string, tripTitle: string, ownerId: string, invite: { email: string; userId?: string }) => {
    const creatorFirstName = data.travellers.find(t => t.id === ownerId)?.name?.split(' ')[0] || 'A traveller';
    if (invite.userId) {
      await createTripNotifications({
        tripId,
        recipientIds: [invite.userId],
        title: 'Trip Invitation',
        message: `**${creatorFirstName}** invited you to join **${tripTitle}**`,
        type: 'invite',
        category: 'trip-admin',
        priority: 'P1',
        actionRequired: true,
        data: {
          tripTitle,
          creatorId: ownerId,
          senderName: data.travellers.find(t => t.id === ownerId)?.name || 'Someone',
        },
      });
    }
    const { sendInviteEmail } = await import('../services/mailService');
    return sendInviteEmail(tripId, invite.email);
  };

  const addTraveller = async () => {
    if (!newTraveller.name || !newTraveller.email) return;
    const invalidDates = participationError(data.destinations, newTravellerDates);
    if (invalidDates) { setInviteDatesError(invalidDates); return; }

    // limit to 20 travellers
    if (data.travellers.length >= 20) {
      alert("Maximum limit of 20 travellers (including you) reached for this trip.");
      return;
    }
    
    setIsSaving(true);
    // Which step we're on, so a failure can name itself. Three separate
    // hypotheses about "Missing or insufficient permissions" here were wrong,
    // each costing a round trip to a real device — the error needs to say
    // what it was rather than leave us inferring it from the rules.
    let step = 'looking up the traveller';
    /** Whether we had to write the trip itself before we could add anyone. */
    let persisted = false;
    try {
      // 1. Check if user exists in our DB — through the lookup callable, which
      //    returns only the public profile (see services/userLookup.ts).
      const existingAccount = await findTravellerByEmail(newTraveller.email);

      // 2. Only public display fields enter the trip; the address and pending
      // role live in the server-owned invitation record until acceptance.
      const foundUser = existingAccount;
      if (foundUser && data.travellers.some(t => t.id === foundUser.id || t.email === foundUser.email)) {
        alert("This traveller is already part of the trip.");
        return;
      }
      const role: TripRole = newTravellerRole === 'admin' && iAmAdmin ? 'admin' : 'traveller';
      const invitee: User = {
        id: foundUser?.id || `invite-${crypto.randomUUID()}`,
        name: foundUser?.name || newTraveller.name,
        email: '',
        initials: foundUser?.initials || newTraveller.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2),
        color: foundUser?.color || '#C5A059',
        emoji: foundUser?.emoji || '👤',
        imgSrc: foundUser?.imgSrc ?? null,
        inviteStatus: 'pending', pendingRole: role,
      };
      let newData: TripConfig = { ...data, travellers: [...data.travellers, invitee] };

      // A wizard draft stays local; the trip is saved before invites are sent.
      if (mode === 'wizard') {
        setQueuedInvites(prev => [...prev, {
          email: newTraveller.email.toLowerCase(), name: newTraveller.name,
          role, placeholderId: invitee.id, userId: foundUser?.id,
          participation: newTravellerDates,
        }]);
        setData(newData);
        onChange?.(newData);
        setNewTraveller({ name: '', email: '' }); setNewTravellerRole('traveller');
        setShowAddTravellerModal(false);
        setNewTravellerDates(null);
        return;
      }

      step = 'adding the traveller';
      const inviteWrite = () => httpsCallable<
        { tripId: string; email: string; name: string; role: TripRole; participation: TripParticipation | null },
        { placeholderId: string; created: boolean }
      >(functions, 'createTripInvitation')({
        tripId: data.id, email: newTraveller.email, name: newTraveller.name, role,
        participation: newTravellerDates,
      });
      let result;
      try {
        result = (await inviteWrite()).data;
      } catch (err) {
        // A trip created locally but not yet persisted can be saved once here.
        if ((err as { code?: string })?.code !== 'functions/not-found') throw err;
        step = 'saving the trip';
        if (!onPersist || !(await onPersist(data))) throw err;
        persisted = true;
        step = 'adding the traveller';
        result = (await inviteWrite()).data;
      }
      if (!result.created) { alert('This traveller is already invited.'); return; }
      newData = { ...newData, travellers: [...data.travellers, { ...invitee, id: result.placeholderId }] };

      // 4. Notification (only after the trip commit, so the Accept flow can
      //    actually load the trip) and the email invite.
      step = 'sending the invitation';
      setData(newData);
      onChange?.(newData);
      const emailOk = await sendInvite(data.id, data.title, currentUser?.id || config.ownerId, {
        email: newTraveller.email.toLowerCase(),
        userId: foundUser?.id,
      });
      if (!emailOk) {
        alert(`Couldn't queue the invite email for ${newTraveller.email}. They've been added as Pending; you can resend later.`);
      }

      setNewTraveller({ name: '', email: '' }); setNewTravellerRole('traveller');
      setShowAddTravellerModal(false);
      setNewTravellerDates(null);
    } catch (err) {
      // Everything we'd otherwise have to guess at: which step, the Firestore
      // or callable error code, and — for a permission failure — how this
      // account actually stands against the trip it's writing to, since that
      // is what the rules decide on.
      const code = (err as { code?: string })?.code;
      // Everything the rules actually decide on, read from the server rather
      // than from the draft: the signed-in uid, and how the STORED document
      // describes it. A draft happily claims owner/admin/member about a
      // document that says nothing of the kind, which is what sent three
      // rounds of this chasing the wrong thing.
      const authUid = auth.currentUser?.uid || '(not signed in)';
      const idNote = currentUser && authUid !== currentUser.id ? ' · your account id does not match your sign-in' : '';
      const detail = code === 'permission-denied' || code === 'functions/permission-denied'
        ? ` · the trip was ${persisted ? 'saved first and still refused' : 'not saved first'}${idNote}`
        : '';
      console.error('Failed to invite traveller:', {
        step, code, err, tripId: data.id, mode, persisted, authUid, currentUserId: currentUser?.id,
        draftOwnerId: data.ownerId, draftMemberIds: data.memberIds,
      });
      alert(
        `Couldn't invite traveller while ${step}` +
        `${code ? ` (${code})` : ''}${detail}.\n\n` +
        `${err instanceof Error ? err.message : 'Unknown error'}`
      );
    } finally {
      setIsSaving(false);
    }
  };

  const removeTraveller = async (id: string): Promise<boolean> => {
    if (isSaving) return false;
    // Only a Trip Admin may remove someone, and never another Trip Admin:
    // they're switched to Traveller first (the cog), which is where handing
    // over the last admin is handled. firestore.rules enforces the rest.
    if (!iAmAdmin) return false;
    const traveller = data.travellers.find(t => t.id === id);
    if (!traveller || id === currentUser?.id) return false;
    if (tripAdminIds(data).includes(id)) return false;

    // Removing access does not erase the group's historical contributions or
    // debts. Those live in subcollections, not in the trip configuration.
    const newData: TripConfig = {
      ...data,
      memberIds: (data.memberIds || []).filter(mid => mid !== id),
      adminIds: (data.adminIds || []).filter(adminId => adminId !== id),
      inviteEmails: (data.inviteEmails || []).filter(email => email.toLowerCase() !== traveller.email?.toLowerCase()),
      ...(data.adminInviteEmails ? { adminInviteEmails: data.adminInviteEmails.filter(email => email !== traveller.email?.toLowerCase()) } : {}),
      travellers: data.travellers.filter(t => t.id !== id),
    };

    if (traveller.inviteStatus === 'pending' && !traveller.email) {
      setIsSaving(true);
      setSaveError(null);
      try {
        const result = await httpsCallable<
          { tripId: string; placeholderId: string; action: 'revoke' }, { changed: boolean }
        >(functions, 'manageTripInvitation')({ tripId: data.id, placeholderId: id, action: 'revoke' });
        if (!result.data.changed) {
          setSaveError('This invitation is no longer active. Reopen the trip to see the latest members.');
          return false;
        }
        updateData(newData);
        try { await httpsCallable(functions, 'revokeTripInvite')({ tripId: data.id, userId: id }); }
        catch (err) { console.warn('Could not withdraw the pending invite notification:', err); }
        return true;
      } catch (err) {
        setSaveError(userMessage(err, 'Could not remove this invitation. Please try again.'));
        return false;
      } finally {
        setIsSaving(false);
      }
    }

    setIsSaving(true);
    setSaveError(null);
    try {
      if (!await onSave(newData)) {
        setSaveError('Could not remove this traveller. Please try again.');
        return false;
      }
      // Withdraw any invitation they haven't answered yet. Without this the
      // notification outlives the invite and its Accept button fails with a
      // permission error, which reads as a broken button rather than a
      // withdrawn invitation. Best-effort: the removal itself has committed,
      // and the notification centre also clears invites it finds stale.
      try {
        await httpsCallable(functions, 'revokeTripInvite')({ tripId: data.id, userId: id });
      } catch (err) {
        console.warn('Could not withdraw the pending invite notification:', err);
      }
      updateData(newData);
      setConfirmingRemovalId(null);
      return true;
    } catch (error) {
      setSaveError(userMessage(error, 'Could not remove this traveller. Please try again.'));
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <fieldset disabled={isSaving} className="space-y-6 animate-fade-up min-w-0">
      {finishingWizard && <WizardLoadingScreen title="Saving your trip…" />}
      {saveError && <p role="alert" className="text-sm text-rose-600">{saveError}</p>}

      {/* ── Import existing plan CTA ── */}
      {onOpenImport && !(mode === 'wizard' && wizardStep === 'travellers') && (
        <>
          <input
            ref={importFileRef}
            type="file"
            multiple
            accept={ITINERARY_FILE_ACCEPT}
            className="hidden"
            onChange={e => {
              const picked = Array.from(e.target.files ?? []) as File[];
              e.target.value = '';
              if (picked.length > 0) onOpenImport(picked);
            }}
          />
          <button
            type="button"
            onClick={() => importFileRef.current?.click()}
            className="w-full p-4 bg-gold/8 border border-gold/25 rounded-2xl text-left transition hover:bg-gold/12 active:scale-[0.99] flex items-center gap-3"
          >
            <div className="w-9 h-9 rounded-xl bg-gold/15 flex items-center justify-center shrink-0">
              <Download className="w-4 h-4 text-gold" />
            </div>
            <div>
              <p className="text-sm font-black text-gold mb-0.5">Already started planning?</p>
              <p className="text-xs text-app-sub leading-relaxed">
                Upload your existing itinerary or notes and we'll extract the details.
              </p>
            </div>
          </button>
        </>
      )}

      {/* ── Destination date overlap warning ── */}
      {overlaps.length > 0 && (
        <div role="alert" className={`${ERROR_BOX} flex items-center gap-3`}>
           <Info className="w-4 h-4 shrink-0" />
           <span>Destination dates overlap. Please resolve highlighted ranges.</span>
        </div>
      )}

      {/* ── Destination outside trip dates warning ── */}
      {outsideTripRange.length > 0 && (
        <div role="alert" className={`${ERROR_BOX} flex items-center gap-3`}>
           <Info className="w-4 h-4 shrink-0" />
           <span>These dates are outside your trip dates. Update your trip dates first.</span>
        </div>
      )}

      {(mode === 'full' || mode === 'info' || (mode === 'wizard' && wizardStep === 'dates')) && (
        <div className="grid grid-cols-1 gap-4">
          {/* 25-char cap — keeps titles fitting on one line at the reduced
              size Home's feature card now renders them at. */}
          <FormInput label={branchSetup ? 'Branch title *' : 'Trip Title *'} value={data.title} onChange={v => updateData({ ...data, title: v })} maxLength={25} />
          <DateRangePicker 
            label={branchSetup ? 'Branch dates *' : 'Trip Dates *'}
            startDate={data.startDate} 
            endDate={data.endDate} 
            onChange={(start, end) => updateData({ ...data, startDate: start, endDate: end })} 
          />
        </div>
      )}

      {(() => {
        const renderDestCard = (dest: typeof data.destinations[number], idx: number) => (
          <div key={destKey(dest)} id={`dest-card-${idx}`} className={`bg-white p-6 rounded-2xl border shadow-sm relative group space-y-4 transition-colors ${
            overlaps.includes(idx) || outsideTripRange.includes(idx) ? 'border-rose-300' : 'border-app-border/40'
          }`}>

            {confirmingDeleteDest === destKey(dest) ? (() => {
              return (
                <div className="flex flex-col items-center text-center gap-4 py-2">
                  <div className="w-10 h-10 bg-rose-50 rounded-full flex items-center justify-center">
                    <Trash2 className="w-4 h-4 text-rose-500" />
                  </div>
                  <div>
                    <p className="font-serif text-2xl font-bold text-app-text">Delete {dest.name || 'this destination'}?</p>
                    <p className="text-xs text-app-sub mt-1">
                      This removes its Shortlist, schedule and associated bookings. Travel arriving at another destination and that destination’s bookings stay. Expenses stay. This cannot be undone.
                    </p>
                  </div>
                  <div className="flex gap-2 w-full">
                    <button
                      onClick={() => setConfirmingDeleteDest(null)}
                      className="flex-1 py-2.5 rounded-xl border border-app-border/60 text-[11px] font-black uppercase tracking-widest text-app-sub hover:bg-app-faint transition-colors"
                    >
                      Keep it
                    </button>
                    <button
                      onClick={() => confirmingDeleteDest && deleteDestinationWithData(confirmingDeleteDest)}
                      disabled={isSaving}
                      className="flex-1 py-2.5 rounded-xl bg-rose-500 text-white text-[11px] font-black uppercase tracking-widest hover:brightness-110 transition disabled:opacity-50"
                    >
                      {isSaving ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                </div>
              );
            })() : (
            <>
            <button aria-label="Delete destination"
              onClick={() => setConfirmingDeleteDest(destKey(dest))}
              className="absolute top-4 right-4 p-2 text-app-sub/40 hover:text-rose-500 transition-colors z-10"
              title="Delete destination"
            >
              <Trash2 className="w-4 h-4" />
            </button>

            <div className="space-y-4 pt-1">
              <div className="space-y-1.5">
                <label className="ds-label block">Location *</label>
                <DestinationSearchInput
                  value={dest.name}
                  onChange={async (v) => {
                      if (!v.trim()) return;
                      const snap = dataRef.current;
                      const currentDest = snap.destinations[idx];
                      const nameChanged = v !== currentDest?.name;
                      const missingInfo = !currentDest?.emoji || currentDest.emoji === '📍' || !currentDest?.blurb;

                      // Nothing to do if name is unchanged and info is already complete
                      if (!nameChanged && !missingInfo) return;

                      // Update name immediately if it changed
                      if (nameChanged) {
                        const newList = snap.destinations.map((d, i) =>
                          i === idx ? { ...d, name: v } : d
                        );
                        updateData({ ...snap, destinations: newList });
                      }
                      if (branchSetup) return;

                      // Always fetch fresh emoji/blurb when name changes or info is incomplete
                      try {
                        const info = await getDestinationInfo(v);
                        const fresh = dataRef.current;
                        const updatedList = fresh.destinations.map(d =>
                          d.name === v
                            ? { ...d, emoji: info.emoji, country: info.country, blurb: info.blurb }
                            : d
                        );
                        updateData({ ...fresh, destinations: updatedList });
                      } catch (e) {
                        console.error("Failed to suggest destination info:", e);
                      }
                  }}
                />
              </div>

              <DateRangePicker
                label=""
                hideGroupLabel
                stacked
                startLabel="Arriving *"
                endLabel="Departing *"
                startDate={dest.arrivalDate || ""}
                endDate={dest.departureDate || ""}
                onChange={(start, end) => {
                  const newList = [...data.destinations];
                  newList[idx] = { ...newList[idx], arrivalDate: start, departureDate: end };
                  updateData({ ...data, destinations: newList });
                }}
              />
            </div>
            </>
            )}
          </div>
        );

        // A dashed "Missing Dates" prompt for an uncovered stretch of the trip.
        // This is the ONLY way to add a destination — there's no separate "Add
        // destination" button, since a gap card already exists everywhere one
        // could be added (including covering the whole trip when there are no
        // destinations yet at all). updateData() re-sorts by arrival date, so
        // the new entry doesn't need to be spliced into a specific position.
        const renderGapCard = (key: string, gapStart: string, gapEnd: string) => (
          <GapDestinationCard
            key={key}
            gapStart={gapStart}
            gapEnd={gapEnd}
            onCreate={async (draft) => {
              const snap = dataRef.current;
              const newList = [...snap.destinations, draft];
              updateData({ ...snap, destinations: newList });
              if (branchSetup) return;
              try {
                const info = await getDestinationInfo(draft.name);
                const fresh = dataRef.current;
                const updatedList = fresh.destinations.map(d =>
                  d.name === draft.name ? { ...d, emoji: info.emoji, country: info.country, blurb: info.blurb } : d
                );
                updateData({ ...fresh, destinations: updatedList });
              } catch (e) {
                console.error("Failed to suggest destination info:", e);
              }
            }}
          />
        );

        if (mode === 'destination') {
          return effectiveEditIndex !== undefined && data.destinations[effectiveEditIndex] ? (
            <div className="space-y-4">
              <SLabel>Destination</SLabel>
              {renderDestCard(data.destinations[effectiveEditIndex], effectiveEditIndex)}
            </div>
          ) : null;
        }

        if (mode !== 'full' && mode !== 'destinations' && !(mode === 'wizard' && wizardStep === 'destinations')) return null;

        return (
        <div className="space-y-4">
          <SLabel>Destinations</SLabel>
          <div className="space-y-4">
            {data.destinations.length === 0 ? (
              data.startDate && data.endDate
                ? renderGapCard('gap-empty', data.startDate, data.endDate)
                : null
            ) : (
              data.destinations.reduce((acc, dest, idx, arr) => {
                if (idx === 0 && data.startDate && dest.arrivalDate) {
                   const tStart = data.startDate.slice(0, 10);
                   const d2 = dest.arrivalDate.slice(0, 10);

                   // A destination's departure day and the next one's arrival day can be
                   // the same calendar day (you travel and arrive same-day) — that's not a
                   // gap, it's a touching handoff (see validateDates's overlap check, which
                   // already allows this). So a new destination slotted in before this one
                   // should default to departing the SAME day this one arrives, not the day
                   // before.
                   if (tStart < d2) {
                     acc.push(renderGapCard('gap-start', tStart, dest.arrivalDate));
                   }
                }

                acc.push(renderDestCard(dest, idx));

                const nextDest = arr[idx + 1];
                if (nextDest && dest.departureDate && nextDest.arrivalDate) {
                   // Calendar dates as strings (addDays is UTC-only): the local
                   // setDate it replaced moved a day across a DST change.
                   const d1 = addDays(dest.departureDate, 1);
                   const d2Prev = addDays(nextDest.arrivalDate, -1);

                   if (d1 <= d2Prev) {
                      acc.push(renderGapCard(`gap-${idx}`, d1, d2Prev));
                   }
                }

                if (idx === arr.length - 1 && dest.departureDate && data.endDate) {
                   const d1 = new Date(dest.departureDate);
                   const tEnd = new Date(data.endDate);

                   // Same touching-handoff logic as the gap-start case above: a new
                   // destination added after this one should default to arriving the
                   // SAME day this one departs, not the day after.
                   if (d1 < tEnd) {
                      acc.push(renderGapCard(`gap-end-${idx}`, dest.departureDate, data.endDate));
                   }
                }

                return acc;
              }, [] as React.ReactNode[])
            )}
          </div>
        </div>
        );
      })()}

      {mode === 'wizard' && wizardStep === 'travellers' && branchSetup}
      {!branchSetup && (mode === 'full' || mode === 'members' || (mode === 'wizard' && wizardStep === 'travellers')) && (
        <div className="space-y-3">
          <SLabel>Trip Members</SLabel>
          {mode !== 'wizard' && currentUser && data.memberIds?.includes(currentUser.id) && <button
            type="button" onClick={() => setShowMyTripDates(true)} className="min-h-11 text-sm text-gold">My trip dates</button>}
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-2">
            {(() => {
              const rowIsAdmin = (u: User) => u.inviteStatus === 'pending'
                ? u.pendingRole === 'admin' || isAdminInvite(data, u.email) : tripAdminIds(data).includes(u.id);
              // Role changes write straight to the stored trip, so not in the
              // creation wizard (no trip yet — the role is picked when adding).
              const canManage = iAmAdmin && mode !== 'wizard' && data.travellers.length > 1;
              return [...data.travellers]
                .sort((a, b) => Number(rowIsAdmin(b)) - Number(rowIsAdmin(a)))
                .map((u) => (
                  <MemberItem
                    key={u.id}
                    u={u}
                    isAdmin={rowIsAdmin(u)}
                    canRemove={iAmAdmin && u.id !== currentUser?.id && !rowIsAdmin(u)}
                    onRemove={() => setConfirmingRemovalId(u.id)}
                    onManage={canManage ? () => setRoleTarget(u) : undefined}
                  />
                ));
            })()}
            <button
              type="button"
              onClick={() => setShowAddTravellerModal(true)}
              className="flex flex-col items-center justify-center gap-2 min-h-[84px] p-3 bg-gold/5 border-2 border-dashed border-gold/30 rounded-xl text-center hover:bg-gold/10 active:scale-[0.99] transition cursor-pointer"
            >
              <div className="w-8 h-8 rounded-full bg-gold text-white flex items-center justify-center shrink-0 shadow-sm shadow-gold/20">
                <UserPlus className="w-4 h-4" />
              </div>
              <div className="text-[11px] font-black uppercase tracking-widest text-gold leading-tight">Add Another Traveller</div>
            </button>
          </div>
        </div>
      </div>
      )}

      {mode !== 'members' && mode !== 'wizard' && (
        <div className="flex gap-3 pt-4 border-t border-app-border/10">
          <button onClick={onCancel} className="flex-1 p-3.5 rounded-xl border border-app-border text-[11px] font-black uppercase tracking-widest text-app-sub hover:bg-app-faint transition">Cancel</button>
          <button
            onClick={() => handleSave()}
            disabled={
              !data.title || !data.startDate || !data.endDate || isSaving || overlaps.length > 0 || outsideTripRange.length > 0 ||
              (mode === 'destination' && (
                effectiveEditIndex === undefined ||
                !data.destinations[effectiveEditIndex]?.name?.trim() ||
                !data.destinations[effectiveEditIndex]?.arrivalDate ||
                !data.destinations[effectiveEditIndex]?.departureDate
              ))
            }
            className="flex-1 p-3.5 rounded-xl bg-gold text-white text-[11px] font-black uppercase tracking-widest shadow-lg shadow-gold/20 hover:brightness-110 active:scale-[0.98] transition disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSaving ? (
              <>
                <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving...
              </>
            ) : mode === 'destination' ? 'Save destination' : 'Save trip'}
          </button>
        </div>
      )}

      {mode === 'wizard' && (
        <div className="flex items-center gap-3 pt-4 border-t border-app-border/10">
          {wizardStep === 'dates' ? (
            <button onClick={onCancel} className="flex-1 p-3.5 rounded-xl border border-app-border text-[11px] font-black uppercase tracking-widest text-app-sub hover:bg-app-faint transition">
              Cancel
            </button>
          ) : (
            <button aria-label="Back"
              onClick={() => setWizardStep(wizardStep === 'travellers' ? 'destinations' : 'dates')}
              className="p-3.5 px-5 rounded-xl border border-app-border text-app-sub hover:bg-app-faint transition flex items-center justify-center"
              title="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}

          {wizardStep === 'destinations' && (
            <button
              onClick={() => {
                // Drop a destination the user started but left completely empty
                const cleaned = data.destinations.filter(d => d.name.trim() || d.arrivalDate || d.departureDate);
                if (cleaned.length !== data.destinations.length) updateData({ ...data, destinations: cleaned });
                setWizardStep('travellers');
              }}
              className="flex-1 p-3.5 rounded-xl border border-app-border text-[11px] font-black uppercase tracking-widest text-app-sub hover:bg-app-faint transition"
            >
              Skip for now
            </button>
          )}

          <button
            onClick={() => {
              if (wizardStep === 'dates') { setWizardStep('destinations'); return; }
              if (wizardStep === 'destinations') { setWizardStep('travellers'); return; }
              handleSave();
            }}
            disabled={
              wizardStep === 'dates'
                ? (!data.title || !data.startDate || !data.endDate)
                : wizardStep === 'destinations'
                ? (overlaps.length > 0 || outsideTripRange.length > 0)
                : (!data.title || !data.startDate || !data.endDate || isSaving || overlaps.length > 0 || outsideTripRange.length > 0)
            }
            className="flex-1 p-3.5 rounded-xl bg-gold text-white text-[11px] font-black uppercase tracking-widest shadow-lg shadow-gold/20 hover:brightness-110 active:scale-[0.98] transition disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {wizardStep === 'travellers' ? (
              isSaving ? (
                <>
                  <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating...
                </>
              ) : branchSetup ? 'Save branch' : 'Create Trip'
            ) : 'Next'}
          </button>
        </div>
      )}

      {showAddTravellerModal && (
        <Modal title="Add a traveller" size="md" onClose={() => { setShowAddTravellerModal(false); setNewTraveller({ name: '', email: '' }); setNewTravellerRole('traveller'); }}>
          <div className="space-y-6">
              <div className="grid grid-cols-1 gap-3">
                <FormInput
                  label="Name"
                  value={newTraveller.name}
                  onChange={v => setNewTraveller({ ...newTraveller, name: v })}
                  placeholder="e.g. John Smith"
                />
                <FormInput
                  label="Email Address"
                  value={newTraveller.email}
                  onChange={v => setNewTraveller({ ...newTraveller, email: v })}
                  placeholder="e.g. john@example.com"
                  type="email"
                />
                <TripRoleSelect value={newTravellerRole} onChange={setNewTravellerRole} canAssignAdmin={iAmAdmin} disabled={isSaving} />
                <TripParticipationFields destinations={data.destinations} value={newTravellerDates}
                  onChange={value => { setNewTravellerDates(value); setInviteDatesError(''); }} disabled={isSaving} />
                {!!inviteDatesError && <p role="alert" className="text-sm text-rose-600">{inviteDatesError}</p>}
              </div>
              <button
                onClick={addTraveller}
                disabled={!newTraveller.name || !newTraveller.email || isSaving}
                className="w-full py-3.5 bg-gold text-white rounded-xl text-[11px] font-black uppercase tracking-widest shadow-lg shadow-gold/20 hover:brightness-110 active:scale-[0.98] transition disabled:opacity-30 flex items-center justify-center gap-2"
              >
                {isSaving ? (
                  <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Send className="w-3 h-3" />
                )}
                Invite Traveller
              </button>
          </div>
        </Modal>
      )}

      {roleTarget && currentUser && (
        <RoleSheet trip={data} target={roleTarget} currentUserId={currentUser.id}
          onClose={() => setRoleTarget(null)}
          onChanged={changes => { if (Object.keys(changes).length) updateData({ ...data, ...changes }); }} />
      )}
      {showMyTripDates && currentUser && <MyTripDates trip={data} userId={currentUser.id}
        onClose={() => setShowMyTripDates(false)} onSaved={dates => {
          const participation = { ...data.participation };
          if (dates) participation[currentUser.id] = dates;
          else delete participation[currentUser.id];
          setData({ ...data, participation });
        }} />}

      {confirmingRemovalId && (
        <ConfirmDeleteModal
          title="Remove this traveller?"
          message="They'll no longer have access to this trip. Their contributions and expenses stay."
          confirmLabel="Remove" busyLabel="Removing…"
          isDeleting={isSaving}
          onCancel={() => setConfirmingRemovalId(null)}
          onConfirm={async () => {
            if (!(await removeTraveller(confirmingRemovalId))) throw new Error(saveError || 'Could not remove this traveller. Please try again.');
            return true;
          }}
        />
      )}

    </fieldset>
  );
}

type DateScrollerItem =
  | { type: 'date'; date: string; label: string; dow: string; isGap?: boolean }
  | { type: 'transport'; key: string; icon: LucideIcon; label: string };

// Day Planner's own top strip — every date across the whole trip (not just
// the selected destination), so you can jump anywhere without first swiping
// destinations. Same text style/layout as the day-pills this app already
// uses elsewhere: weekday/day/month stacked, serif day number with
// figureNudge optical centring. A destination-transition date that has a
// logged transport gets a small mode-icon badge sitting between the two
// pills, not a dashed border on either of them.
/** Fully transparent across the month label, opaque again just past it, so a
 *  date scrolling out on the left dissolves instead of being cut off. The
 *  stops are the label's own box: pl-7 (28px) plus roughly three characters. */
const DATE_STRIP_FADE_END = 104;
// Clear all the way under the label (pl-7 + "SEPT" ≈ 64px): stopping at 30px
// left a circle showing through it at up to 35%, so "OCT" sat on top of a date.
const DATE_STRIP_FADE = `linear-gradient(to right, transparent 0px, transparent 66px, #000 ${DATE_STRIP_FADE_END}px)`;

function DateScroller({ items, activeDate, onSelect, accent, onHeightChange }: {
  items: DateScrollerItem[];
  activeDate: string;
  onSelect: (date: string) => void;
  accent: string;
  /** Reports this scroller's own rendered height so the parent can reserve
   *  the same vertical footprint for DestinationScroller (Shortlists mode),
   *  keeping the destination card below at the same Y in both tabs. */
  onHeightChange?: (h: number) => void;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);

  // Half the scroller's own rendered width, measured directly (same fix and
  // same reasoning as DestinationScroller) rather than via CSS `padding: 50%`,
  // which some mobile WebKit versions don't reliably resolve against a
  // scrolling flex/overflow ancestor's width — that was what pinned the last
  // date to the right edge instead of letting it reach centre.
  const [gutter, setGutter] = useState(0);
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const update = () => {
      // A full viewport width, not half — now that centring itself is native
      // (scroll-snap + scrollIntoView, not a hand-rolled formula that needed
      // gutter to be *exactly* half to cancel out), there's no downside to
      // over-provisioning: extra scrollable slack beyond the minimum needed
      // doesn't change what's visible once an edge item is actually centred,
      // it only guarantees the range comfortably covers it. Half wasn't
      // reliably enough room in practice for the true edges.
      setGutter(scroller.clientWidth);
      onHeightChange?.(scroller.offsetHeight);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(scroller);
    return () => ro.disconnect();
  }, [onHeightChange]);

  // Native CSS scroll-snap (snap-x/snap-center below) owns both centring and
  // boundary behaviour now — including "can't drag an edge pill past centre,
  // it springs back" — using the browser's own tested implementation, after
  // several rounds of hand-rolled scrollLeft maths that were each subtly
  // wrong about some aspect of the real geometry (rem-scaled Tailwind
  // constants, then padding-vs-scrollWidth ambiguity). This effect only
  // needs to handle the *programmatic* case (tapping a different pill, or an
  // external `activeDate` change) — scrollIntoView reads the current,
  // already-updated computed layout, so it targets the right element
  // immediately even though pill selection also triggers a colour/scale
  // transition (nothing here changes width, so there's no risk of it firing
  // mid-resize the way DestinationScroller's circles could).
  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const idx = items.findIndex(it => it.type === 'date' && it.date === activeDate);
    if (idx === -1) return;
    // +1: the leading spacer is `row`'s first child (see render below), so
    // `items[idx]` is one position further along than `idx` alone.
    const activeEl = row.children[idx + 1] as HTMLElement | undefined;
    activeEl?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [activeDate, items, gutter]);

  if (items.length === 0) return null;

  const todayISO = localDateKey();
  /**
   * The month shown at the strip's left edge. It follows the first date
   * actually on screen, not the selected one — scrolling from late January
   * into February should say February whether or not you've tapped anything
   * yet, and the selected date is often nowhere near the left edge.
   *
   * Measured rather than computed from scrollLeft: the circles are two
   * different sizes and the row carries transport badges between them, so
   * there's no fixed stride to divide by.
   */
  const selectedMonth = (() => {
    const active = items.find(i => i.type === 'date' && i.date === activeDate);
    return (active && active.type === 'date' ? active.label : '').split(' ')[1] || '';
  })();
  const [visibleMonth, setVisibleMonth] = useState('');
  const monthFrame = useRef<number | null>(null);
  const syncVisibleMonth = useCallback(() => {
    if (monthFrame.current) return;
    monthFrame.current = requestAnimationFrame(() => {
      monthFrame.current = null;
      const el = scrollerRef.current;
      if (!el) return;
      // The first date clear of the fade — anything left of that is dissolving
      // under the label and shouldn't be what the label names.
      const edge = el.getBoundingClientRect().left + DATE_STRIP_FADE_END;
      for (const node of Array.from(el.querySelectorAll<HTMLElement>('[data-month]'))) {
        if (node.getBoundingClientRect().right >= edge) {
          setVisibleMonth(node.dataset.month || '');
          return;
        }
      }
    });
  }, []);
  useEffect(() => {
    syncVisibleMonth();
    return () => { if (monthFrame.current) cancelAnimationFrame(monthFrame.current); };
  }, [items, activeDate, syncVisibleMonth]);
  const activeMonth = visibleMonth || selectedMonth;

  // font-sans, not font-mono. A booking reference is monospace on purpose —
  // it's a code you read character by character — but a weekday isn't, and
  // rendering these in a different face from every other label in the app is
  // what made the row look like it couldn't decide on a font.
  const labelCls = 'text-[10px] font-black uppercase opacity-40 tracking-widest font-sans leading-none';
  // A content-less element as the very last child of a scrollable row isn't
  // counted towards the container's scrollWidth in some engines — confirmed
  // by direct testing, not a spec detail found by inspection — which is what
  // was silently capping how far you could ever scroll right, regardless of
  // how big `gutter` was. A trailing text node (even a non-breaking space)
  // didn't defeat it either; only a genuine nested *element* child does —
  // rendered at 1×1px so it can never be visible.
  const spacerStyle = { width: gutter, flexShrink: 0 } as const;

  return (
    // A solid page-matching background (not white — this sits directly on
    // the page background, not inside a card), dropped on a tinted tab where
    // the page already is the background: see .date-strip in index.css. The
    // parent (ItineraryTab)
    // renders this as a flex-shrink-0 sibling outside the scrolling area
    // entirely, not via `position: sticky` — an earlier sticky-based attempt
    // only stayed stuck within its own immediate parent's height, which was
    // barely taller than the strip itself, so it just scrolled away with
    // everything else instead of remaining visible. `pt-4` (was `pt-2`)
    // replaces the top breathing room <main> used to provide via its own
    // padding — <main> has none for this tab specifically now.
    /* The month overlays the strip rather than taking a column of its own.
       A column was the wrong fix for the plate problem: it narrowed the
       scroller, and the scroller centres the selected date within ITS OWN
       box — so every date sat half a label's width right of the screen's
       centre.
       
       So the scroller is full width again, and what hides the circles
       passing under the label is a mask rather than a plate. A mask paints
       nothing: the circles fade out as they reach the left edge instead of
       meeting an opaque rectangle with an edge of its own. Aligned to the
       inside of the card below, not to the screen. */
    <div className="relative -mx-6">
      <div className="absolute left-0 top-0 bottom-0 z-20 flex items-center pl-7 pointer-events-none">
        <span className={labelCls} style={{ opacity: 0.55 }}>{activeMonth}</span>
      </div>
    <div
      ref={scrollerRef}
      className="date-strip overflow-x-auto no-scrollbar pt-4 pb-4 snap-x snap-mandatory"
      onScroll={syncVisibleMonth}
      style={{
        maskImage: DATE_STRIP_FADE,
        WebkitMaskImage: DATE_STRIP_FADE,
      }}
    >
      {/* Spacers are direct siblings of the pills/badges in one flat flex row
          (not nested inside their own wrapper) — nesting them one level
          deeper was *also* silently excluding the trailing spacer's width
          from scrollWidth, on top of the empty-element issue above. */}
      <div ref={rowRef} className="flex items-center gap-4">
        <div aria-hidden style={spacerStyle}><div style={{ width: 1, height: 1 }} /></div>
        {items.map(item => {
          if (item.type === 'transport') {
            const Icon = item.icon;
            return (
              <div
                key={item.key}
                title={item.label}
                className="flex-shrink-0 w-7 h-7 rounded-full bg-app-faint border border-app-border/50 flex items-center justify-center text-app-sub/50 self-center"
              >
                <Icon className="w-3.5 h-3.5" />
              </div>
            );
          }
          const d = item;
          const isActive = d.date === activeDate;
          const isToday = d.date === todayISO;
          const isGap = !!d.isGap;
          const dayPart = d.label.split(' ')[0];
          const monthPart = d.label.split(' ')[1] || '';
          const weekday = (d.dow || '').slice(0, 3).toUpperCase();
          return (
            /* Same circle as the Shortlist tab's destination scroller — same
               sizes, same rings, same solid fill — so the two strips read as
               one control in two modes rather than two designs. The date
               itself is all the circle can hold at 36px, so the weekday and
               month sit beneath it, in a fixed-height column that keeps
               every circle on one line. */
            <div key={d.date} data-month={monthPart} className="w-12 flex flex-col items-center gap-1.5 flex-shrink-0">
              <button
                onClick={() => onSelect(d.date)}
                style={{ borderColor: isActive ? accent : undefined }}
                className={`tap-target surface-solid snap-center rounded-full bg-white flex items-center justify-center shadow-sm transition-[width,height,opacity] duration-300 relative ${
                  isActive ? 'w-12 h-12 ring-1 ring-gold z-10'
                    : isGap ? 'w-9 h-9 ring-1 ring-dashed ring-app-border/60 opacity-40'
                    : 'w-9 h-9 ring-1 ring-app-border/60 opacity-70 hover:opacity-100'
                }`}
              >
                {/* Sits on the card's own top border with a slight overlap,
                    rather than as a separate pill below it — that extra
                    element used to add height under today's card
                    specifically, throwing off vertical alignment against
                    the other (badge-less) date cards in the row. No gold
                    border on the card itself any more either — the badge
                    alone is enough to mark today. */}
                {isToday && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded-full bg-gold text-white text-[10px] font-black uppercase tracking-wider leading-none shadow-sm whitespace-nowrap">
                    Today
                  </div>
                )}
                <div
                  style={{ color: isActive ? accent : undefined, transform: `translateY(${figureNudge(dayPart)})` }}
                  className={`flex items-center justify-center font-serif font-black leading-none ${isActive ? 'text-2xl' : 'text-lg'}`}
                >
                  {dayPart}
                </div>
              </button>
              {/* Fixed height so nothing here can push a circle out of line.
                  The month lives on the strip's own left edge now, not under
                  each date — it was repeating itself down the row. */}
              <div className="h-3 flex items-center" style={{ color: isActive ? accent : undefined }}>
                <span className={labelCls}>{weekday}</span>
              </div>
            </div>
          );
        })}
        <div aria-hidden style={spacerStyle}><div style={{ width: 1, height: 1 }} /></div>
      </div>
    </div>
    </div>
  );
}

// Horizontal, scrollable destination selector shared by the Itinerary tab's
// Shortlists and Day Planner modes — a sideways version of the Trips tab's
// vertical destination timeline (see TripOverview's cityGroups render). The
// selected destination is centred and larger; unselected neighbours are
// smaller. Generous edge padding means even the first/last destination
// can still reach true visual centre — the scroll-to-centre effect below does
// the actual centring, the padding just guarantees there's room to scroll to.
function DestinationScroller({ destinations, selectedIdx, onSelect, onHeightChange }: {
  destinations: Destination[];
  selectedIdx: number;
  onSelect: (idx: number) => void;
  /** Reports this scroller's own rendered height so the parent can reserve
   *  the same vertical footprint for DateScroller (Day Planner mode),
   *  keeping the destination card below at the same Y in both tabs. */
  onHeightChange?: (h: number) => void;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  // Half the scroller's own rendered width — just for sizing the centring
  // gutter spacers below. Everything else about *reaching* centre (including
  // boundary behaviour: can't drag an edge circle past centre, it springs
  // back) is now native CSS scroll-snap (snap-x/snap-center below), not
  // hand-rolled scrollLeft maths — several rounds of that were each subtly
  // wrong about some aspect of the real geometry (rem-scaled Tailwind
  // constants, then padding-vs-scrollWidth ambiguity).
  const [gutter, setGutter] = useState(0);
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const update = () => {
      // A full viewport width, not half — now that centring itself is native
      // (scroll-snap + scrollIntoView, not a hand-rolled formula that needed
      // gutter to be *exactly* half to cancel out), there's no downside to
      // over-provisioning: extra scrollable slack beyond the minimum needed
      // doesn't change what's visible once an edge item is actually centred,
      // it only guarantees the range comfortably covers it. Half wasn't
      // reliably enough room in practice for the true edges.
      setGutter(scroller.clientWidth);
      onHeightChange?.(scroller.offsetHeight);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(scroller);
    return () => ro.disconnect();
  }, [onHeightChange]);

  // Only the *programmatic* case (tapping a different circle) needs handling
  // here — scrollIntoView reads the current, already-updated computed layout
  // at call time, so it targets the circle's final post-resize position
  // immediately rather than a stale one, even though selecting a circle also
  // triggers its own 300ms w-9→w-16 resize transition (that's what
  // previously produced a visible "overlap" between the shrinking and
  // growing circles when this used a DOM read deferred behind a timeout).
  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const activeEl = row.querySelector('[data-active="true"]') as HTMLElement | null;
    activeEl?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [selectedIdx, gutter]);

  if (destinations.length === 0) return null;

  // A content-less element as the very last child of a scrollable row isn't
  // counted towards the container's scrollWidth in some engines — confirmed
  // by direct testing, not a spec detail found by inspection — which is what
  // was silently capping how far you could ever scroll right, regardless of
  // how big `gutter` was. A trailing text node (even a non-breaking space)
  // didn't defeat it either; only a genuine nested *element* child does —
  // rendered at 1×1px so it can never be visible.
  const spacerStyle = { width: gutter, flexShrink: 0 } as const;

  // pt-4 (was pt-2) — this is the first thing rendered on the Shortlist
  // tab, and <main> now has zero top padding there (its sticky section
  // headers need to sit flush with <main>'s own top edge, with no gap for
  // scrolled content to peek through above whichever one is currently
  // pinned) — so this owns the lost breathing room itself, same fix as
  // DateScroller's equivalent pt-2→pt-4 for the Schedule tab.
  return (
    <div ref={scrollerRef} className="overflow-x-auto no-scrollbar pt-4 pb-4 -mx-6 snap-x snap-mandatory">
      {/* Spacers are direct siblings of the circles in one flat flex row (not
          nested inside their own wrapper) — nesting them one level deeper was
          *also* silently excluding the trailing spacer's width from
          scrollWidth, on top of the empty-element issue above. The
          connecting line now insets by `gutter` on each side (instead of
          Tailwind's left-0/right-0) to still span exactly the circles' own
          width now that it shares a row with the gutters either side of it. */}
      <div ref={rowRef} className="relative flex items-center gap-7">
        <div className="absolute top-1/2 h-[2px] bg-app-border/40 -translate-y-1/2 -z-10" style={{ left: gutter, right: gutter }} />
        <div aria-hidden style={spacerStyle}><div style={{ width: 1, height: 1 }} /></div>
        {destinations.map((dest, idx) => {
          const isSelected = idx === selectedIdx;
          return (
            <button
              key={idx}
              data-active={isSelected}
              onClick={() => onSelect(idx)}
              className={`surface-solid snap-center shrink-0 rounded-full flex items-center justify-center shadow-sm transition-[width,height,opacity] duration-300 bg-app-surface ${
                // Selected used to jump to w-16 (64px) against unselected's
                // w-9 (36px) — the biggest circle in the row set this
                // scroller's whole height. Only "slightly bigger" now
                // (w-12/48px) so the row takes noticeably less vertical
                // space without losing the size cue for which is selected.
                isSelected ? 'w-12 h-12 text-xl ring-1 ring-gold' : 'w-9 h-9 text-base ring-1 ring-app-border/60 opacity-60 hover:opacity-90'
              }`}
              title={dest.name}
            >
              {renderEmojiOrIcon(dest.emoji || getLocationEmoji(dest.name), isSelected ? 22 : 16)}
            </button>
          );
        })}
        <div aria-hidden style={spacerStyle}><div style={{ width: 1, height: 1 }} /></div>
      </div>
    </div>
  );
}

function TripOverview({
  branchTimeline,
  itineraryDays,
  onSelectDay,
  tripConfig,
  onUpdateConfig,
  currentUser,
  managementMode,
  setManagementMode,
  highlightDestIdx,
  setHighlightDestIdx,
  newDestDates,
  setNewDestDates,
  trips,
  activeTripId,
  onSelectTrip,
  onAddTrip,
  onDeleteTrip,
  onDraftChange,
  onOpenImport,
}: {
  branchTimeline?: BranchTimelineContext,
  itineraryDays: Day[],
  onSelectDay: (day: Day) => void,
  tripConfig: TripConfig,
  onUpdateConfig: (c: TripConfig) => Promise<boolean>,
  currentUser: User,
  managementMode: 'info' | 'destinations' | 'destination' | 'members' | 'full' | null,
  setManagementMode: (v: 'info' | 'destinations' | 'destination' | 'members' | 'full' | null) => void,
  highlightDestIdx: number | null,
  setHighlightDestIdx: (idx: number | null) => void,
  newDestDates: { arrivalDate: string; departureDate: string } | null,
  setNewDestDates: (v: { arrivalDate: string; departureDate: string } | null) => void,
  trips: TripConfig[],
  activeTripId: string,
  onSelectTrip: (id: string) => void,
  onAddTrip: () => void,
  onDeleteTrip: (id: string) => void,
  onDraftChange?: (c: TripConfig) => void,
  onOpenImport?: () => void,
}) {
  const destinationsRef = useRef<HTMLDivElement>(null);
  const personalRoute = React.useContext(PersonalRouteContext);

  // Shared gaps modal, opened from Home or Schedule. Keep its host here so
  // actions can open existing item details or the relevant itinerary editor.
  const [showGapsModal, setShowGapsModal] = useState(false);
  const tripJunctions = React.useContext(JunctionContext);
  // The Schedule's "what's missing" banner opens this modal from another tab:
  // taken on mount (sent before this view existed) or live.
  useEffect(() => {
    if (takePendingNav('nav-itinerary-gaps')) setShowGapsModal(true);
    const open = () => { takePendingNav('nav-itinerary-gaps'); setShowGapsModal(true); };
    window.addEventListener('nav-itinerary-gaps', open);
    return () => window.removeEventListener('nav-itinerary-gaps', open);
  }, []);
  const plannerInsights = computeItineraryGaps(itineraryDays, tripConfig, {
    onDestinationsGap: (gapStart, gapEnd) => {
      setShowGapsModal(false);
      setManagementMode('destination');
      setHighlightDestIdx(null);
      setNewDestDates({ arrivalDate: gapStart, departureDate: gapEnd });
    },
    onScrollToDestination: (destIdx) => {
      setShowGapsModal(false);
      if (destIdx < 0) return;
      setTimeout(() => {
        const el = document.getElementById(`itinerary-dest-${destIdx}`);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Same deep-link gold glow used for the destinations-editor jump
        // below and by Expenses/Memories — reset+reflow so it replays even
        // if this card was just flashed a moment ago.
        el.classList.remove('itinerary-flash');
        void el.offsetWidth;
        el.classList.add('itinerary-flash');
        setTimeout(() => el.classList.remove('itinerary-flash'), 2600);
      }, 150);
    },
    onScrollToTransport: (toDestIdx) => {
      setShowGapsModal(false);
      if (toDestIdx < 0) return;
      // cityGroups is declared further down in this same component/render —
      // safe to reference here since this callback only ever runs later, on
      // click, by which point cityGroups has long since been assigned.
      // Its index space (interspersed with gap entries) is different from
      // destIdx (tripConfig.destinations), which is why this needs its own
      // lookup rather than reusing itinerary-dest-${destIdx} directly.
      const groupIdx = cityGroups.findIndex(g => g.destIdx === toDestIdx);
      // groupIdx === 0 → the leading transport-start row; > 0 → the row
      // rendered as part of that group's own iteration, transport-${idx}.
      // That row is only rendered when the PRECEDING group is also a real
      // destination — an unfilled gap directly before this one suppresses
      // it entirely, so the id can legitimately not exist even though
      // groupIdx itself resolved fine.
      const anchorId = groupIdx === 0 ? 'transport-start' : groupIdx > 0 ? `transport-${groupIdx}` : null;
      setTimeout(() => {
        // Prefer the transport row itself; fall back to the destination's
        // own card (always present, via itinerary-dest-${destIdx}) when the
        // row doesn't exist or groupIdx couldn't be resolved at all —
        // still lands on the right part of the itinerary rather than
        // silently doing nothing.
        const el = (anchorId && document.getElementById(anchorId)) || document.getElementById(`itinerary-dest-${toDestIdx}`);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // A background-shape flash didn't work here -- this row has no
        // card edge for a halo/wash to hug, just centred icon+text, so it
        // read as a stray shape with nothing to anchor to. Pulsing the
        // "Add transport" CTA's own text instead: its usual text-gold/60
        // gives way to fully-saturated gold for the duration of the pulse.
        const cta = el.querySelector('.itinerary-transport-add-cta');
        if (cta) {
          cta.classList.remove('itinerary-flash-text');
          void (cta as HTMLElement).offsetWidth;
          cta.classList.add('itinerary-flash-text');
          setTimeout(() => cta.classList.remove('itinerary-flash-text'), 2400);
        } else {
          // Landed on the destination-card fallback instead — flash that
          // with the same card glow onScrollToDestination uses, so there's
          // still a clear visual confirmation of where this scrolled to.
          el.classList.remove('itinerary-flash');
          void (el as HTMLElement).offsetWidth;
          el.classList.add('itinerary-flash');
          setTimeout(() => el.classList.remove('itinerary-flash'), 2600);
        }
      }, 150);
    },
    onOpenAccommodation: (accommodation, dayDate) => {
      setShowGapsModal(false);
      const tripStay = (tripConfig.accommodations || []).find(saved =>
        accommodation.id ? saved.id === accommodation.id
          : !saved.id && saved.name === accommodation.name
            && saved.checkIn === accommodation.checkIn && saved.checkOut === accommodation.checkOut
      );
      setViewAccom(tripStay || accommodation);
      setViewAccomDayDate(tripStay ? undefined : dayDate);
      setEditingAccom(false);
    },
    onOpenTransport: (transport, dayDate) => {
      setShowGapsModal(false);
      setViewTransport({ ...transport, _dayDate: dayDate });
      setEditingTransport(false);
    },
  }, branchTimeline?.personalSegment ? [currentUser.id] : undefined,
    // All view: people away on a private branch that day aren't this stop's gaps.
    branchTimeline?.personalSegment ? undefined : date => awayOn(tripJunctions[tripConfig.id] ?? [], date), currentUser.id);

  useEffect(() => {
    if (managementMode !== 'destinations') return;

    // First scroll the whole destinations panel into view
    if (destinationsRef.current) {
      destinationsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Then, if a specific destination was targeted, scroll to its card and flash it
    if (highlightDestIdx === null) return;
    const cardId = `dest-card-${highlightDestIdx}`;
    let attempts = 0;
    const poll = setInterval(() => {
      attempts++;
      const el = document.getElementById(cardId);
      if (el) {
        clearInterval(poll);
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.remove('itinerary-flash');
          void el.offsetWidth;
          el.classList.add('itinerary-flash');
          setTimeout(() => el.classList.remove('itinerary-flash'), 3000);
        }, 250);
        setHighlightDestIdx(null);
      } else if (attempts >= 20) {
        clearInterval(poll);
      }
    }, 100);
    return () => clearInterval(poll);
  }, [managementMode, highlightDestIdx]);

  const cityGroups: { city: string; icon: string; flag: string; country: string; range: string; nights: number; destinationId?: string; via?: string; destIdx?: number; startDateISO?: string; endDateISO?: string }[] = [];

  itineraryDays.forEach((day, idx) => {
    const last = cityGroups[cityGroups.length - 1];
    const isGap = day.destinationId === 'gap';
    const cityLabel = isGap ? "Add destinations" : day.city;

    const destinations = tripConfig?.destinations || [];
    const destIdx = destinations.indexOf(destinationForDate(destinations, day.date)!);

    if (last && last.destIdx === destIdx && !isGap) {
      last.range = `${last.range.split('–')[0]}–${day.dateShort}`;
      last.nights += 1;
      last.endDateISO = day.date;
    } else if (last && last.city === "Add destinations" && isGap) {
       const newRangeStart = last.range.split('–')[0] || last.range;
       last.range = `${newRangeStart}–${day.dateShort}`;
       last.nights += 1;
       last.endDateISO = day.date;
    } else {
      cityGroups.push({
        city: cityLabel,
        icon: day.emoji,
        flag: day.flag,
        country: day.country,
        destinationId: day.destinationId,
        range: day.dateShort,
        nights: 1,
        via: day.via,
        destIdx: destIdx !== -1 ? destIdx : undefined,
        startDateISO: day.date,
        endDateISO: day.date,
      });
    }
  });

  // Override range and nights from the stored destination dates, computed directly
  // from arrivalDate/departureDate rather than the day-by-day itineraryDays grouping
  // above. getExpandedDays() already treats the departure day as exclusive (it lands
  // in the arriving city, not the departing one — see the comment there), so this is
  // a redundant-but-consistent recompute, not a workaround for a bug in that logic.
  const _destList = tripConfig?.destinations || [];
  cityGroups.forEach(group => {
    if (group.destIdx === undefined) return;
    const dest = _destList[group.destIdx];
    if (!dest?.arrivalDate || !dest?.departureDate) return;
    // Use noon to avoid any local-timezone midnight-UTC shift
    const arr = new Date(dest.arrivalDate.slice(0, 10) + 'T12:00');
    const dep = new Date(dest.departureDate.slice(0, 10) + 'T12:00');
    if (isNaN(arr.getTime()) || isNaN(dep.getTime())) return;
    // nights = number of overnight sleeps: arrival to departure is exclusive, so
    // (dep - arr) in days equals the night count directly.
    group.nights = Math.max(0, Math.round((dep.getTime() - arr.getTime()) / 86_400_000));
    const arrShort = arr.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    // Show arrival → departure (inclusive) so the range reflects the full stay,
    // e.g. "25 Jun–29 Jun | 4 nights". The nights count is dep - arr, which is
    // correct because the departure day itself is not a sleep night.
    const depShort = group.nights > 0
      ? dep.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
      : arrShort;
    group.range = `${arrShort}–${depShort}`;
  });

  // Which cityGroups entry (if any) today falls inside — drives the gold
  // "current destination" ring/pill on its timeline circle, and the
  // scroll-on-open below. -1 outside travel dates (pre-trip, post-trip, or
  // sitting in an unfilled gap), matching "during travel dates" in the ask.
  const todayISO = localDateKey();
  const currentGroupIdx = cityGroups.findIndex(g =>
    g.destinationId !== 'gap' && g.destinationId !== undefined &&
    g.startDateISO && g.endDateISO && todayISO >= g.startDateISO && todayISO <= g.endDateISO
  );

  // Default the Logistics/Itinerary tab to opening on the current
  // destination (with the transport row above it also in view) whenever
  // today falls within a destination's stay. Runs once per mount — e.g.
  // each time the user navigates back to this tab, not on every re-render.
  useEffect(() => {
    if (currentGroupIdx === -1) return;
    const anchorId = currentGroupIdx === 0 ? 'transport-start' : `transport-${currentGroupIdx}`;
    let attempts = 0;
    const poll = setInterval(() => {
      attempts++;
      const el = document.getElementById(anchorId);
      if (el) {
        clearInterval(poll);
        el.scrollIntoView({ behavior: 'auto', block: 'start' });
      } else if (attempts >= 20) {
        clearInterval(poll);
      }
    }, 100);
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [editingEmojiIdx, setEditingEmojiIdx] = useState<number | null>(null);
  const isAdmin = isTripAdmin(tripConfig, currentUser.id);

  const updateDestinationEmoji = (idx: number, emoji: string) => {
    const newDestinations = [...tripConfig.destinations];
    newDestinations[idx] = { ...newDestinations[idx], emoji };
    onUpdateConfig({ ...tripConfig, destinations: newDestinations });
    setEditingEmojiIdx(null);
  };

  // ── Accommodation / Transport (Trips tab) ─────────────────────────────────
  // Both are read here to display saved entries and detect gaps (accommodations
  // are trip-level, transports live per itinerary day). Adding/importing opens
  // the same Add/Import flow the itinerary day view uses, as a portal popup
  // rather than navigating the user away from the Trips tab; the write path
  // (and expense-linking) still only exists once — in DayDetail's own handlers —
  // this is just a second UI entry point onto the same trip-level / per-day
  // Firestore fields, so nothing here needs its own sync.
  const [accomAdd, setAccomAdd] = useState<{ dest: Destination; range: { start: string; end: string }; step: 'choose' | 'form' } | null>(null);
  const [transportAdd, setTransportAdd] = useState<{ dayDate: string; step: 'choose' | 'form' } | null>(null);
  const [savingTransport, setSavingTransport] = useState(false);
  const [savingAccommodation, setSavingAccommodation] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  const handleAddAccommodation = async (dest: Destination, a: Accommodation) => {
    const accId = a.id || Math.random().toString(36).substr(2, 9);
    const newAcc: Accommodation = {
      ...a,
      id: accId,
      contributedBy: a.contributedBy || currentUser.id,
      createdAt: a.createdAt || new Date().toISOString(),
    };
    if (branchTimeline && (branchTimeline.preview || branchTimeline.isBranch || branchTimeline.personalSegment)) {
      await branchTimeline.saveAccommodationDraft(null, newAcc);
      setAccomAdd(null);
      return;
    }
    await saveAccommodationBooking(tripConfig, null, newAcc, currentUser.id);
    setAccomAdd(null);
    const cat = ACCOM_CATS.find(c => c.name === a.category);
    notifyAccommodationAdded(
      tripConfig.id, tripConfig.title,
      currentUser.id, getFirstName(currentUser.name),
      tripConfig.memberIds || [],
      a.name, dest.country || dest.name, a.checkIn || dest.arrivalDate, cat?.emoji || '🏨', accId
    ).catch(console.error);
  };

  const sortTransportsByDep = (list: Transport[]) => [...list].sort((a, b) => {
    const aTime = a.dep ? new Date(a.dep.includes('T') ? a.dep : `1970-01-01T${a.dep}`).getTime() : 0;
    const bTime = b.dep ? new Date(b.dep.includes('T') ? b.dep : `1970-01-01T${b.dep}`).getTime() : 0;
    return aTime - bTime;
  });

  const handleAddTransport = async (dayDate: string, t: Transport) => {
    const itemId = t.id || Math.random().toString(36).substr(2, 9);
    const finalT: Transport = { ...t, id: itemId, createdAt: t.createdAt || new Date().toISOString() };
    const scope = personalRoute?.some(v=>v.source.id===tripConfig.id || v.source.rootTripId===tripConfig.id) ? boundaryScope(finalT,personalRoute) ?? tripConfig : tripConfig;
    if (branchTimeline) {
      await branchTimeline.saveTransportDraft(dayDate, null, finalT);
      setTransportAdd(null);
      if (branchTimeline.preview) return;
    } else {
      await saveTransportBooking(scope, dayDate, null, finalT, currentUser.id);
      setTransportAdd(null);
    }
    const label = t.flightNum
      ? `${t.carrier} ${t.flightNum} — ${t.fromCode || t.from} → ${t.toCode || t.to}`
      : `${t.mode} — ${t.from} → ${t.to}`;
    notifyTransportAdded(
      scope.id, scope.title,
      currentUser.id, getFirstName(currentUser.name),
      scope.memberIds || [],
      label, dayDate, t.icon, itemId,
      t.fromCity, t.from, t.fromCode,
      t.toCity, t.to, t.toCode,
    ).catch(console.error);
  };

  // Tapping a saved accommodation/transport entry opens a details popup right
  // here on the Trips tab — read-only at first (reusing the exact same
  // presentational components, AccommodationSection / TransportDetail, the
  // itinerary day view renders), with an Edit toggle that swaps in
  // AccommodationForm / TransportForm within the same popup rather than
  // navigating away. Saving updates the item in place and returns to the
  // read-only view so the popup reflects the change immediately.
  const [viewAccom, setViewAccom] = useState<Accommodation | null>(null);
  const [viewAccomDayDate, setViewAccomDayDate] = useState<string | undefined>();
  const [editingAccom, setEditingAccom] = useState(false);
  const [viewTransport, setViewTransport] = useState<(Transport & { _dayDate?: string }) | null>(null);
  const [confirmRemoveAccom, setConfirmRemoveAccom] = useState<Accommodation | null>(null);
  const [confirmRemoveTransport, setConfirmRemoveTransport] = useState<(Transport & { _dayDate?: string }) | null>(null);
  const [editingTransport, setEditingTransport] = useState(false);

  // Also open this same popup in response to a cross-tab "open-item-modal"
  // request (a notification, a Home-tab insight card, an Expenses "View in
  // Itinerary" link) — reusing the exact state/handlers above rather than a
  // second write path.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as ItemModalRequest | undefined;
      if (!detail) return;
      if (detail.type === 'accommodation') {
        const acc = (tripConfig.accommodations || []).find(a => a.id === detail.itemId);
        if (acc) { setViewAccom(acc); setViewAccomDayDate(undefined); setEditingAccom(detail.mode === 'edit'); }
      } else if (detail.type === 'transport' && detail.dayDate) {
        const t = (tripConfig.itinerary?.[detail.dayDate]?.transports || []).find(x => x.id === detail.itemId);
        if (t) { setViewTransport({ ...t, _dayDate: detail.dayDate }); setEditingTransport(detail.mode === 'edit'); }
      }
    };
    window.addEventListener('open-item-modal', handler);
    return () => window.removeEventListener('open-item-modal', handler);
  }, [tripConfig]);

  // Itinerary items (transport/accommodation — the Itinerary tab) notify the
  // person who originally logged the booking plus the trip owner when its
  // date moves; Shortlist items (attraction/eat) notify only the item's own
  // owner — see notifyDateChangeRecipients below for the shared "who" logic.
  // Never the editor themself, and never the same person twice.
  const notifyDateChangeRecipients = (...ids: (string | undefined)[]): string[] =>
    [...new Set(ids.filter((id): id is string => !!id && id !== currentUser.id))];

  const handleEditAccommodation = async (a: Accommodation, previous: Accommodation | null) => {
    if (!previous) throw new Error('Reopen this accommodation before editing it.');
    const updated: Accommodation = { ...a, contributedBy: a.contributedBy || currentUser.id };
    const saved = branchTimeline && (branchTimeline.preview || branchTimeline.isBranch || branchTimeline.personalSegment)
      ? await branchTimeline.saveAccommodationDraft(previous, updated)
      : await saveAccommodationBooking(tripConfig, previous, updated, currentUser.id, viewAccomDayDate);
    setViewAccom(saved);
    setEditingAccom(false);
    if (!saved || branchTimeline?.preview) return;
    void notifyOwnBookingParticipation(tripConfig, currentUser, previous, updated, saved, viewAccomDayDate || saved.checkIn.slice(0, 10), 'accommodation');
    // "Date" for a stay is really a range; either bound moving is a real
    // reschedule, and the notification names whichever one changed (or
    // check-in, if oddly both did in the same edit).
    // A merged concurrent edit isn't this editor's reschedule.
    const newDate = previous.checkIn !== updated.checkIn && previous.checkIn !== saved.checkIn ? saved.checkIn
      : previous.checkOut !== updated.checkOut && previous.checkOut !== saved.checkOut ? saved.checkOut : '';
    if (newDate) {
      const recipients = notifyDateChangeRecipients(previous.contributedBy, tripConfig.ownerId);
      if (recipients.length && newDate) {
        notifyItemDateChanged(
          tripConfig.id, tripConfig.title,
          currentUser.id, getFirstName(currentUser.name),
          recipients,
          saved.name, fmtShortDate(newDate), viewAccomDayDate || saved.checkIn.slice(0, 10), 'accommodation', '🏨', saved.id,
        ).catch(console.error);
      }
    }
  };

  // Removal. The atomic writer has always supported a null `next` — it flips
  // the linked expense to itineraryDeleted in the same transaction — but no
  // caller ever passed one: the only delete affordance lived in DayDetail's
  // transport/accommodation sections, which were unreachable behind a
  // `mode !== 'shortlist'` guard. So a booking could be added and edited but
  // never removed.
  const handleRemoveAccommodation = async (previous: Accommodation) => {
    if (branchTimeline && (branchTimeline.preview || branchTimeline.isBranch || branchTimeline.personalSegment)) await branchTimeline.saveAccommodationDraft(previous, null);
    else await saveAccommodationBooking(tripConfig, previous, null, currentUser.id, viewAccomDayDate);
    setConfirmRemoveAccom(null);
    setViewAccom(null);
    setEditingAccom(false);
  };

  const handleRemoveTransport = async (dayDate: string, previous: Transport) => {
    if (branchTimeline && (branchTimeline.preview || branchTimeline.isBranch || branchTimeline.personalSegment)) await branchTimeline.saveTransportDraft(dayDate, previous, null);
    else await saveTransportBooking(tripConfig, dayDate, previous, null, currentUser.id);
    setConfirmRemoveTransport(null);
    setViewTransport(null);
    setEditingTransport(false);
  };

  const handleEditTransport = async (dayDate: string, t: Transport, previous: Transport | null) => {
    if (!previous) throw new Error('Reopen this transport before editing it.');
    const saved = branchTimeline && (branchTimeline.preview || branchTimeline.isBranch || branchTimeline.personalSegment) ? await branchTimeline.saveTransportDraft(dayDate, previous, t)
      : await saveTransportBooking(tripConfig, dayDate, previous, t, currentUser.id);
    setViewTransport({ ...saved!, _dayDate: /^\d{4}-\d{2}-\d{2}/.test(saved?.dep || '') ? saved!.dep.slice(0, 10) : dayDate });
    setEditingTransport(false);
    if (!saved || branchTimeline?.preview) return;
    void notifyOwnBookingParticipation(tripConfig, currentUser, previous, t, saved, saved.dep?.slice(0, 10) || dayDate, 'transport');
    const dateChanged = (key: 'dep' | 'arr') => previous[key]?.slice(0, 10) !== t[key]?.slice(0, 10)
      && previous[key]?.slice(0, 10) !== saved[key]?.slice(0, 10);
    const newDate = dateChanged('dep') ? saved.dep?.slice(0, 10) : dateChanged('arr') ? saved.arr?.slice(0, 10) : '';
    const recipients = notifyDateChangeRecipients(previous.contributedBy, tripConfig.ownerId);
    if (newDate && recipients.length) {
      const label = `${saved.carrier || saved.mode}${saved.flightNum ? ` ${saved.flightNum}` : ''} — ${saved.fromCode || saved.from} → ${saved.toCode || saved.to}`;
      notifyItemDateChanged(
        tripConfig.id, tripConfig.title, currentUser.id, getFirstName(currentUser.name), recipients,
        label, fmtShortDate(newDate), saved.dep?.slice(0, 10) || dayDate, 'transport', saved.icon, saved.id,
      ).catch(console.error);
    }
  };

  // Maps a destination's raw tripConfig.destinations index (which editing code
  // elsewhere relies on for lookups, so it can't just be replaced) to its
  // position in chronological (arrival-date) order. Destination-card coloring
  // uses this instead of the raw index so the palette flows smoothly through
  // the trip's actual date sequence and re-derives automatically whenever
  // destinations are reordered or redated — the raw storage order isn't
  // guaranteed to match chronological order.
  // A plain array keyed by raw index. (This file used to import lucide's `Map`
  // icon unaliased, which shadowed the global and made `new Map()` construct
  // an icon — tests/shadowedGlobals.test.ts now keeps that from coming back.)
  const chronoColorIdx = useMemo(() => {
    const byRawIdx: number[] = [];
    tripConfig.destinations
      .map((d, i) => ({ d, i }))
      .sort((a, b) => (a.d.arrivalDate || '').localeCompare(b.d.arrivalDate || ''))
      .forEach((entry, pos) => { byRawIdx[entry.i] = pos; });
    return byRawIdx;
  }, [tripConfig.destinations]);

  // All transports across the trip, sorted chronologically by departure — the
  // single source used to slot saved legs into the before-first / between /
  // after-last gaps rendered in the timeline below.
  const allTransports = useMemo(() => {
    const out: (Transport & { _dayDate: string })[] = [];
    Object.entries(tripConfig.itinerary || {}).forEach(([date, d]) => {
      (d?.transports || []).forEach(t => out.push({ ...t, _dayDate: date }));
    });
    return sortTransportsByDep(out) as (Transport & { _dayDate: string })[];
  }, [tripConfig.itinerary]);

  const transportDateOf = (t: Transport & { _dayDate: string }) => (t.dep ? t.dep.slice(0, 10) : t._dayDate);
  const viewJourney = viewTransport ? journeyFor(viewTransport as Transport & { _dayDate: string }, allTransports) : [];
  const viewJourneyRefs = [...new Set((viewJourney.length ? viewJourney : viewTransport ? [viewTransport] : []).map(l => l.ref).filter(Boolean))] as string[];
  const viewJourneyBookingServices = [...new Set((viewJourney.length ? viewJourney : viewTransport ? [viewTransport] : []).map(l => l.bookingService).filter(Boolean))] as string[];
  const viewLegs = viewJourney.length ? viewJourney : viewTransport ? [viewTransport] : [];
  const transportBookingContent = viewTransport && viewLegs.some(hasBookingContent) ? (
    <div className="space-y-3">
      <SavedBookingInformation item={{
        booked: viewLegs.some(leg => leg.booked),
        ref: viewJourneyRefs.join(', '), bookingService: viewJourneyBookingServices.join(', '),
        documents: [...new Map(viewLegs.flatMap(leg => leg.documents || []).map(doc => [doc.path, doc])).values()],
        bookingNotes: [...new Set(viewLegs.map(leg => leg.bookingNotes).filter(Boolean))].join('\n'),
      }} />
      <BookingCosts item={viewLegs.find(leg => leg.expenseLogId) || viewLegs.find(leg => leg.costAmount != null || leg.cost) || viewTransport}
        relatedItems={viewLegs} sourceType="transport" expenses={tripConfig.expenses} />
    </div>
  ) : null;

  // Transports whose journey overlaps [afterDate, beforeDate] (inclusive) — a
  // destination's departure day and the next one's arrival day can be the same
  // calendar day (touching, not a gap — see the destination gap-boundary logic
  // elsewhere in this file), so this uses inclusive bounds to match.
  // Overlap rather than a single date because an overnight leg occupies two of
  // them: a red-eye between touching destinations departs the day *before* the
  // shared boundary date, so testing its departure alone dropped it from the
  // very gap row it belongs to — which then offered to add it again.
  // Shared with Save (utils/transportPlacement.ts): journeys stay together,
  // and a leg gets a day's leeway into the next stop — a 01:55 connection on
  // the day after the next destination starts was orphaned by the strict range.
  const transportsInRange = (afterDate: string | null, beforeDate: string | null, nextDest?: Destination) =>
    transportsInWindow(allTransports, tripConfig.destinations || [], afterDate, beforeDate, nextDest);

  // Every saved record needs an entry point, even after dates move inside a
  // stay (or into an unfilled destination gap). Do not invent a connector.
  const unplacedTransports = allTransports.filter(t => {
    if (cityGroups.some(group => {
      const travellers = itineraryDays.find(d => d.date === group.startDateISO)?.travellers;
      const visible = !travellers?.length || travellers.includes(currentUser.id);
      return visible && group.destIdx != null && transportDuringVisit(t, tripConfig.destinations[group.destIdx], allTransports);
    })) return false;
    if (cityGroups.length && tripConfig.startDate && transportsInRange(null, tripConfig.startDate).includes(t)) return false;
    if (cityGroups.length && tripConfig.endDate && transportsInRange(tripConfig.endDate, null).includes(t)) return false;
    return !cityGroups.some((group, idx) => {
      const prev = cityGroups[idx - 1];
      if (!prev || prev.destinationId === 'gap' || group.destinationId === 'gap') return false;
      const a = prev.destIdx == null ? undefined : tripConfig.destinations[prev.destIdx];
      const b = group.destIdx == null ? undefined : tripConfig.destinations[group.destIdx];
      return a && b && transportsInRange(a.departureDate, b.arrivalDate, b).includes(t);
    });
  });

  // A gap's dashed CTA — opens the Add/Import popup. Reads "Add accommodation"
  // when nothing's booked yet for this destination at all, or "X nights missing.
  // Add accommodation?" once something has been added but a stretch is still gapped.
  const renderAccomAdder = (dest: Destination, range: { start: string; end: string }, opts: { nights: number; hasExisting: boolean }) => {
    const label = opts.hasExisting
      ? `${opts.nights} night${opts.nights !== 1 ? 's' : ''} missing. Add accommodation?`
      : 'Add accommodation';
    return (
      <button
        onClick={() => setAccomAdd({ dest, range, step: 'choose' })}
        className="w-full flex items-center justify-center gap-1.5 py-3 px-2 rounded-xl border-2 border-dashed border-app-border/50 bg-app-faint/70 text-app-sub/60 text-[13px] font-normal whitespace-nowrap hover:border-app-sub/40 hover:text-app-sub transition-colors"
      >
        <Plus className="w-4 h-4 shrink-0" />
        {label}
      </button>
    );
  };

  // "+ Add transport" between two cards (or before the first / after the last)
  // — opens the Add/Import popup.
  const renderTransportAdder = (dayDate: string) => (
    <button
      onClick={() => setTransportAdd({ dayDate, step: 'choose' })}
      // itinerary-transport-add-cta: the deep-link target for a missing-
      // transport gap insight — see onScrollToTransport in TripOverview,
      // which pulses *this* button's text rather than flashing a background
      // shape behind the whole (uncontainerised) row.
      // Same size as a saved transport line (13px), so the row doesn't jump
      // between an empty and a filled slot.
      className="itinerary-transport-add-cta w-full flex items-center justify-center gap-1.5 py-1 text-gold/70 text-[13px] font-bold"
    >
      <Plus className="w-4 h-4" /> Add transport
    </button>
  );

  // A transport "slot" in the timeline: any saved legs whose date falls between
  // afterDate/beforeDate, rendered uncontainerised (icon + label, gold, no card),
  // centered in the space between the two cards it sits between. Clicking a saved
  // leg jumps to it in the itinerary day view. dayDate is the day the "+ Add
  // transport" CTA jumps to — compact once something is already saved here.
  const renderTransportGap = (key: string, dayDate: string, afterDate: string | null, beforeDate: string | null, nextDest?: Destination) => {
    const items = transportsInRange(afterDate, beforeDate, nextDest);
    // Adjacent personal-route sections have separate private storage scopes.
    // Add a new journey on its route in All; avoid two ambiguous empty CTAs.
    if (branchTimeline?.personalSegment && items.length === 0) return null;
    // The very first gap (before any destination card) sits directly under
    // the scroll body's own pt-6 — this row's usual pt-6 would stack on top
    // of that with nothing in between to justify two separate top-spacing
    // mechanisms, reading as excess padding under the header. Every other
    // occurrence (between two destinations, or after the last one) keeps
    // the full py-6, since there it's genuinely providing the breathing
    // room between two cards.
    const isFirstGap = key === 'transport-start';
    const connector = <div className="h-3 border-l-2 border-dotted border-gold/50" />;
    return (
      <div key={key} id={key} className={`${isFirstGap ? 'pb-6' : 'py-6'} pl-[72px] flex flex-col items-center justify-center`}>
        <div className={`w-full flex flex-col items-center justify-center ${items.length ? 'pr-11' : ''}`}>
        {items.length > 0 && !isFirstGap && connector}
        {items.map((t, i) => {
          const ModeIcon = TRANSPORT_MODE_ICON[t.mode] || MapPin;
          // Mode stays Proper Case when it's the only/first word ("Flight"), but
          // lowercases when it follows a known carrier ("Singapore Airlines flight").
          const carrierName = t.carrier?.trim();
          let mainLabel = carrierName ? `${carrierName} ${t.mode.toLowerCase()}` : t.mode;
          // With more than one leg in this gap it's not obvious which arrives
          // where, so append the arrival city where known. A single leg between
          // two destinations doesn't need it — the destination cards already say.
          // Legs of one connecting journey skip it too: where the first leg
          // lands is the layover row between them, not part of either label.
          const next = items[i + 1];
          // The same flight saved twice (one typed in, one imported) used to
          // render as a "layover" of minus 75 minutes. Same mode, same times:
          // say so instead, and leave which copy to keep to the traveller.
          const possibleDuplicate = allTransports.some(other => sameTransportLeg(t, other)
            && conflictAudience(t, other, tripConfig.memberIds ?? [], [tripConfig.ownerId, ...(tripConfig.adminIds ?? [])]).includes(currentUser.id));
          const layoverCityName = next && !sameTransportLeg(t, next) ? layoverCity(t, next) : null;
          const chainedToNext = !!layoverCityName;
          const chainedFromPrev = i > 0 && !!layoverCity(items[i - 1], t);
          if (items.length > 1 && !chainedToNext && !chainedFromPrev) {
            const arrival = _cityLabel(t.to, t.toCity);
            if (arrival) mainLabel += ` to ${arrival}`;
          }
          const layover = chainedToNext ? layoverMinutes(t, next) : null;
          return (
            <React.Fragment key={t.id || i}>
            <div className="relative w-full flex items-center justify-center">
            <button
              onClick={() => setViewTransport(t)}
              // 13px, one step up the type scale from the 11px floor it sat on:
              // this line is the only thing telling you how you get from one
              // destination to the next, and it read as a footnote.
              className="flex min-w-0 max-w-full items-center justify-center gap-2 py-1.5 text-[13px] hover:opacity-80 transition-opacity"
            >
              <ModeIcon className="w-[18px] h-[18px] shrink-0 text-gold" />
              <span className="font-bold text-gold truncate">{mainLabel}</span>
              <TransportDuration t={t} />
            </button>
            {possibleDuplicate && <button type="button" aria-label="Possible duplicate — open Itinerary review"
              onClick={() => setShowGapsModal(true)}
              className="shrink-0 min-h-11 min-w-11 flex items-center justify-center text-rose-700">
              <CircleAlert className="w-4 h-4" aria-hidden="true" />
            </button>}
            {i === items.length - 1 && <button type="button" aria-label="Add another transport" title="Add transport"
              onClick={() => setTransportAdd({ dayDate, step: 'choose' })}
              className="absolute -right-11 top-1/2 -translate-y-1/2 min-h-11 min-w-11 flex items-center justify-center text-gold/70 hover:text-gold transition-colors">
              <Plus className="w-4 h-4" aria-hidden="true" />
            </button>}
            </div>
            {chainedToNext && (
              <>
                {connector}
                <div className="py-1 text-[13px] text-app-sub/60">
                  Layover in <strong className="font-bold">{layoverCityName}</strong>{layover != null ? ` · ${formatMinutes(layover)}` : ''}
                </div>
                {connector}
              </>
            )}
            </React.Fragment>
          );
        })}
        {items.length > 0 && key !== 'transport-end' && connector}
        {items.length === 0 && <div className="w-full">{renderTransportAdder(dayDate)}</div>}
        </div>
      </div>
    );
  };

  return (
    // Bled to full bleed (-mx-6 cancels <main>'s own px-6) so the header
    // below can paint edge-to-edge; each of the two children below restores
    // its own px-6 inset. Root is a fixed-height flex column (App.tsx gives
    // <main> a matching h-full/overflow-hidden for this tab) specifically
    // so the header sits OUTSIDE the scrolling area — only the body below
    // it scrolls (and rubber-band-bounces on iOS), the header never moves.
    <div className={`${branchTimeline?.personalSegment ? '' : 'h-full'} flex flex-col animate-fade-up -mx-6`}>
      {/* The pinned trip-name banner that used to sit here (a flex-shrink-0
          sibling above the scroller, holding TripIdentitySummary) is gone —
          the "Your Trip" switcher carousel below took its place. It's far
          too tall to pin, and it names the trip itself, so it scrolls with
          the rest of the body instead. The h-full flex column is kept: with
          a single flex-1 child it behaves as a plain full-height scroller,
          and App.tsx's `overflow-hidden` for this tab still lines up. */}
      <div className={branchTimeline?.personalSegment ? 'px-6' : 'flex-1 min-h-0 overflow-y-auto overflow-x-hidden no-scrollbar px-6 pb-[6.5rem]'}>
      <div className="space-y-6 pt-4">
      {!branchTimeline?.hideHeader && <TripSwitcherCard
        trips={trips}
        activeTripId={activeTripId}
        tripConfig={branchTimeline?.summary ?? tripConfig}
        currentUser={currentUser}
        onSelectTrip={onSelectTrip}
        onAddTrip={onAddTrip}
        onDeleteTrip={onDeleteTrip}
        onUpdateConfig={onUpdateConfig}
        itineraryDays={branchTimeline ? getExpandedDays(branchTimeline.summary) : itineraryDays}
      />}
      {branchTimeline?.controls}
      {!branchTimeline?.hideHeader && <ItineraryOriginalDocuments key={tripConfig.id} tripId={tripConfig.id} />}
      {managementMode === 'destinations' && (
        <div ref={destinationsRef} className="animate-fade-up">
          <Card className="p-8 border-gold/40 shadow-2xl bg-app-bg relative overflow-visible z-[70]">
            <div className="flex justify-between items-center mb-6">
              <div className="space-y-1">
                <div className="font-serif text-2xl font-bold text-app-text">Refine your trip...</div>
              </div>
              <button aria-label="Close" disabled={savingConfig} onClick={() => setManagementMode(null)} className="p-2 text-app-sub/40 hover:text-gold transition-colors"><X className="w-6 h-6" /></button>
            </div>
            <TripManagementForm
               key={tripConfig.id}
               config={tripConfig}
               mode="destinations"
               currentUser={currentUser}
               onSavingChange={setSavingConfig}
               onSave={async (c) => {
                 const saved = await onUpdateConfig(c);
                 if (saved) setManagementMode(null);
                 return saved;
               }}
               onPersist={onUpdateConfig}
               onChange={onDraftChange}
               onCancel={() => setManagementMode(null)}
            />
          </Card>
        </div>
      )}

      {managementMode === 'destination' && (highlightDestIdx !== null || newDestDates !== null) && createPortal(
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-app-bg/80 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-app-bg border border-app-border rounded-3xl p-6 max-w-xl w-full max-h-[85vh] overflow-y-auto overflow-x-hidden shadow-2xl"
            >
              <div className="flex justify-between items-center mb-6">
                <div className="space-y-1">
                  <div className="font-serif text-2xl font-bold text-app-text">Where are you going?</div>
                </div>
                <button aria-label="Close"
                  disabled={savingConfig}
                  onClick={() => { setManagementMode(null); setHighlightDestIdx(null); setNewDestDates(null); }}
                  className="p-2 text-app-sub/40 hover:text-gold transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <TripManagementForm
                 key={tripConfig.id}
                 config={tripConfig}
                 mode="destination"
                 editingDestIndex={highlightDestIdx ?? undefined}
                 newDestinationDates={newDestDates ?? undefined}
                 currentUser={currentUser}
                 onSavingChange={setSavingConfig}
                 onSave={async (c) => {
                   const saved = await onUpdateConfig(c);
                   if (saved) {
                     setManagementMode(null);
                     setHighlightDestIdx(null);
                     setNewDestDates(null);
                   }
                   return saved;
                 }}
                 onPersist={onUpdateConfig}
                 onChange={onDraftChange}
                 onCancel={() => { setManagementMode(null); setHighlightDestIdx(null); setNewDestDates(null); }}
              />
            </motion.div>
          </motion.div>,
          document.getElementById('import-modal-root') ?? document.body
      )}

      {accomAdd && createPortal(
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => { if (!savingAccommodation) setAccomAdd(null); }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-app-bg/80 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-app-bg border border-app-border rounded-3xl p-6 max-w-xl w-full max-h-[85vh] overflow-y-auto overflow-x-hidden shadow-2xl"
            >
              <div className="flex justify-between items-center mb-6">
                <div className="font-serif text-2xl font-bold text-app-text">Add Accommodation</div>
                <button aria-label="Close" disabled={savingAccommodation} onClick={() => setAccomAdd(null)} className="p-2 text-app-sub/40 hover:text-gold transition-colors">
                  <X className="w-6 h-6" />
                </button>
              </div>
              {accomAdd.step === 'choose' ? (
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setAccomAdd({ ...accomAdd, step: 'form' })}
                    className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-gold/30 bg-gold/5 rounded-2xl text-[11px] font-black uppercase tracking-widest text-gold transition active:scale-[0.98]"
                  >
                    <Plus className="w-5 h-5" /> Add
                  </button>
                  <button
                    onClick={() => { setAccomAdd(null); onOpenImport?.(); }}
                    className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-gold/30 bg-gold/5 rounded-2xl text-[11px] font-black uppercase tracking-widest text-gold transition active:scale-[0.98]"
                  >
                    <Download className="w-5 h-5" /> Import
                  </button>
                </div>
              ) : (
                <AccommodationForm
                  accommodation={{ id: '', name: "", stars: 0, area: "", checkIn: "", checkOut: "", address: "", confirmRef: "", confirmed: false, isAirbnb: false }}
                  isNew
                  onSavingChange={setSavingAccommodation}
                  onSave={(a) => handleAddAccommodation(accomAdd.dest, a)}
                  onCancel={() => setAccomAdd(null)}
                  country={accomAdd.dest.country}
                  defaultDates={accomAdd.range}
                  tripConfig={tripConfig}
                  currentUser={currentUser}
                />
              )}
            </motion.div>
          </motion.div>,
          document.getElementById('import-modal-root') ?? document.body
      )}

      {transportAdd && createPortal(
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => { if (!savingTransport) setTransportAdd(null); }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-app-bg/80 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-app-bg border border-app-border rounded-3xl p-6 max-w-xl w-full max-h-[85vh] overflow-y-auto overflow-x-hidden shadow-2xl"
            >
              <div className="flex justify-between items-center mb-6">
                <div className="font-serif text-2xl font-bold text-app-text">Add Transport</div>
                <button aria-label="Close" disabled={savingTransport} onClick={() => setTransportAdd(null)} className="p-2 text-app-sub/40 hover:text-gold transition-colors">
                  <X className="w-6 h-6" />
                </button>
              </div>
              {transportAdd.step === 'choose' ? (
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setTransportAdd({ ...transportAdd, step: 'form' })}
                    className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-gold/30 bg-gold/5 rounded-2xl text-[11px] font-black uppercase tracking-widest text-gold transition active:scale-[0.98]"
                  >
                    <Plus className="w-5 h-5" /> Add
                  </button>
                  <button
                    onClick={() => { setTransportAdd(null); onOpenImport?.(); }}
                    className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-gold/30 bg-gold/5 rounded-2xl text-[11px] font-black uppercase tracking-widest text-gold transition active:scale-[0.98]"
                  >
                    <Download className="w-5 h-5" /> Import
                  </button>
                </div>
              ) : (
                <TransportForm
                  isNew
                  transports={allTransports}
                  transport={{
                    mode: 'Flight', isRental: false,
                    icon: '✈️', color: '#c5a059', from: '', to: '', carrier: '', dep: '', arr: '',
                    flightNum: '', notes: '', ref: '', terminal: '', confirmed: false, contributedBy: currentUser.id,
                  } as Transport}
                  onSave={(t) => handleAddTransport(transportAdd.dayDate, t)}
                  onSavingChange={setSavingTransport}
                  onCancel={() => setTransportAdd(null)}
                  dayDate={transportAdd.dayDate}
                  tripConfig={tripConfig}
                  currentUser={currentUser}
                />
              )}
            </motion.div>
          </motion.div>,
          document.getElementById('import-modal-root') ?? document.body
      )}

      {viewAccom && createPortal(
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => { if (!savingAccommodation) { setViewAccom(null); setEditingAccom(false); } }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-app-bg/80 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-app-bg border border-app-border rounded-3xl max-w-xl w-full max-h-[85vh] overflow-hidden shadow-2xl flex flex-col"
            >
              {!editingAccom && hasBookingDetails(viewAccom) && (
                <BookingBanner targetId="accommodation-booking-details" />
              )}
              <div className="p-6 overflow-y-auto overflow-x-hidden min-h-0">
              <div className="flex justify-between items-center mb-6">
                <By userId={viewAccom.contributedBy || ""} />
                <div className="flex items-center gap-1">
                  {!editingAccom && (
                    <>
                    <button aria-label="Edit"
                      onClick={() => setEditingAccom(true)}
                      className="p-2 text-app-sub/40 hover:text-gold transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {(isAdmin || viewAccom.contributedBy === currentUser.id) && <button
                      disabled={savingAccommodation}
                      onClick={() => setConfirmRemoveAccom(viewAccom)}
                      className="p-2 text-app-sub/40 hover:text-rose-500 transition-colors"
                      title="Delete"
                      aria-label="Delete this accommodation"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>}
                    </>
                  )}
                  <button aria-label="Close" disabled={savingAccommodation} onClick={() => { setViewAccom(null); setEditingAccom(false); }} className="p-2 text-app-sub/40 hover:text-gold transition-colors">
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
              {editingAccom && !isAdmin && viewAccom.contributedBy !== currentUser.id ? (
                <ItemParticipationForm key={viewAccom.id} scopeId={tripConfig.id}
                  type={viewAccomDayDate ? 'day-accommodation' : 'accommodation'} item={viewAccom} title={viewAccom.name || 'Accommodation'}
                  date={viewAccomDayDate} userId={currentUser.id} onSavingChange={setSavingAccommodation}
                  onClose={() => { setViewAccom(null); setEditingAccom(false); }} />
              ) : editingAccom ? (
                <AccommodationForm
                  accommodation={viewAccom}
                  onSavingChange={setSavingAccommodation}
                  onSave={handleEditAccommodation}
                  onCancel={() => setEditingAccom(false)}
                  tripConfig={tripConfig}
                  currentUser={currentUser}
                />
              ) : (
                <>
                  <AccommodationSection
                    accommodation={viewAccom}
                    accent="#c5a059"
                    expenses={tripConfig.expenses}
                    travellers={tripConfig.travellers}
                    bookingSectionId="accommodation-booking-details"
                  >
                  </AccommodationSection>
                </>
              )}
              </div>
            </motion.div>
          </motion.div>,
          document.getElementById('import-modal-root') ?? document.body
      )}

      {/* Sub-portals over the view modals above, same as every other delete in
          the app. Deleting a booking unlinks its expense in the SAME
          transaction (writeTransportBooking / writeAccommodationBooking flip
          itineraryDeleted), so the message says where the money goes rather
          than leaving the traveller to wonder. */}
      {confirmRemoveAccom && (
        <ConfirmDeleteModal
          title="Delete this accommodation?"
          message={`"${confirmRemoveAccom.name || 'This stay'}" will be removed from your itinerary.${confirmRemoveAccom.expenseLogId ? ' Its logged expense stays in the Expenses tab, marked as no longer linked to an itinerary item.' : ''}`}
          onCancel={() => setConfirmRemoveAccom(null)}
          onConfirm={() => handleRemoveAccommodation(confirmRemoveAccom)}
        />
      )}
      {confirmRemoveTransport && (
        <ConfirmDeleteModal
          title="Delete this transport?"
          message={`This ${(confirmRemoveTransport.mode || 'booking').toLowerCase()} will be removed from your itinerary.${confirmRemoveTransport.expenseLogId ? ' Its logged expense stays in the Expenses tab, marked as no longer linked to an itinerary item.' : ''}`}
          onCancel={() => setConfirmRemoveTransport(null)}
          onConfirm={() => handleRemoveTransport(confirmRemoveTransport._dayDate!, confirmRemoveTransport)}
        />
      )}

      {viewTransport && createPortal(
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => { if (!savingTransport) { setViewTransport(null); setEditingTransport(false); } }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-app-bg/80 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-app-bg border border-app-border rounded-3xl max-w-xl w-full max-h-[85vh] overflow-hidden shadow-2xl flex flex-col"
            >
              {!editingTransport && (viewJourney.length ? viewJourney : [viewTransport]).some(hasBookingDetails) && (
                <BookingBanner targetId="transport-booking-details" />
              )}
              <div className="p-6 overflow-y-auto overflow-x-hidden min-h-0">
              <div className="flex justify-between items-center mb-6">
                <By userId={viewTransport.contributedBy || ""} />
                <div className="flex items-center gap-1">
                  {!editingTransport && viewJourney.length === 1 && (
                    <>
                    <button aria-label="Edit"
                      onClick={() => setEditingTransport(true)}
                      className="p-2 text-app-sub/40 hover:text-gold transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {(isAdmin || viewTransport.contributedBy === currentUser.id) && <button
                      disabled={savingTransport}
                      onClick={() => setConfirmRemoveTransport(viewTransport)}
                      className="p-2 text-app-sub/40 hover:text-rose-500 transition-colors"
                      title="Delete"
                      aria-label="Delete this transport"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>}
                    </>
                  )}
                  <button aria-label="Close" disabled={savingTransport} onClick={() => { setViewTransport(null); setEditingTransport(false); }} className="p-2 text-app-sub/40 hover:text-gold transition-colors">
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
              {editingTransport && !isAdmin && viewTransport.contributedBy !== currentUser.id ? (
                <ItemParticipationForm key={viewTransport.id} scopeId={tripConfig.id} type="transport" item={viewTransport}
                  title={[viewTransport.carrier || viewTransport.mode, viewTransport.from, viewTransport.to].filter(Boolean).join(' · ')}
                  date={viewTransport._dayDate} userId={currentUser.id} onSavingChange={setSavingTransport}
                  onClose={() => { setViewTransport(null); setEditingTransport(false); }} />
              ) : editingTransport ? (
                <TransportForm
                  transport={viewTransport}
                  transports={allTransports}
                  onSave={(t, previous) => handleEditTransport(viewTransport._dayDate!, t, previous)}
                  onSavingChange={setSavingTransport}
                  onCancel={() => setEditingTransport(false)}
                  dayDate={viewTransport._dayDate}
                  tripConfig={tripConfig}
                  currentUser={currentUser}
                />
              ) : viewJourney.length > 1 ? (
                // One ticket, several legs: shown together, the layover between
                // each pair called out, each leg editable on its own.
                <div className="space-y-5">
                  <div className="ds-label text-gold">
                    {viewJourney[0].fromCity || viewJourney[0].from} → {viewJourney.at(-1)!.toCity || viewJourney.at(-1)!.to} · {viewJourney.length} legs
                  </div>
                  {viewJourney.map((leg, i) => {
                    const layover = i > 0 ? layoverMinutes(viewJourney[i - 1], leg) : null;
                    return (
                      <div key={leg.id || i} className="space-y-5">
                        {i > 0 && (
                          <div className="flex items-center gap-3 text-sm text-gold">
                            <div className="flex-1 h-px bg-app-border/40" />
                            <span className="min-w-0 text-center">
                              <strong>Layover in {viewJourney[i - 1].toCity || leg.fromCity || viewJourney[i - 1].to}</strong> · {layover != null ? formatMinutes(layover) : 'TBC'}
                            </span>
                            <div className="flex-1 h-px bg-app-border/40" />
                          </div>
                        )}
                        <div className="flex justify-end gap-1">
                          <button onClick={() => { setViewTransport(leg); setEditingTransport(true); }}
                            aria-label={`Edit the ${leg.fromCode || leg.from} to ${leg.toCode || leg.to} leg`} title="Edit leg"
                            className="p-2 text-app-sub/40 hover:text-gold transition-colors">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {(isAdmin || leg.contributedBy === currentUser.id) && <button disabled={savingTransport} onClick={() => setConfirmRemoveTransport(leg)}
                            aria-label={`Delete the ${leg.fromCode || leg.from} to ${leg.toCode || leg.to} leg`}
                            title="Delete leg" className="p-2 text-app-sub/40 hover:text-rose-500 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>}
                        </div>
                        <TransportDetail t={leg} expenses={tripConfig.expenses} travellers={tripConfig.travellers} hideCosts />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <TransportDetail
                  t={viewTransport}
                  expenses={tripConfig.expenses}
                  travellers={tripConfig.travellers}
                  hideCosts
                  bookingSectionId="transport-booking-details"
                >{transportBookingContent}</TransportDetail>
              )}
              {!editingTransport && viewJourney.length > 1 && transportBookingContent && <ItemDetailsSection id="transport-booking-details" label="Booking & payment">{transportBookingContent}</ItemDetailsSection>}
              </div>
            </motion.div>
          </motion.div>,
          document.getElementById('import-modal-root') ?? document.body
      )}

      <div key={branchTimeline?.key} style={branchTimeline ? { '--branch-slide': `${branchTimeline.direction * 60}px` } as React.CSSProperties : undefined} className={`space-y-0 relative px-0 ${branchTimeline ? 'itinerary-branch-route' : ''}`}>
          <div className={branchTimeline?.isBranch && !branchTimeline.personalSegment ? `itinerary-core-branch-line ${branchTimeline.returns ? 'returning' : ''}` : 'itinerary-core-line'} />
          {branchTimeline?.entry}
          
          {cityGroups.flatMap((group, idx) => {
            const isGap = group.destinationId === 'gap';
            const displayCity = group.city;

            // Determine if the current user has been explicitly excluded from this destination.
            // day.travellers undefined / empty = all accepted members included (default).
            const firstGroupDay = !isGap
              // By start date, not city: a return visit is its own group.
              ? itineraryDays.find(d => d.date === group.startDateISO)
              : undefined;
            const explicitTravellers = firstGroupDay?.travellers;
            const isExcluded = Array.isArray(explicitTravellers) &&
              explicitTravellers.length > 0 &&
              !explicitTravellers.includes(currentUser.id);
            const isCurrentDest = idx === currentGroupIdx;

            const rows: React.ReactNode[] = [];

            // Leading transport row, before the very first card — tied to the trip's
            // own start date, regardless of whether that first card is a destination
            // or an unfilled gap.
            if (idx === 0 && tripConfig.startDate) {
              rows.push(renderTransportGap('transport-start', tripConfig.startDate, null, tripConfig.startDate));
            }

            // Between-card transport row — only when this card AND the previous one
            // are both real destinations (a gap between them means the dates for
            // that stretch aren't even set yet, so there's nothing to transit between).
            if (idx > 0 && !isGap) {
              const prevGroup = cityGroups[idx - 1];
              if (prevGroup.destinationId !== 'gap' && prevGroup.destIdx !== undefined && group.destIdx !== undefined) {
                const prevDest = tripConfig.destinations[prevGroup.destIdx];
                const nextDest = tripConfig.destinations[group.destIdx];
                if (prevDest && nextDest) {
                  rows.push(renderTransportGap(`transport-${idx}`, prevDest.departureDate, prevDest.departureDate, nextDest.arrivalDate, nextDest));
                }
              }
            }

            // A transport row follows this card whenever the next card is also a
            // real destination (see the condition above, mirrored here one card
            // earlier), OR this is the last card and a trailing transport row is
            // about to be pushed after it (same condition as that push, below) —
            // in either case the transport row itself owns all the vertical
            // spacing (symmetric py-6) so its text sits centred between the two
            // cards, rather than being pushed down by this card's own bottom
            // padding stacking on top of it.
            const nextGroup = cityGroups[idx + 1];
            const isLastGroup = idx === cityGroups.length - 1;
            const followedByTransportRow = !isGap && (
              (nextGroup && nextGroup.destinationId !== 'gap') ||
              (isLastGroup && !!tripConfig.endDate)
            );

            const branchDestination = group.destIdx == null ? undefined : tripConfig.destinations[group.destIdx];
            if (branchDestination) rows.push(branchTimeline?.before(branchDestination));
            rows.push(
              <div key={idx} data-itinerary-visit={branchDestination ? destinationKey(branchDestination) : undefined} className={`flex gap-6 ${followedByTransportRow ? 'pb-0' : 'pb-12'} last:pb-0 relative group items-center text-left`}>
                {/* gap-2: the ring sits on the circle's own edge now (the
                    4px offset that used to hold it clear read as a far
                    heavier mark than the 1px border it replaced), so this is
                    plain breathing room rather than clearance for a ring
                    floating outside the box. */}
                <div className="w-12 flex flex-col items-center gap-2 shrink-0">
                  {/* surface-solid on the circle too: its ring reads as an
                      edge, so anything showing through it looks like a
                      mistake rather than depth. */}
                  <div className={`surface-solid w-12 h-12 rounded-full flex items-center justify-center text-xl z-20 transition-transform shadow-sm relative overflow-visible shrink-0 ${isGap ? 'bg-app-faint border border-dashed border-app-border ring-0' : isExcluded ? 'bg-gray-100 ring-1 ring-gray-200' : isCurrentDest ? 'bg-app-surface ring-1 ring-gold' : 'bg-app-surface ring-1 ring-app-border/60'}`}>
                     {!isGap && <div className="absolute inset-0 bg-gold/5 opacity-0 group-hover:opacity-100 transition-opacity" />}
                    {isGap ? null : (
                      <div className="relative">
                        <button
                          onClick={() => {
                            if (!branchTimeline?.personalSegment && (isAdmin || branchTimeline?.preview) && group.destIdx !== undefined) {
                              setEditingEmojiIdx(editingEmojiIdx === group.destIdx ? null : group.destIdx);
                            }
                          }}
                          disabled={branchTimeline?.personalSegment || (!isAdmin && !branchTimeline?.preview)}
                          className={`${isAdmin || branchTimeline?.preview ? 'cursor-pointer hover:scale-125 transition-transform' : 'cursor-default'} ${isExcluded ? 'grayscale opacity-40' : ''}`}
                        >
                          {renderEmojiOrIcon(group.icon as string, 24)}
                        </button>

                        <AnimatePresence>
                          {editingEmojiIdx === group.destIdx && (
                            <EmojiPicker
                              currentEmoji={group.icon as string}
                              onSelect={(emoji) => updateDestinationEmoji(group.destIdx!, emoji)}
                              onClose={() => setEditingEmojiIdx(null)}
                            />
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </div>
                  {isCurrentDest && !isExcluded && (
                    <div className="px-2 py-0.5 rounded-full bg-gold text-white text-[10px] font-black uppercase tracking-wider leading-none z-20 shadow-sm">
                      Today
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  {isGap ? (
                    <button
                      onClick={() => {
                        setManagementMode('destination');
                        setHighlightDestIdx(null);
                        setNewDestDates({
                          arrivalDate: group.startDateISO || '',
                          departureDate: group.endDateISO || '',
                        });
                      }}
                      className="group/add w-full flex flex-col items-start gap-1 px-5 py-4 rounded-2xl bg-gold/5 border-2 border-dashed border-gold/30 transition text-left active:scale-[0.98]"
                    >
                      <span className="text-sm font-bold text-gold flex items-center gap-2">
                        <Plus className="w-4 h-4" /> Add destinations
                      </span>
                      <span className="text-sm font-semibold tracking-wide text-gold/80">
                        {group.range}
                      </span>
                    </button>
                  ) : (
                    <ItineraryDestinationCard
                      id={group.destIdx !== undefined ? `itinerary-dest-${group.destIdx}` : undefined}
                      excluded={isExcluded}
                    >
                      {!branchTimeline?.personalSegment && (branchTimeline?.isBranch ? branchTimeline.canEditDestination : isAdmin) && (
                        <button
                          onClick={() => {
                            if (branchTimeline?.isBranch && branchDestination) { branchTimeline.editDestination(branchDestination); return; }
                            if (group.destIdx !== undefined) {
                              setManagementMode('destination');
                              setHighlightDestIdx(group.destIdx);
                              setNewDestDates(null);
                            } else {
                              // The itinerary day couldn't be matched back to a destination
                              // (slug lookup miss — can happen if the destination was just
                              // renamed and the day cache hasn't caught up). Fall back to the
                              // full list rather than the button silently doing nothing.
                              console.warn('Could not resolve destIdx for destination card:', group);
                              setManagementMode('destinations');
                            }
                          }}
                          className="tap-target absolute top-4 right-4 w-8 h-8 bg-app-bg border border-app-border rounded-xl flex items-center justify-center text-app-sub hover:bg-gold hover:text-white hover:border-gold transition shadow-sm cursor-pointer"
                          aria-label="Edit destination"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <h3 className={`font-serif text-2xl font-bold tracking-tight leading-none pr-10 ${isExcluded ? 'text-gray-400' : 'text-app-text'}`}>
                        {displayCity} {group.via && <span className="text-sm font-sans font-normal text-app-sub/60 ml-2">(via {group.via})</span>}
                      </h3>
                      <div className={`flex items-center gap-2 text-[11px] font-black tracking-widest uppercase ${isExcluded ? 'text-gray-400' : 'text-app-sub'}`}>
                        <span className="text-xl leading-none">{group.flag}</span>
                        <span>{group.country}</span>
                      </div>
                      <button
                        disabled={branchTimeline?.isBranch}
                        onClick={() => {
                          const found = itineraryDays.find(d => d.date === group.startDateISO);
                          if (found) onSelectDay(found);
                        }}
                        className={`flex flex-wrap items-center gap-y-1 text-sm font-semibold tracking-wide text-left transition-all group/btn ${isExcluded ? 'text-gray-400' : 'text-app-sub hover:text-gold'}`}
                      >
                        <span className="whitespace-nowrap">{group.range}</span>
                        <span className={`font-normal tracking-normal whitespace-nowrap ${isExcluded ? 'text-gray-400' : 'text-app-sub/60 group-hover/btn:text-gold/70'}`}>
                          <span className="mx-1.5">|</span>
                          {group.nights} {group.nights === 1 ? 'night' : 'nights'}
                        </span>
                      </button>

                      {branchDestination && branchTimeline?.during?.(branchDestination)}
                      {!branchTimeline?.personalSegment && group.destIdx !== undefined && <TripParticipationNotice trip={tripConfig}
                        visitId={destinationKey(tripConfig.destinations[group.destIdx])} />}

                      {group.destIdx !== undefined && !isExcluded && (() => {
                        const destIdx = group.destIdx!;
                        const dest = tripConfig.destinations[destIdx];
                        if (!dest) return null;
                        const accs = accsForDest(dest, tripConfig.accommodations || []);
                        const gaps = accommodationGaps(dest, accs);
                        return (
                          <div className="pt-3 mt-1 border-t border-app-border/10 space-y-2">
                            {/* Saved stays and the nights still missing, in date order —
                                a gap before the first booking reads before it, not
                                after every saved stay. */}
                            {[
                              ...accs.map(acc => ({ date: (acc.checkIn || '').slice(0, 10), acc, gap: null as null | (typeof gaps)[number] })),
                              ...gaps.map(gap => ({ date: gap.start, acc: null as null | (typeof accs)[number], gap })),
                            ].sort((a, b) => a.date.localeCompare(b.date)).map((entry, ei) => entry.acc ? (() => { const acc = entry.acc; return (
                              <button
                                key={acc.id}
                                onClick={() => { setViewAccomDayDate(undefined); setViewAccom(acc); }}
                                className="relative w-full flex items-center gap-3 pl-3 pr-5 py-2.5 bg-app-faint/40 rounded-xl overflow-hidden text-left hover:bg-app-faint/70 transition-colors"
                              >
                                <BedDouble className="w-5 h-5 text-app-sub/40 shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <div className="text-[13px] font-black text-app-text truncate">{acc.name}</div>
                                  {(accs.length > 1 || acc.checkIn.slice(0, 10) !== dest.arrivalDate || acc.checkOut.slice(0, 10) !== dest.departureDate) && (
                                    <div className="text-sm font-normal text-app-sub mt-0.5">
                                      {shortDateLabel(acc.checkIn.slice(0, 10))}–{shortDateLabel(acc.checkOut.slice(0, 10))} · {nightsBetween(acc.checkIn, acc.checkOut)} night{nightsBetween(acc.checkIn, acc.checkOut) !== 1 ? 's' : ''}
                                    </div>
                                  )}
                                </div>
                                <div
                                  className="absolute right-0 top-0 bottom-0 w-2 rounded-r-xl"
                                  style={{ background: destAccentColor(chronoColorIdx[destIdx] ?? destIdx) }}
                                />
                              </button>
                            ); })() : (
                              <div key={`gap-${ei}`}>
                                {renderAccomAdder(dest, { start: entry.gap!.start, end: entry.gap!.end }, { nights: entry.gap!.nights, hasExisting: accs.length > 0 })}
                              </div>
                            ))}
                            {allTransports.filter(t => transportDuringVisit(t, dest, allTransports)).map((t, i) => {
                              const title = t.carrier?.trim() || [t.fromCity || t.from, t.toCity || t.to].filter(Boolean).join(' → ') || 'Transport';
                              return <button
                              key={t.id || `rental-${i}`} onClick={() => { setViewTransport(t); setEditingTransport(false); }}
                              className="relative w-full flex items-center gap-3 pl-3 pr-5 py-2.5 bg-app-faint/40 rounded-xl overflow-hidden text-left hover:bg-app-faint/70 transition-colors">
                              <SubcategoryIcon name={t.mode} l1="transport" className="w-5 h-5 text-app-sub/40 shrink-0" />
                              <span className="min-w-0 flex-1"><span className="block text-[13px] font-black text-app-text truncate" title={title}>{title}</span>
                                <span className="block text-sm text-app-sub">{shortDateLabel((t.dep || t._dayDate).slice(0, 10))}{t.isRental ? ` – ${shortDateLabel((t.arr || t.dep || t._dayDate).slice(0, 10))}` : t.dep?.includes('T') ? ` · ${formatClock(t.dep)}` : ''}</span></span>
                              <span className="absolute right-0 top-0 bottom-0 w-2 rounded-r-xl" style={{ background: destAccentColor(chronoColorIdx[destIdx] ?? destIdx) }} />
                            </button>;
                            })}
                          </div>
                        );
                      })()}
                    </ItineraryDestinationCard>
                  )}
                </div>
              </div>
            );

            if (branchDestination) rows.push(branchTimeline?.after(branchDestination));
            // Trailing transport row, after the very last card — tied to the trip's
            // own end date.
            if (idx === cityGroups.length - 1 && tripConfig.endDate) {
              rows.push(renderTransportGap('transport-end', tripConfig.endDate, tripConfig.endDate, null));
            }

            return rows;
          })}
          {branchTimeline?.exit}
          {branchTimeline?.isBranch && (tripConfig.accommodations || []).filter(a=>!tripConfig.destinations.some(d=>accsForDest(d,[a]).length)).map(a=><button key={a.id}
            onClick={()=>{setViewAccomDayDate(undefined);setViewAccom(a);setEditingAccom(false);}}
            className="w-full flex items-center gap-3 p-3 rounded-xl bg-app-faint/40 text-left mt-4">
            <BedDouble className="w-5 h-5 text-app-sub/40 shrink-0" />
            <span className="min-w-0"><span className="block text-sm font-semibold">{a.name}</span><span className="block text-sm text-app-sub">Outside current destination dates</span></span>
          </button>)}
          {unplacedTransports.length > 0 && <section className="pt-6 space-y-2">
            <SLabel>Other saved transport</SLabel>
            <p className="text-sm text-app-sub">Outside destination changeover dates</p>
            {unplacedTransports.map((t, i) => <button key={t.id || `${t._dayDate}-${i}`}
              onClick={() => { setViewTransport(t); setEditingTransport(false); }}
              className="w-full flex items-center gap-3 p-3 rounded-xl bg-app-faint/40 text-left">
              <SubcategoryIcon name={t.mode} l1="transport" className="w-5 h-5 text-app-sub/40 shrink-0" />
              <span className="min-w-0"><span className="block text-sm font-semibold">{t.isRental ? `Car hire${t.carrier ? ` · ${t.carrier}` : ''}` : `${t.carrier || t.mode} · ${t.fromCity || t.from} → ${t.toCity || t.to}`}</span>
                <span className="block text-sm text-app-sub">{shortDateLabel(transportDateOf(t))}</span></span>
            </button>)}
          </section>}
        </div>
    </div>
      </div>
      {showGapsModal && (
        <ItineraryGapsModal insights={plannerInsights} onClose={() => setShowGapsModal(false)} />
      )}
  </div>
);
}

/**
 * What each of this component's three tabs says when it has nothing to show.
 * Shared by two callers that must stay identical: the no-trip card (CTA
 * creates a trip) and the one-time intro card shown once a trip exists (CTA
 * dismisses it).
 */
export const TAB_INTRO_COPY: Record<'logistics' | 'bucketlist' | 'dayplanner', { icon: React.ReactNode; title: string; body: string }> = {
  logistics:  { icon: <Route className="w-9 h-9" />,
                title: 'Your route, start to finish',
                body: 'Align destinations, accommodation and transport to form the backbone of your trip' },
  bucketlist: { icon: <Star className="w-9 h-9" />,
                title: 'Ideas worth keeping',
                body: "Capture inspiration for things to do and see, so you don't miss a thing" },
  dayplanner: { icon: <CalendarClock className="w-9 h-9" />,
                title: 'Your days, hour by hour',
                body: 'Take the stress out of planning with day-by-day schedules that match your travel style' },
};

type RouteShortlist = {
  scroller: React.ReactNode; focusId: string; hasPrev: boolean; hasNext: boolean; go: (dir: 'prev' | 'next') => void;
  /** Schedule: this visit's days and same-scope neighbours (routeScheduleVisit), the route-wide date strip. */
  dates: { date: string; label: string; dow: string }[]; prev?: Destination; next?: Destination;
  visit: Destination; arrivalFrom?: Destination;
  dateItems: DateScrollerItem[]; selectDate: (date: string) => void; focusDate?: string;
};

/** The Shortlist across one traveller's continuous route. Each visit renders
 *  from its own source (main trip or private branch), so reads, writes and
 *  linked expenses stay in that scope — nothing is flattened into the root. */
function PersonalShortlist({ route, routeError, ...props }: React.ComponentProps<typeof ItineraryTabContent> & { route: PersonalHomeRoute | null; routeError?: string }) {
  const [picked, setPicked] = useState<number | null>(null);
  const [pickedDate, setPickedDate] = useState<string>();
  const visits = route?.visits ?? [];
  const pendingItem = peekPendingNav('nav-itinerary-day');
  const requestedIdx = pendingItem?.startsWith('itinerary-')
    ? routeVisitIndex(visits, { itemId: pendingItem.slice('itinerary-'.length) }) : -1;
  const idx = requestedIdx >= 0 ? requestedIdx
    : Math.min(visits.length - 1, picked ?? Math.max(0, routeVisitIndex(visits, { date: localDateKey() })));
  const current = visits[idx];
  useEffect(() => {
    if (requestedIdx >= 0) setPicked(requestedIdx);
  }, [requestedIdx]);
  const visitsRef = useRef(visits);
  visitsRef.current = visits;
  // A deep link to an item in another scope (Home, notifications) moves the
  // route to the visit that owns it; that visit's own instance then opens it.
  useEffect(() => {
    const onNav = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (typeof detail !== 'string' || !detail.startsWith('itinerary-')) return;
      const found = routeVisitIndex(visitsRef.current, { itemId: detail.slice('itinerary-'.length) });
      if (found >= 0) setPicked(found);
    };
    // Home's Schedule links carry a date, since root day indexes mean nothing here.
    const onDate = (e: Event) => {
      const date = (e as CustomEvent).detail;
      const found = typeof date === 'string' ? routeVisitIndex(visitsRef.current, { date }) : -1;
      if (found >= 0) { setPicked(found); setPickedDate(date); }
    };
    window.addEventListener('nav-itinerary-day', onNav);
    window.addEventListener('nav-schedule-date', onDate);
    return () => { window.removeEventListener('nav-itinerary-day', onNav); window.removeEventListener('nav-schedule-date', onDate); };
  }, []);
  if (route === null) return <p role="status" className="py-10 text-center text-app-sub">{routeError || 'Loading your itinerary…'}</p>;
  if (!current) return <ItineraryTabContent {...props} />;
  const isRoot = current.source.id === props.tripConfig?.id;
  const schedule = routeScheduleVisit(visits, idx);
  const step = (to: number) => { setPicked(Math.max(0, Math.min(visits.length - 1, to))); setPickedDate(undefined); };
  return <ExpenseScope.Provider value={current.source.id}>
    <ItineraryTabContent key={current.source.id} {...props} tripConfig={current.source}
      // Private stop details are edited by the branch organiser in Itinerary.
      onUpdateConfig={isRoot ? props.onUpdateConfig : async () => false}
      routeShortlist={{
        scroller: <DestinationScroller destinations={visits.map(v => v.visit)} selectedIdx={idx} onSelect={step} />,
        focusId: destinationKey(current.sourceVisit), hasPrev: idx > 0, hasNext: idx < visits.length - 1,
        go: dir => step(idx + (dir === 'prev' ? -1 : 1)),
        ...schedule, focusDate: pickedDate,
        // ponytail: no transport badges in the route strip; add when travellers miss them.
        dateItems: visits.flatMap((_, i) => routeScheduleVisit(visits, i).dates).map(d => ({ type: 'date' as const, ...d, isGap: false })),
        selectDate: date => { const found = routeVisitIndex(visits, { date }); if (found >= 0) { setPicked(found); setPickedDate(date); } },
      }} />
  </ExpenseScope.Provider>;
}

export function ItineraryTab(props: React.ComponentProps<typeof ItineraryTabContent> & { branchWorkspace?: BranchWorkspaceData; personalRoute?: PersonalHomeRoute | null; routeError?: string }) {
  const { personalRoute, routeError, branchWorkspace: _workspace, ...content } = props;
  if (personalRoute !== undefined && props.tripConfig && !props.branchWorkspace) {
    return <PersonalShortlist {...content} route={personalRoute} routeError={routeError} />;
  }
  if (props.branchWorkspace && props.tripConfig) {
    return <ItineraryBranchWorkspace key={`${props.tripConfig.id}:${props.user.id}`} trip={props.tripConfig} user={props.user} workspace={props.branchWorkspace}>
      {(config, timeline) => <ExpenseScope.Provider value={config.id}><ItineraryTabContent key={config.id} {...props} tripConfig={config} onUpdateConfig={timeline.saveConfig} branchTimeline={timeline} /></ExpenseScope.Provider>}
    </ItineraryBranchWorkspace>;
  }
  return <ExpenseScope.Provider value={props.tripConfig?.id}><ItineraryTabContent {...props} /></ExpenseScope.Provider>;
}

function ItineraryTabContent({
  branchTimeline,
  routeShortlist,
  user,
  tripConfig,
  onUpdateConfig,
  initialManageState,
  onOpenChange,
  autoOpenCreate,
  onConsumeOpenCreate,
  onCancelCreate,
  onAddTrip,
  onTripCreated,
  trips,
  activeTripId,
  onSelectTrip,
  onDeleteTrip,
  view,
  onViewChange,
  detailMode,
  onDetailModeChange,
}: {
  branchTimeline?: BranchTimelineContext;
  /** Set by PersonalShortlist: the route-wide scroller and prev/next replace this trip's own. */
  routeShortlist?: RouteShortlist;
  user: User;
  tripConfig: TripConfig | null;
  onUpdateConfig: (c: TripConfig) => Promise<boolean>;
  initialManageState?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** When true, open the new-trip creation form immediately (set by addNewTrip only). */
  autoOpenCreate?: boolean;
  /** Reset the autoOpenCreate flag once the form has been opened. */
  onConsumeOpenCreate?: () => void;
  /** Called when the new-trip creation form is dismissed without saving. */
  onCancelCreate?: () => void;
  onAddTrip: () => void;
  onTripCreated?: (tripId: string) => void;
  /** All the user's trips + selection handlers — only used to render the
   *  "Your Trip" switcher carousel at the top of the overview (Itinerary
   *  tab); passed straight through to TripOverview. */
  trips: TripConfig[];
  activeTripId: string;
  onSelectTrip: (id: string) => void;
  onDeleteTrip: (id: string) => void;
  view: 'overview' | 'day';
  onViewChange: (v: 'overview' | 'day') => void;
  detailMode: 'shortlists' | 'dayPlanner';
  onDetailModeChange: (m: 'shortlists' | 'dayPlanner') => void;
}) {
  // view / setView come from parent — alias so internal call-sites need no changes
  const setView = onViewChange;
  const [savingConfig, setSavingConfig] = useState(false);
  const [managementMode, setManagementMode] = useState<'info' | 'destinations' | 'destination' | 'members' | 'full' | null>(initialManageState ? 'full' : null);
  const [highlightDestIdx, setHighlightDestIdx] = useState<number | null>(null);
  // Set whenever something wants the single-destination "Add destination"
  // panel opened pre-filled with a specific gap's dates, instead of the full
  // multi-destination list — the "Add destinations" gap block on the
  // Itinerary tab itself, the Itinerary Gaps modal's destination insight,
  // Home's "Add destination" CTA (nav-itinerary-overview), and DayDetail's
  // empty-day "Add a destination" button (MANAGE_TRIP_TRIGGER) all funnel
  // through this so gap-filling and editing share one menu.
  const [newDestDates, setNewDestDates] = useState<{ arrivalDate: string; departureDate: string } | null>(null);
  const isManaging = !!managementMode;
  const [draftConfig, setDraftConfig] = useState<TripConfig | null>(null);
  // Lazy initializer: if this component MOUNTS with autoOpenCreate=true and a
  // tripConfig already set (pendingTrip), seed creatingTrip on the very first
  // render — before any useEffect can run and clear it.
  const suppressTripIdResetRef = useRef<boolean>(!!(autoOpenCreate && tripConfig));
  // Isolated state for new-trip creation — never touches draftConfig / effectiveConfig
  const [creatingTrip, setCreatingTrip] = useState<TripConfig | null>(
    () => (autoOpenCreate && tripConfig ? { ...tripConfig } : null)
  );
  const [wizardStep, setWizardStep] = useState<'dates' | 'destinations' | 'travellers'>('dates');
  const wizardStepOrder = ['dates', 'destinations', 'travellers'] as const;
  const wizardStepTitle: Record<typeof wizardStepOrder[number], string> = {
    dates: "What\u2019s the trip, and when?",
    destinations: "Where are you going?",
    travellers: "Who are you travelling with?",
  };
  const [showImportModal, setShowImportModal] = useState(false);
  const [showItineraryImportModal, setShowItineraryImportModal] = useState(false);
  const [preloadedFiles, setPreloadedFiles] = useState<File[]>([]);
  const isAdmin = isTripAdmin(tripConfig, user.id);

  useEffect(() => {
    if (isManaging && tripConfig && !draftConfig) {
      setDraftConfig(tripConfig);
    } else if (!isManaging) {
      setDraftConfig(null);
    }
  }, [isManaging, tripConfig]);


  const hasTrip = !!tripConfig && tripConfig.title.trim() !== "";

  // Reset activeDayIdx if it's out of bounds when itineraryDays changes
  const effectiveConfig = (isManaging && draftConfig) ? draftConfig : tripConfig;
  
  // Calculate expanded days for preview - include all destination dates even if outside trip range
  const itineraryDays = React.useMemo(() => {
    if (!effectiveConfig) return [];
    
    if (isManaging && effectiveConfig) {
      let minDate = effectiveConfig.startDate ? new Date(effectiveConfig.startDate) : new Date();
      let maxDate = effectiveConfig.endDate ? new Date(effectiveConfig.endDate) : new Date();
      
      const hasValidStart = !isNaN(minDate.getTime());
      const hasValidEnd = !isNaN(maxDate.getTime());

      (effectiveConfig.destinations || []).forEach(d => {
        if (d.arrivalDate) {
          const arr = new Date(d.arrivalDate);
          if (!isNaN(arr.getTime())) {
            if (!hasValidStart || arr < minDate) {
              minDate = arr;
            }
          }
        }
        if (d.departureDate) {
          const dep = new Date(d.departureDate);
          if (!isNaN(dep.getTime())) {
            if (!hasValidEnd || dep > maxDate) {
              maxDate = dep;
            }
          }
        }
      });
      
      const configWithFullRange = {
        ...effectiveConfig,
        startDate: !isNaN(minDate.getTime()) ? minDate.toISOString().split('T')[0] : effectiveConfig.startDate,
        endDate: !isNaN(maxDate.getTime()) ? maxDate.toISOString().split('T')[0] : effectiveConfig.endDate
      };
      return getExpandedDays(configWithFullRange);
    }
    
    return getExpandedDays(effectiveConfig);
  }, [effectiveConfig, isManaging]);

  const [activeDayIdx, setActiveDayIdx] = useState(0);
  /**
   * Land on today the first time a tab is opened in a session.
   *
   * Day 1 is the right answer for a trip that hasn't started; it's the wrong
   * one the moment you're on it, when "today" is the only day you actually
   * want. Runs once per mount and only when today falls inside the trip, so
   * it can't fight a day the traveller has since chosen, and a past or future
   * trip still opens where it always did.
   */
  const startedOnToday = useRef(false);
  useEffect(() => {
    if (startedOnToday.current || itineraryDays.length === 0) return;
    startedOnToday.current = true;
    const today = localDateKey();
    const idx = itineraryDays.findIndex(d => d.date === today);
    if (idx > 0) setActiveDayIdx(idx);
  }, [itineraryDays]);

  const itinDaysRef = useRef(itineraryDays);
  const handleNavDayRef = useRef<((e: CustomEvent) => void) | null>(null);
  const junctionsInScope = React.useContext(JunctionContext);
  const routeShortlistRef = useRef(routeShortlist);
  routeShortlistRef.current = routeShortlist;
  useEffect(() => {
    itinDaysRef.current = itineraryDays;
  }, [itineraryDays]);

  // Finds the contiguous run of gap days (no destination) containing `date`,
  // resolving to the same {arrivalDate, departureDate} shape the "Add
  // destinations" gap block already uses — shared by handleNavOverview
  // (Home's "Add destination" CTA) and saveDayUpdate's MANAGE_TRIP_TRIGGER
  // (DayDetail's own empty-day button) so both open the focused
  // single-destination panel instead of the full list, same as the
  // Itinerary Gaps modal's destination insight.
  const findGapRange = (date: string): { arrivalDate: string; departureDate: string } | null => {
    const days = itinDaysRef.current;
    const idx = days.findIndex(d => d.date === date);
    if (idx === -1) return null;
    const isGap = (d: Day) => !d.city || d.city === "Gap in Itinerary";
    if (!isGap(days[idx])) return null;
    let start = idx, end = idx;
    while (start > 0 && isGap(days[start - 1])) start--;
    while (end < days.length - 1 && isGap(days[end + 1])) end++;
    return { arrivalDate: days[start].date, departureDate: days[end].date };
  };

  // When the active trip changes, reset local view state.
  // Creation form is now opened only via explicit autoOpenCreate signal, never
  // by inspecting tripConfig.title — that caused the form to pop open whenever
  // the Trips tab was navigated to while a pendingTrip happened to be in state.
  useEffect(() => {
    // The ref is pre-seeded (useRef init) when mounting with autoOpenCreate=true,
    // or set by the useLayoutEffect below when autoOpenCreate flips true later.
    // Either way: skip the reset on that first mount so we don't clear the form.
    if (suppressTripIdResetRef.current) {
      suppressTripIdResetRef.current = false;
      return;
    }
    // Today when the trip is under way, else day 1. This also runs on mount,
    // after the land-on-today effect above — a plain 0 here silently undid it.
    setActiveDayIdx(Math.max(0, itineraryDays.findIndex(d => d.date === localDateKey())));
    setDraftConfig(null);
    setCreatingTrip(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripConfig?.id]);

  // Explicit creation trigger — fires only when addNewTrip() sets autoOpenCreate.
  // useLayoutEffect (vs useEffect) fires synchronously after DOM mutations and
  // before the browser paints, so the creation overlay is applied in the same
  // frame — no visible flash of the Trips home screen when arriving from another tab.
  // Setting suppressTripIdResetRef here is visible to the tripConfig?.id useEffect
  // (which runs after paint) so it knows to skip its one-time mount reset.
  useLayoutEffect(() => {
    if (autoOpenCreate && tripConfig) {
      suppressTripIdResetRef.current = true;
      setCreatingTrip({ ...tripConfig });
      setView('overview');
      // Close any destination/members/etc panel left open from whatever trip
      // was active before — otherwise it stays mounted (referencing the old
      // trip's data) and renders on top of the new-trip flow, blank once its
      // index no longer resolves against the new trip's empty destinations.
      setManagementMode(null);
      setHighlightDestIdx(null);
      setWizardStep('dates');
      onConsumeOpenCreate?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenCreate]);

  useEffect(() => {
    if (initialManageState) {
      setManagementMode('full');
      setView('overview');
    }
  }, [initialManageState]);

  useEffect(() => {
    // Maps handleNavDay's internal section id to an ItemModalRequest type.
    const SECTION_TO_ITEM_TYPE: Record<string, ItemModalRequest['type']> = {
      'section-attractions': 'attraction',
      'section-eats': 'eat',
      'section-nightlife': 'nightlife',
      'section-shopping': 'shopping',
      'section-notes': 'note',
      'section-accommodations': 'accommodation',
      'section-transports': 'transport',
    };
    // Sections that live inside a specific day's DayDetail (Shortlist tab)
    // vs. trip-level sections shown on the Logistics/Itinerary tab.
    const DAY_LEVEL_SECTIONS = new Set(['section-attractions', 'section-eats', 'section-nightlife', 'section-shopping', 'section-notes']);

    const handleNavDay = (e: CustomEvent) => {
      const detail = e.detail;
      const parsedDetail = typeof detail === 'string' ? detail.split('-') : [];
      let idx = typeof detail === 'number' ? detail : parseInt(detail, 10);
      let activityId: string | null = null;
      let foundSection: string | null = null;

      if (parsedDetail.length >= 2 && parsedDetail[0] === 'itinerary') {
         activityId = parsedDetail.slice(1).join('-');
         let foundDayIdx = -1;
         itinDaysRef.current.forEach((day, index) => {
           if (day.attractions?.some(a => a.id === activityId)) { foundDayIdx = index; foundSection = 'section-attractions'; }
           else if (day.eats?.some(a => a.id === activityId)) { foundDayIdx = index; foundSection = 'section-eats'; }
           else if (day.nightlife?.some(a => a.id === activityId)) { foundDayIdx = index; foundSection = 'section-nightlife'; }
           else if (day.shopping?.some(a => a.id === activityId)) { foundDayIdx = index; foundSection = 'section-shopping'; }
           else if (day.accommodations?.some(a => a.id === activityId)) { foundDayIdx = index; foundSection = 'section-accommodations'; }
           else if (day.transports?.some(a => (a.id || a.flightNum || String(a.dep)) === activityId)) { foundDayIdx = index; foundSection = 'section-transports'; }
           else if (day.notes?.some(n => n.id === activityId)) { foundDayIdx = index; foundSection = 'section-notes'; }
         });
         idx = foundDayIdx;
      }

      if (typeof idx === 'number' && !isNaN(idx) && idx >= 0 && idx < itinDaysRef.current.length) {
        takePendingNav('nav-itinerary-day');
        const dayDate = itinDaysRef.current[idx].date;
        const itemType = foundSection ? SECTION_TO_ITEM_TYPE[foundSection] : undefined;

        if (itemType && activityId && DAY_LEVEL_SECTIONS.has(foundSection!)) {
          // Land on the right day in the Shortlist tab, then let DayDetail
          // mount with it before asking it to open the item's modal.
          window.dispatchEvent(new CustomEvent('nav-tab', { detail: { tab: 'bucketlist' } }));
          setActiveDayIdx(idx);
          setTimeout(() => {
            openItemModal({ type: itemType, itemId: activityId!, dayDate, mode: 'view' });
          }, 150);
        } else if (itemType && activityId) {
          // Transport / accommodation live on the Logistics/Itinerary tab —
          // TripOverview listens for this regardless of which day is active.
          setView('overview');
          setTimeout(() => {
            openItemModal({ type: itemType, itemId: activityId!, dayDate, mode: 'view' });
          }, 150);
        } else {
          setView('day');
          setActiveDayIdx(idx);
          // Keep Day Planner's own per-date state (selectedDestIdx is
          // bridged from activeDayIdx separately, just below) in sync too,
          // so a deep link lands on the same day regardless of which of the
          // two Plan-tab toggles you're sent to. Without this, Day Planner
          // fell back to the destination's *first* day instead of the one
          // actually being linked to.
          setActiveDate(dayDate);
        }
      } else if (!routeShortlistRef.current) {
        // Fallback: If item wasn't found or idx is invalid, go to overview.
        // On a personal route the item may belong to another visit's scope,
        // which PersonalShortlist switches to instead.
        setView('overview');
      }
    };
    
    // Home's "+ Update itinerary" pill (shown on a gap day) dispatches this
    // with today's date so we can resolve the specific gap and open the
    // focused single-destination "Add" panel directly — same target the
    // Itinerary Gaps modal's destination insight and the destination
    // timeline's own "Add destinations" block use. findGapRange also backs
    // saveDayUpdate's MANAGE_TRIP_TRIGGER branch (DayDetail's empty-day
    // button) below.
    const handleNavOverview = (e: CustomEvent) => {
      setView('overview');
      const date = typeof e.detail === 'string' ? e.detail : undefined;
      const range = date ? findGapRange(date) : null;
      if (range) {
        setManagementMode('destination');
        setHighlightDestIdx(null);
        setNewDestDates(range);
      } else {
        // No date passed, or it no longer resolves to a gap (already filled
        // by the time this fires) — fall back to the full list rather than
        // silently doing nothing.
        setManagementMode('destinations');
      }
    };

    handleNavDayRef.current = handleNavDay;
    window.addEventListener('nav-itinerary-day', handleNavDay as EventListener);
    window.addEventListener('nav-itinerary-overview', handleNavOverview as EventListener);

    return () => {
      window.removeEventListener('nav-itinerary-day', handleNavDay as EventListener);
      handleNavDayRef.current = null;
      window.removeEventListener('nav-itinerary-overview', handleNavOverview as EventListener);
    };
  }, []);

  // Hold the latest onOpenChange in a ref so the effect's only dependency is
  // isManaging itself. App.tsx passes an inline arrow for onOpenChange, which
  // gets a fresh reference every render — including it in deps would re-fire
  // the effect on every parent re-render, calling onOpenChange(false) which
  // clears pendingTrip, killing the new-trip flow before the form opens.
  const onOpenChangeRef = useRef(onOpenChange);
  useEffect(() => { onOpenChangeRef.current = onOpenChange; });
  const onOpenChangeFirstRun = useRef(true);
  useEffect(() => {
    if (onOpenChangeFirstRun.current) {
      onOpenChangeFirstRun.current = false;
      return;
    }
    onOpenChangeRef.current?.(isManaging);
  }, [isManaging]);

  // Add useEffect to handle scrolling to top on view or day changes
  useEffect(() => {
    // Scroll window for general cases
    window.scrollTo(0, 0);
    
    // Specifically target the App's main scroll container
    const mainContainer = document.querySelector('main');
    if (mainContainer) {
      mainContainer.scrollTop = 0;
      // Also try formal scrollTo
      mainContainer.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [view, activeDayIdx]);

  const activeDay = (itineraryDays.length > 0 && hasTrip) ? (itineraryDays[activeDayIdx] || itineraryDays[0]) : null;

  // Destinations in chronological (visit) order — the source of truth for the
  // horizontal DestinationScroller shared by Shortlists and Day Planner. Index
  // into THIS array, not the raw tripConfig.destinations storage order, which
  // isn't guaranteed to match visit order (same reasoning as chronoColorIdx
  // in TripOverview).
  const sortedDestinations = useMemo(
    () => [...(tripConfig?.destinations || [])].sort((a, b) => (a.arrivalDate || '').localeCompare(b.arrivalDate || '')),
    [tripConfig?.destinations]
  );
  const [selectedDestIdx, setSelectedDestIdx] = useState(0);

  // Bridge to the existing day-based deep-link plumbing (nav-itinerary-day,
  // used by Home/Notifications/Expenses/etc.) without rewriting any of it:
  // whenever activeDayIdx changes for any reason, re-derive which destination
  // it belongs to so Shortlists/Day Planner land on the right one.
  // By date, not by city: a trip that returns to Paris has two Paris stops,
  // and matching the name always landed on the first of them.
  const destIdxForDate = (date: string) => {
    const dest = destinationForDate(sortedDestinations, date);
    return dest ? sortedDestinations.indexOf(dest) : -1;
  };
  useEffect(() => {
    if (!activeDay) return;
    const idx = destIdxForDate(activeDay.date);
    if (idx !== -1) setSelectedDestIdx(idx);
  }, [activeDay?.date, sortedDestinations]); // eslint-disable-line react-hooks/exhaustive-deps

  // Picking a destination in the scroller also moves activeDayIdx to that
  // destination's first day, so DayDetail (which still needs a concrete Day)
  // keeps working unchanged.
  const handleSelectDest = (idx: number) => {
    setSelectedDestIdx(idx);
    const dest = sortedDestinations[idx];
    if (!dest) return;
    const dayIdx = itineraryDays.findIndex(d => destIdxForDate(d.date) === idx);
    if (dayIdx >= 0) setActiveDayIdx(dayIdx);
  };

  // PersonalShortlist picks the visit; select it here by identity, not name.
  useEffect(() => {
    if (!routeShortlist) return;
    const i = sortedDestinations.findIndex(d => destinationKey(d) === routeShortlist.focusId);
    if (i >= 0) handleSelectDest(i);
  }, [routeShortlist?.focusId, sortedDestinations]); // eslint-disable-line react-hooks/exhaustive-deps

  // After the visit's default-day selection, so it cannot overwrite the
  // linked item's day before its card opens (including a freshly mounted scope).
  useEffect(() => {
    const detail = peekPendingNav('nav-itinerary-day');
    if (detail) handleNavDayRef.current?.(new CustomEvent('nav-itinerary-day', { detail }));
  }, [itineraryDays, routeShortlist?.focusId]);

  // Include departure even without a next stop, so its travel allowance is visible.
  const destDateList = (dest: Destination) => {
    const a = new Date((dest.arrivalDate || '').slice(0, 10) + 'T00:00:00Z');
    const b = new Date((dest.departureDate || '').slice(0, 10) + 'T00:00:00Z');
    const out: { date: string; label: string; dow: string }[] = [];
    for (let t = new Date(a); t < b && out.length < 60; t.setUTCDate(t.getUTCDate() + 1)) {
      out.push({
        date: t.toISOString().slice(0, 10),
        label: t.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }),
        dow: t.toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' }),
      });
    }
    const list = out.length ? out : [{ date: (dest.arrivalDate || '').slice(0, 10), label: '', dow: '' }];
    const sharedDate = (dest.departureDate || '').slice(0, 10);
    if (!sharedDate || list.some(d => d.date === sharedDate)) return list;
    const t = new Date(sharedDate + 'T00:00:00Z');
    return [...list, {
      date: sharedDate,
      label: t.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }),
      dow: t.toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' }),
    }];
  };

  // Day Planner's active date — which day within the selected destination the
  // scheduler is showing. Resets to the destination's first day whenever the
  // selected destination changes (via swipe or the date strip).
  const [activeDate, setActiveDate] = useState('');
  useEffect(() => {
    const dest = sortedDestinations[selectedDestIdx];
    if (!dest) return;
    const list = routeShortlist && destinationKey(dest) === routeShortlist.focusId ? routeShortlist.dates : destDateList(dest);
    // Kept if still in this destination; otherwise today when it falls here
    // (an active trip opens the Schedule on today), else the first day.
    const today = localDateKey();
    setActiveDate(cur => (list.some(d => d.date === cur) ? cur : list.some(d => d.date === today) ? today : list[0].date));
  }, [selectedDestIdx, sortedDestinations, routeShortlist?.focusId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (routeShortlist?.focusDate) setActiveDate(routeShortlist.focusDate);
  }, [routeShortlist?.focusDate, routeShortlist?.focusId]);

  // The hero card's swipe / the scheduler's own prev/next-destination step —
  // both update the same shared selectedDestIdx, so the scroller (and, in Day
  // Planner mode, the date strip) stays in sync automatically.
  const handleNavigateDest = (dir: 'prev' | 'next') => {
    const targetIdx = dir === 'prev' ? selectedDestIdx - 1 : selectedDestIdx + 1;
    if (targetIdx < 0 || targetIdx >= sortedDestinations.length) return;
    handleSelectDest(targetIdx);
  };

  // Every date across the whole trip span (first destination's arrival →
  // last destination's departure, inclusive), each tagged with which
  // destination it belongs to — or destIdx -1 / isGap for the dates that
  // fall between two destinations with nothing booked. Drives Day Planner's
  // top strip; gap dates render greyed there but stay tappable (they land on
  // a "no destination for these dates" prompt).
  const allTripDates = useMemo(() => {
    const out: { date: string; label: string; dow: string; destIdx: number; isGap: boolean }[] = [];
    if (!sortedDestinations.length) return out;
    const firstArr = (sortedDestinations[0].arrivalDate || '').slice(0, 10);
    const lastDep = (sortedDestinations[sortedDestinations.length - 1].departureDate || '').slice(0, 10);
    const a = new Date(firstArr + 'T00:00:00Z');
    const b = new Date(lastDep + 'T00:00:00Z');
    for (let t = new Date(a); t <= b && out.length < 400; t.setUTCDate(t.getUTCDate() + 1)) {
      const iso = t.toISOString().slice(0, 10);
      let di = sortedDestinations.findIndex(d =>
        iso >= (d.arrivalDate || '').slice(0, 10) && iso < (d.departureDate || '').slice(0, 10));
      // Without an arriving owner, the departing visit still needs its travel day.
      if (di === -1) di = sortedDestinations.findIndex(d => d.departureDate === iso);
      out.push({
        date: iso,
        label: t.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }),
        dow: t.toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' }),
        destIdx: di,
        isGap: di === -1,
      });
    }
    return out;
  }, [sortedDestinations]);

  // A small transport-mode badge above the pill (not a dashed border) marks a
  // destination transition that actually has a logged transport — keyed by
  // the boundary (arrival) date. A travel day can have multiple legs in
  // different modes (e.g. taxi then flight); primaryTransport picks the
  // single "highest" one (plane beats boat beats train beats bus beats car)
  // to badge, rather than just the first leg saved. Also covers the very
  // first destination's own arrival (the outbound leg getting you to the
  // trip at all) — the loop below only walks *transitions between*
  // destinations, which skips that one the same way it skips the final
  // destination's departure (handled separately in dateScrollerItems, since
  // that date has no pill of its own to badge).
  // A leg is stored on its departure date, so the day document for an arrival
  // date doesn't contain the overnight leg that lands on it — the boundary
  // date between two destinations then showed no transport badge at all for a
  // booked red-eye. Match either end of the journey, and keep the day's own
  // rows so a leg saved with no dates at all still badges the day it's on.
  const badgeFor = (dayDate: string) => {
    const onDay = itineraryDays.find(d => d.date === dayDate)?.transports || [];
    const touching = itineraryDays.flatMap(d => d.transports || []).filter(t =>
      !onDay.includes(t) && ((t.dep || '').slice(0, 10) === dayDate || (t.arr || '').slice(0, 10) === dayDate));
    const candidates = [...onDay, ...touching].filter(t => !t.isRental);
    const t = candidates.length ? primaryTransport(candidates) : undefined;
    if (!t) return undefined;
    return {
      icon: TRANSPORT_MODE_ICON[t.mode] || MapPin,
      label: t.carrier?.trim() ? `${t.carrier.trim()} ${t.mode.toLowerCase()}` : t.mode,
    };
  };

  const transportBadgeByDate = useMemo(() => {
    const out: Record<string, { icon: LucideIcon; label: string }> = {};
    if (sortedDestinations.length > 0) {
      const firstArrival = (sortedDestinations[0].arrivalDate || '').slice(0, 10);
      const badge = badgeFor(firstArrival);
      if (badge) out[firstArrival] = badge;
    }
    for (let i = 0; i < sortedDestinations.length - 1; i++) {
      const boundaryDate = (sortedDestinations[i + 1].arrivalDate || '').slice(0, 10);
      const badge = badgeFor(boundaryDate);
      if (badge) out[boundaryDate] = badge;
    }
    return out;
  }, [sortedDestinations, itineraryDays]);

  // The final destination's departure date has no pill of its own in
  // allTripDates (it's exclusive — for every other destination that date IS
  // the next one's arrival, but the trip's true last destination has no
  // "next" to carry it), so a return-leg transport logged there can't be
  // badged via transportBadgeByDate. Surfaced as a trailing, pill-less badge
  // appended after the last date instead.
  const lastDepartureBadge = useMemo(() => {
    if (sortedDestinations.length === 0) return undefined;
    const lastDest = sortedDestinations[sortedDestinations.length - 1];
    return badgeFor((lastDest.departureDate || '').slice(0, 10));
  }, [sortedDestinations, itineraryDays]);

  const dateScrollerItems: DateScrollerItem[] = useMemo(() => {
    const items: DateScrollerItem[] = [];
    allTripDates.forEach(d => {
      const badge = transportBadgeByDate[d.date];
      if (badge) items.push({ type: 'transport', key: `transport-${d.date}`, icon: badge.icon, label: badge.label });
      items.push({ type: 'date', date: d.date, label: d.label, dow: d.dow, isGap: d.isGap });
    });
    if (lastDepartureBadge) {
      items.push({ type: 'transport', key: 'transport-end', icon: lastDepartureBadge.icon, label: lastDepartureBadge.label });
    }
    return items;
  }, [allTripDates, transportBadgeByDate, lastDepartureBadge]);


  // Picking a date that belongs to a different destination than the one
  // currently selected also switches selectedDestIdx (and, via
  // handleSelectDest, activeDayIdx) — the date strip is trip-wide, so this is
  // how it stays in sync with the hero card / scheduler below it.
  const handleSelectTripDate = (date: string) => {
    const entry = allTripDates.find(d => d.date === date);
    if (!entry) return;
    // Gap dates (destIdx -1) don't belong to any destination — keep the
    // current selectedDestIdx, just move the active date so the body can
    // show its "no destination for these dates" prompt.
    if (entry.destIdx >= 0 && entry.destIdx !== selectedDestIdx) handleSelectDest(entry.destIdx);
    setActiveDate(date);
  };

  // Shared by DayDetail's onUpdate (Shortlists) and the Day Planner hero
  // card's traveller toggle — both ultimately write the same Day-doc shape,
  // so this is hoisted out rather than duplicated. `previousDay` (whichever
  // Day the caller was showing before this edit) drives the "did a
  // destination-shared field change" diff, since that's caller-specific
  // (Shortlists diffs against `activeDay`, Day Planner against `cardDay`).
  const saveDayUpdate = async (updated: Day, previousDay: Day, expense?: Expense) => {
    if (updated.city === "MANAGE_TRIP_TRIGGER") {
      setView('overview');
      const range = findGapRange(updated.date);
      if (range) {
        setManagementMode('destination');
        setHighlightDestIdx(null);
        setNewDestDates(range);
      } else {
        setManagementMode('destinations');
      }
      return;
    }
    if (!tripConfig) throw new Error('This trip is no longer available.');
    await sharedSaveDayUpdate(tripConfig, updated, previousDay, user.id, expense);
  };

  // Emoji-edit state for the Day Planner hero card (Shortlists' DayDetail
  // owns its own equivalent locally — this one lives here since the Day
  // Planner card is rendered directly by ItineraryTab, not by DayDetail).
  const [dayPlannerEditingEmoji, setDayPlannerEditingEmoji] = useState(false);
  const updateSelectedDestEmoji = (emoji: string) => {
    if (!tripConfig) return;
    const dests = tripConfig.destinations || [];
    if (selectedDestIdx < 0 || selectedDestIdx >= dests.length) return;
    const newDests = [...dests];
    newDests[selectedDestIdx] = { ...newDests[selectedDestIdx], emoji };
    onUpdateConfig({ ...tripConfig, destinations: newDests });
    setDayPlannerEditingEmoji(false);
  };

  const selectDay = (day: Day) => {
    const idx = itineraryDays.findIndex(d => d.dateShort === day.dateShort);
    if (idx !== -1) {
      setActiveDayIdx(idx);
      setView('day');
    }
  };

  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setConfirmDelete(false);
  }, [managementMode]);

  // Keep the review mounted when creating the first trip switches the surrounding screen.
  // https://react.dev/learn/preserving-and-resetting-state
  const itineraryImportModal = showItineraryImportModal && (
    <Suspense fallback={null}>
    <ImportItineraryModal
      key="itinerary-import"
      tripConfig={null}
      currentUser={user}
      onUpdateConfig={onUpdateConfig}
      onTripCreated={onTripCreated}
      initialFiles={preloadedFiles}
      onClose={() => { setShowItineraryImportModal(false); setPreloadedFiles([]); }}
    />
    </Suspense>
  );

  if (!hasTrip || !tripConfig) {
    // This component backs three separate nav tabs, so the empty state names
    // whichever one the user actually tapped and says what it's for — the
    // single generic "Your Trips" card here predates the nav split and left
    // Shortlist/Schedule describing themselves as trip creation.
    const emptyTab: NavTabId = view === 'overview'
      ? 'logistics'
      : detailMode === 'dayPlanner' ? 'dayplanner' : 'bucketlist';
    const emptyCopy = TAB_INTRO_COPY[emptyTab];

    // The wrapping <div> is load-bearing, not decoration: both this branch and
    // the active-trip return must render the keyed import modal under the same
    // host element, or React remounts it and discards an in-progress import
    // review when the first trip is created (itineraryImportHost.test.ts).
    return (
      <div className="animate-fade-up flex-1 flex flex-col">
        <EmptyState
          icon={emptyCopy.icon}
          label={TAB_LABELS[emptyTab]}
          title={emptyCopy.title}
          body={emptyCopy.body}
          actionLabel="Create a trip"
          onAction={onAddTrip}
        />

        {creatingTrip && (
          <div className="fixed inset-0 z-[60] bg-app-bg/95 backdrop-blur-xl">
            <div className="h-full overflow-y-auto px-6 py-12 pb-32">
              <div className="max-w-md mx-auto">
                <div className="mb-10">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="font-serif text-3xl font-bold text-app-text text-balance pr-4">{wizardStepTitle[wizardStep]}</h2>
                    <button aria-label="Close" disabled={savingConfig} onClick={() => { setCreatingTrip(null); setManagementMode(null); onCancelCreate?.(); }} className="text-app-sub/40 hover:opacity-100 transition-opacity shrink-0"><X className="w-6 h-6" /></button>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {wizardStepOrder.map(step => (
                      <div
                        key={step}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          step === wizardStep
                            ? 'bg-gold'
                            : wizardStepOrder.indexOf(step) < wizardStepOrder.indexOf(wizardStep)
                              ? 'bg-gold/40'
                              : 'bg-app-border/40'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <TripManagementForm
                  key={creatingTrip.id}
                  config={creatingTrip}
                  mode="wizard"
                  onWizardStepChange={setWizardStep}
                  currentUser={user}
                  onSavingChange={setSavingConfig}
                  onSave={async (c) => {
                    const saved = await onUpdateConfig(c);
                    if (saved) { setCreatingTrip(null); setManagementMode(null); }
                    return saved;
                  }}
                  onPersist={onUpdateConfig}
                  onCancel={() => { setCreatingTrip(null); setManagementMode(null); onCancelCreate?.(); }}
                  onOpenImport={(files) => { setCreatingTrip(null); setManagementMode(null); onCancelCreate?.(); setPreloadedFiles(files); setShowItineraryImportModal(true); }}
                />
              </div>
            </div>
          </div>
        )}

        {itineraryImportModal}
      </div>
    );
  }
  // The Schedule (dayPlanner) case's body, extracted to a plain function
  // (not a component) purely so it can be called from either branch below
  // without duplicating ~140 lines — identical output either way, just a
  // different wrapper around it.
  const renderDayPlannerBody = () => {
    return (() => {
          const schedDest = sortedDestinations[selectedDestIdx];
          if (!schedDest) {
            return (
              <EmptyState
                icon={<CalendarClock className="w-9 h-9" />}
                label={TAB_LABELS.dayplanner}
                title="Nowhere to schedule yet"
                body={`Add a destination in the ${TAB_LABELS.logistics} tab and its days will show up here, ready to plan.`}
                actionLabel={`Go to ${TAB_LABELS.logistics}`}
                onAction={() => setView('overview')}
              />
            );
          }
          // Gap date — a trip day with no destination booked for it. The
          // strip lets you land here (greyed); nudge back to Logistics to
          // fill it rather than showing an empty scheduler.
          const activeIsGap = allTripDates.find(d => d.date === activeDate)?.isGap;
          if (activeIsGap) {
            return (
              <EmptyState
                icon={<MapPin className="w-9 h-9" />}
                label={TAB_LABELS.dayplanner}
                title="No destination for this date"
                body={`Nothing's booked for this day yet. Add a destination covering it in the ${TAB_LABELS.logistics} tab to start planning.`}
                actionLabel={`Go to ${TAB_LABELS.logistics}`}
                onAction={() => setView('overview')}
              />
            );
          }
          const repDay = itineraryDays.find(d => d.date === schedDest.arrivalDate);
          // The card shows whichever calendar date is active in the date strip
          // (may be a different day than repDay, or the shared travel day at
          // the end of the strip) — falls back to repDay if that date's own
          // record isn't found for some reason.
          const cardDay = itineraryDays.find(d => d.date === activeDate) || repDay;
          const dates = routeShortlist?.dates ?? destDateList(schedDest);
          const schedDestId = (schedDest.arrivalDate || '').slice(0, 10);
          const accomForDest = (dst: Destination | undefined) => (tripConfig.accommodations || []).find(a => {
            const ci = (a.checkIn || '').slice(0, 10);
            return dst && ci >= (dst.arrivalDate || '').slice(0, 10) && ci < (dst.departureDate || '').slice(0, 10);
          });
          const schedAccom = accomForDest(schedDest);
          // The destination visited immediately before this one, so the scheduler
          // can ground a travel day's departure (accommodation + country).
          const previousVisit = routeShortlist ? routeShortlist.prev : sortedDestinations[selectedDestIdx - 1];
          const prevDestRaw = previousVisit?.departureDate === schedDest.arrivalDate ? previousVisit : undefined;
          const prevAccom = accomForDest(prevDestRaw);
          const prevDest = prevDestRaw ? {
            name: prevDestRaw.name,
            country: prevDestRaw.country,
            accomName: prevAccom?.name,
            accomAddress: prevAccom?.address,
            destId: (prevDestRaw.arrivalDate || '').slice(0, 10),
          } : routeShortlist?.arrivalFrom ? { name: routeShortlist.arrivalFrom.name, country: routeShortlist.arrivalFrom.country } : undefined;
          // The destination visited immediately after this one: its arrival date
          // is this destination's departure day — the shared travel day.
          const nextVisit = routeShortlist ? routeShortlist.next : sortedDestinations[selectedDestIdx + 1];
          const nextDestRaw = nextVisit?.arrivalDate === schedDest.departureDate ? nextVisit : undefined;
          const sharedDate = nextDestRaw ? (nextDestRaw.arrivalDate || '').slice(0, 10) : undefined;
          const sharedDay = sharedDate ? { date: sharedDate, ownerDestId: sharedDate } : undefined;
          const gradIdx = (i: number) => ((i % DEST_GRADIENTS.length) + DEST_GRADIENTS.length) % DEST_GRADIENTS.length;
          // cardDay is already the ARRIVING side's Day record on a transit day
          // (getExpandedDays assigns a shared travel date to the arriving
          // destination) — no extra lookups needed for weather/nights there.
          const isArrivingToday = !!prevDestRaw && activeDate === schedDestId;
          const isDepartingToday = !!nextDestRaw && activeDate === sharedDate;
          const isTransitDay = isArrivingToday || isDepartingToday;
          const stripSides: DestinationStripSide[] = isArrivingToday
            ? [
                { name: destTitle(prevDestRaw!.name), emoji: prevDestRaw!.emoji || '📍', background: DEST_GRADIENTS[gradIdx(selectedDestIdx - 1)] },
                { name: cardDay?.city || destTitle(schedDest.name), emoji: cardDay?.emoji || schedDest.emoji || '📍', background: cardDay?.cityBg || DEST_GRADIENTS[gradIdx(selectedDestIdx)] },
              ]
            : isDepartingToday
            ? [
                { name: destTitle(schedDest.name), emoji: schedDest.emoji || '📍', background: DEST_GRADIENTS[gradIdx(selectedDestIdx)] },
                { name: cardDay?.city || destTitle(nextDestRaw!.name), emoji: cardDay?.emoji || nextDestRaw!.emoji || '📍', background: cardDay?.cityBg || DEST_GRADIENTS[gradIdx(selectedDestIdx + 1)] },
              ]
            : [{ name: cardDay?.city || destTitle(schedDest.name), emoji: cardDay?.emoji || schedDest.emoji || '📍', background: cardDay?.cityBg || DEST_GRADIENTS[gradIdx(selectedDestIdx)] }];
          // One accent hex per strip side, same indices as stripSides above —
          // feeds the day-header's own colour wash/blobs below, so a transit
          // day's header splits departure/arrival exactly like the strip does.
          const headerTints: string[] = isArrivingToday
            ? [destAccentColor(gradIdx(selectedDestIdx - 1)), destAccentColor(gradIdx(selectedDestIdx))]
            : isDepartingToday
            ? [destAccentColor(gradIdx(selectedDestIdx)), destAccentColor(gradIdx(selectedDestIdx + 1))]
            : [destAccentColor(gradIdx(selectedDestIdx))];
          // "Day X of Y" within this destination's own stay (arrival→departure,
          // excluding the trailing shared travel day). Hidden on transit days —
          // the split strip already signals arrival/departure.
          const stayDates = sharedDate ? dates.filter(dt => dt.date !== sharedDate) : dates;
          const dayOfStay = stayDates.findIndex(dt => dt.date === activeDate) + 1;
          const dayBadge = (!isTransitDay && dayOfStay > 0 && stayDates.length > 0)
            ? `Day ${dayOfStay} of ${stayDates.length}`
            : undefined;
          return (
            <motion.div key={`sched-${schedDestId}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pb-32">
              {/* No gap to DestinationScheduler below — the strip's rounded
                  TOP corners and its own day-header's square everything
                  read as one joined block (colour strip flowing into a
                  white header) rather than two stacked cards. */}
              <DestinationStrip
                sides={stripSides}
                weatherSlot={!isTransitDay ? (
                  <WeatherDisplay
                    city={cardDay?.city || destTitle(schedDest.name)}
                    date={cardDay?.date || activeDate}
                    initialWeather={cardDay?.weather || ''}
                  />
                ) : undefined}
                hasPrev={routeShortlist ? routeShortlist.hasPrev : selectedDestIdx > 0}
                hasNext={routeShortlist ? routeShortlist.hasNext : selectedDestIdx < sortedDestinations.length - 1}
                onNavigateDest={routeShortlist?.go ?? handleNavigateDest}
                canEditEmoji={!isTransitDay && isAdmin}
                editingEmoji={dayPlannerEditingEmoji}
                onEditEmoji={() => isAdmin && setDayPlannerEditingEmoji(true)}
                onSelectEmoji={(emoji) => updateSelectedDestEmoji(emoji)}
                onCloseEmojiPicker={() => setDayPlannerEditingEmoji(false)}
              />
              <TripParticipationNotice trip={tripConfig} visitId={destinationKey(schedDest)} date={activeDate} />
              <DestinationScheduler
                itinerary={tripConfig.itinerary}
                dest={{
                  name: repDay?.city || schedDest.name,
                  country: repDay?.country || schedDest.country || '',
                  flag: repDay?.flag || '',
                  emoji: repDay?.emoji || schedDest.emoji || '📍',
                  cityBg: repDay?.cityBg || DEST_GRADIENTS[selectedDestIdx % DEST_GRADIENTS.length],
                  accent: repDay?.accent || '#c5a059',
                }}
                dates={dates}
                activeDate={activeDate}
                dayBadge={dayBadge}
                headerTints={headerTints}
                otherDestinations={sortedDestinations.filter((_, i) => i !== selectedDestIdx).map(d => d.name)}
                tripId={tripConfig.id}
                destId={schedDestId}
                currentUserId={user.id}
                travellers={tripConfig.travellers}
                junctions={(junctionsInScope[tripConfig.id] ?? []).filter(e => e.anchorVisitId === destinationKey(schedDest))}
                accommodation={schedAccom ? { name: schedAccom.name, address: schedAccom.address, area: schedAccom.area } : undefined}
                accomNightsMissing={accommodationGaps(schedDest, accsForDest(schedDest, tripConfig.accommodations || [])).reduce((n, g) => n + g.nights, 0)}
                prevDest={prevDest}
                sharedDay={sharedDay}
                travelContext={{ visit: routeShortlist?.visit ?? schedDest, previous: previousVisit ?? routeShortlist?.arrivalFrom, next: nextVisit,
                  transports: Object.entries(tripConfig.itinerary || {}).flatMap(([date, d]) =>
                    (d.transports || []).map(t => ({ ...t, _dayDate: date }))) }}
                onExitToItinerary={(activityId) => {
                  onDetailModeChange('shortlists');
                  [100, 400, 700, 1200, 2000].forEach(delay =>
                    setTimeout(() => window.dispatchEvent(new CustomEvent('nav-itinerary-day', { detail: `itinerary-${activityId}` })), delay)
                  );
                }}
              />
            </motion.div>
          );
        })();
  };

  const renderShortlistBody = () => (
    activeDay ? (
      <DayDetail
        day={activeDay}
        displayVisit={routeShortlist?.visit}
        mode="shortlist"
        hasPrevDest={routeShortlist ? routeShortlist.hasPrev : selectedDestIdx > 0}
        hasNextDest={routeShortlist ? routeShortlist.hasNext : selectedDestIdx < sortedDestinations.length - 1}
        onNavigateDest={routeShortlist?.go ?? handleNavigateDest}
        onUpdate={saveDayUpdate}
        onUpdateConfig={onUpdateConfig}
        currentUser={user}
        tripConfig={tripConfig}
        onOpenImport={() => setShowImportModal(true)}
      />
    ) : (
      <EmptyState
        icon={<Star className="w-9 h-9" />}
        label={TAB_LABELS.bucketlist}
        title="Nowhere to shortlist yet"
        body={`Add a destination in the ${TAB_LABELS.logistics} tab, then collect places to eat, see and do for it here.`}
        actionLabel={`Go to ${TAB_LABELS.logistics}`}
        onAction={() => setView('overview')}
      />
    )
  );

  // Overview (Logistics) and Schedule (Day Planner) both get a fixed-
  // height flex column so their own header/scroll-body split can fill it
  // exactly — a plain `position: sticky` header only stays stuck within
  // its own immediate parent's height, which for the date strip's tight
  // wrapper was barely taller than the strip itself, so it just scrolled
  // away with everything else instead of remaining visible. Shortlists
  // keeps plain document flow inside <main>'s own scroller, unchanged.
  return (
    <div className={(view === 'overview' || detailMode === 'dayPlanner') ? `${branchTimeline?.personalSegment ? '' : 'h-full'} flex flex-col` : undefined}>
      {detailMode === 'dayPlanner' && view === 'day' ? (
        <>
          {/* Inside the scroller, not pinned above it. Freezing the date
              strip kept it permanently in view at the cost of permanently
              spending that height — on a phone it was a fixed band the day
              below could never reclaim, and the day is what you came to
              read. It scrolls away like everything else now. */}
          {/* overflow-x-clip, explicitly: a box with overflow-y set and
              overflow-x left alone computes x to `auto` too, which turned
              the whole day into a sideways scroller the moment the date
              strip (deliberately wider, to bleed past the page gutter)
              landed inside it. -mx-6/px-6 puts this box's own edges at the
              screen edges, so the clip happens there rather than cutting
              the strip off at the gutter. */}
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-clip no-scrollbar -mx-6 px-6">
            <DateScroller
              items={routeShortlist?.dateItems ?? dateScrollerItems}
              activeDate={activeDate}
              onSelect={routeShortlist?.selectDate ?? handleSelectTripDate}
              accent={itineraryDays.find(d => destIdxForDate(d.date) === selectedDestIdx)?.accent || '#c5a059'}
            />
            {renderDayPlannerBody()}
          </div>
        </>
      ) : (
        <>
          {view === 'day' && (routeShortlist?.scroller ?? (
            <DestinationScroller
              destinations={sortedDestinations}
              selectedIdx={selectedDestIdx}
              onSelect={handleSelectDest}
            />
          ))}
          {view === 'overview' ? (
            <TripOverview
              branchTimeline={branchTimeline}
              itineraryDays={itineraryDays}
              onSelectDay={selectDay}
              tripConfig={effectiveConfig || tripConfig}
              onUpdateConfig={onUpdateConfig}
              currentUser={user}
              managementMode={managementMode}
              setManagementMode={setManagementMode}
              highlightDestIdx={highlightDestIdx}
              setHighlightDestIdx={setHighlightDestIdx}
              newDestDates={newDestDates}
              setNewDestDates={setNewDestDates}
              trips={trips}
              activeTripId={activeTripId}
              onSelectTrip={onSelectTrip}
              onAddTrip={onAddTrip}
              onDeleteTrip={onDeleteTrip}
              onDraftChange={setDraftConfig}
              onOpenImport={() => setShowImportModal(true)}
            />
          ) : renderShortlistBody()}
        </>
      )}

      {/* Suspense outside AnimatePresence: AnimatePresence tracks its direct
          children to run exit animations, so a boundary in between would
          break the modal's close animation. */}
      <Suspense fallback={null}>
        <AnimatePresence>
          {showImportModal && tripConfig && (
            <ImportBookingModal
              tripConfig={tripConfig}
              allTrips={trips}
              itineraryDays={itineraryDays}
              currentUser={user}
              onUpdateConfig={onUpdateConfig}
              onClose={() => setShowImportModal(false)}
            />
          )}
        </AnimatePresence>
      </Suspense>
      {itineraryImportModal}

      {creatingTrip && (
        <div className="fixed inset-0 z-[60] bg-app-bg/95 backdrop-blur-xl">
          <div className="h-full overflow-y-auto px-6 py-12 pb-32">
            <div className="max-w-md mx-auto">
              <div className="mb-10">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="font-serif text-3xl font-bold text-app-text text-balance pr-4">{wizardStepTitle[wizardStep]}</h2>
                    <button aria-label="Close" disabled={savingConfig} onClick={() => { setCreatingTrip(null); setManagementMode(null); onCancelCreate?.(); }} className="text-app-sub/40 hover:opacity-100 transition-opacity shrink-0"><X className="w-6 h-6" /></button>
                </div>
                <div className="flex items-center gap-1.5">
                  {wizardStepOrder.map(step => (
                    <div
                      key={step}
                      className={`h-1 flex-1 rounded-full transition-colors ${
                        step === wizardStep
                          ? 'bg-gold'
                          : wizardStepOrder.indexOf(step) < wizardStepOrder.indexOf(wizardStep)
                            ? 'bg-gold/40'
                            : 'bg-app-border/40'
                      }`}
                    />
                  ))}
                </div>
              </div>
              <TripManagementForm
                key={creatingTrip.id}
                config={creatingTrip}
                mode="wizard"
                onWizardStepChange={setWizardStep}
                currentUser={user}
                  onSavingChange={setSavingConfig}
                  onSave={async (c) => {
                    const saved = await onUpdateConfig(c);
                    if (saved) { setCreatingTrip(null); setManagementMode(null); }
                    return saved;
                  }}
                  onPersist={onUpdateConfig}
                onCancel={() => { setCreatingTrip(null); setManagementMode(null); onCancelCreate?.(); }}
                onOpenImport={(files) => { setCreatingTrip(null); setManagementMode(null); setPreloadedFiles(files); setShowItineraryImportModal(true); }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
